import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import OfflineGenerator from '@/components/OfflineGenerator';
import FoodManager from '@/components/FoodManager';
import TrainingManager from '@/components/TrainingManager';
import VIPClientsManager from '@/components/VIPClientsManager';
import PendingPlansManager from '@/components/PendingPlansManager';
import FormBuilder from '@/components/FormBuilder';
import ClientProfileModal from '@/components/ClientProfileModal';
import SupplementsStatusManager from '@/components/SupplementsStatusManager';
import AdminGoldCalendar from '@/components/AdminGoldCalendar';
import NotificationBell from '@/components/NotificationBell';
import RecipeVideoManager from '@/components/RecipeVideoManager';
import UserPlanViewer from '@/components/UserPlanViewer';
import ManualPaymentMethodsManager from '@/components/ManualPaymentMethodsManager';
import RecipeLibraryManager from '@/components/RecipeLibraryManager';
import CoachMessagesManager from '@/components/CoachMessagesManager';
import RecipePromptTemplatesEditor from '@/components/RecipePromptTemplatesEditor';
import PathologyCasesManager from '@/components/PathologyCasesManager';
import FitEvolutionAdminManager from '@/components/FitEvolutionAdminManager';
import FitEvolutionTemplateManager from '@/components/FitEvolutionTemplateManager';
import BulkTagEditor from '@/pages/BulkTagEditor';
import ReplacementExercisesField from '@/components/ReplacementExercisesField';
import AdminMissingVideos from '@/components/AdminMissingVideos';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { FaCalendarAlt, FaWhatsapp } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function para obtener headers con autenticación
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Componente para gestionar secciones en construcción
const SectionsUnderConstructionPanel = () => {
  const [sections, setSections] = useState({
    videos: false,
    ebooks: false,
    recetarios: false,
    tienda: true, // Tienda en construcción por defecto
    my_diet: false,
    my_workout: false,
    data_progress: false,
    gold_calendar: false,
    recipe_ideas: false,
    appointments: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sectionLabels = {
    videos: { label: '📹 Videos', desc: 'Biblioteca de videos de entrenamiento' },
    ebooks: { label: '📚 Ebooks', desc: 'Guías y documentos descargables' },
    recetarios: { label: '🍳 Recetarios', desc: 'Videos de recetas' },
    tienda: { label: '🛒 Tienda', desc: 'Tienda de productos y suplementos' },
    my_diet: { label: '🥗 Mi Dieta', desc: 'Plan de alimentación personalizado' },
    my_workout: { label: '💪 Mi Rutina', desc: 'Plan de entrenamiento personalizado' },
    data_progress: { label: '📊 Data Progress', desc: 'Seguimiento de progreso' },
    gold_calendar: { label: '📅 Calendario Gold', desc: 'Reserva de sesiones' },
    recipe_ideas: { label: '✨ Ideas Recetas', desc: 'Cocina Inteligente: generador de recetas' },
    appointments: { label: '📆 Citas', desc: 'Sistema de citas' }
  };

  useEffect(() => {
    fetchSectionsStatus();
    // Auto-limpiar duplicados al cargar admin
    autoCleanupDuplicates();
  }, []);

  const autoCleanupDuplicates = async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      const res = await axios.post(`${API}/admin/cleanup-duplicate-plans`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data.users_merged > 0 || res.data.plans_cleaned > 0) {
        toast.success(`Auto-limpieza: ${res.data.users_merged} usuarios fusionados, ${res.data.plans_cleaned} planes duplicados eliminados`);
      }
    } catch (e) { /* silencioso si falla */ }
  };

  const fetchSectionsStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/sections-status`);
      if (response.data.sections) {
        setSections(prev => ({ ...prev, ...response.data.sections }));
      }
    } catch (error) {
      console.error('Error fetching sections status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newSections = { ...sections, [key]: !sections[key] };
    setSections(newSections);
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/sections-status`, { sections: newSections });
      toast.success(`${sectionLabels[key].label} ${newSections[key] ? 'marcada como en construcción' : 'activada'}`);
    } catch (error) {
      console.error('Error updating section status:', error);
      toast.error('Error al actualizar el estado');
      // Revertir cambio
      setSections(sections);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-400 mt-2">Cargando estado de secciones...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Object.entries(sectionLabels).map(([key, { label, desc }]) => (
        <div 
          key={key}
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            sections[key] 
              ? 'bg-orange-500/20 border-2 border-orange-500/50 ring-2 ring-orange-500/20' 
              : 'bg-green-500/10 border border-green-500/30 hover:border-green-500/50'
          }`}
          onClick={() => !saving && handleToggle(key)}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-medium text-white">{label}</span>
            <div className={`w-12 h-6 rounded-full relative transition-colors ${
              sections[key] ? 'bg-orange-500' : 'bg-green-500'
            }`}>
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                sections[key] ? 'right-0.5' : 'left-0.5'
              }`}></div>
            </div>
          </div>
          <p className="text-xs text-gray-400">{desc}</p>
          <div className={`mt-2 px-2 py-1 rounded text-xs font-medium inline-block ${
            sections[key] 
              ? 'bg-orange-500/30 text-orange-300' 
              : 'bg-green-500/30 text-green-300'
          }`}>
            {sections[key] ? '🚧 En Construcción' : '✅ Disponible'}
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminPanelNew = ({ user, onLogout }) => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const { refreshConfig } = useSiteConfig();

  // States para Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // States para Videos
  const [videoData, setVideoData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    category: '',
    is_free: false,
    allowed_plans: [], // ['basico', 'premium', 'vip_gold', 'competitivo']
    video_source: 'drive', // 'drive', 'youtube' (removed 'file' - all storage via Drive links)
    // === FIT EVOLUTION — Etiquetas para generador automático de rutinas ===
    exercise_type: '',      // activador | principal | secundario | complementario
    muscle_group: '',       // gluteo | isquios | cuadriceps | ...
    recommended_sex: 'ambos', // mujer | hombre | ambos
    location: 'ambos',      // casa | gym | ambos — dónde se puede realizar el ejercicio
    grip: '',               // supino | prono | neutro | mixto | '' (sin especificar)
    is_mandatory: false,    // ⭐ Ejercicio obligatorio (Siempre incluir en la rutina)
    is_welcome_video: false, // 🎬 Video de bienvenida (se muestra en el modal a nuevas clientas)
    replacement_video_ids: [] // 🔁 IDs de videos marcados como reemplazo
  });
  const [videosList, setVideosList] = useState([]);
  const [videoNameAudit, setVideoNameAudit] = useState({}); // { videoId: { status, video_title, closest_routine_name } }
  const [loadingVideos, setLoadingVideos] = useState(false);
  // 2026-02 · Categorías de video personalizadas (creadas por la admin)
  const [customVideoCategories, setCustomVideoCategories] = useState([]);
  const [showVideoCategoryManager, setShowVideoCategoryManager] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showBulkTagEditor, setShowBulkTagEditor] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState(''); // 🔍 Buscador en vivo de videos (insensible a mayúsculas/acentos)
  const [videoTagFilter, setVideoTagFilter] = useState(''); // '' | 'health-ok' | 'health-error' | 'formato-mov' | 'pesado' | 'nombre-errado'
  const [planViewerEmail, setPlanViewerEmail] = useState(null); // Email del usuario a mostrar en el modal Ver/Editar Plan
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Estados de ordenamiento global
  const [sortConfig, setSortConfig] = useState({
    videos: { field: 'title', order: 'asc' },
    ebooks: { field: 'title', order: 'asc' },
    recetarios: { field: 'title', order: 'asc' },
    productos: { field: 'name', order: 'asc' },
    vlogs: { field: 'title', order: 'asc' },
    programs: { field: 'name', order: 'asc' }
  });

  // Función de ordenamiento genérica
  const sortItems = (items, type) => {
    const config = sortConfig[type];
    if (!config || !items) return items;
    
    return [...items].sort((a, b) => {
      let aVal = a[config.field];
      let bVal = b[config.field];
      
      // Manejar fechas
      if (config.field === 'created_at' || config.field === 'updated_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Manejar strings
      else if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      // Manejar números
      else if (typeof aVal === 'number') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      
      if (aVal < bVal) return config.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return config.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Componente de selector de ordenamiento
  const SortSelector = ({ type, options }) => (
    <div className="flex items-center gap-2">
      <select
        value={`${sortConfig[type].field}-${sortConfig[type].order}`}
        onChange={(e) => {
          const [field, order] = e.target.value.split('-');
          setSortConfig(prev => ({ ...prev, [type]: { field, order } }));
        }}
        className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 bg-gray-800 text-gray-200 focus:border-pink-500 focus:outline-none"
      >
        {options.map(opt => (
          <optgroup key={opt.field} label={opt.label}>
            <option value={`${opt.field}-asc`}>↑ {opt.label} (A-Z)</option>
            <option value={`${opt.field}-desc`}>↓ {opt.label} (Z-A)</option>
          </optgroup>
        ))}
      </select>
    </div>
  );

  // Planes disponibles
  const PLAN_OPTIONS = [
    { id: 'free', name: 'Usuarios Gratis', color: 'bg-gray-500' },
    { id: 'basic', name: 'Fit Evolution', color: 'bg-blue-500' },
    { id: 'premium', name: 'Plan Premium', color: 'bg-purple-500' },
    { id: 'vip_gold', name: 'VIP Gold', color: 'bg-yellow-500' },
    { id: 'competitivo', name: 'Competitivo', color: 'bg-red-500' }
  ];

  // States para Ebooks
  const [ebookData, setEbookData] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '', // Precio gancho (tachado)
    category: 'nutricion',
    file_url: '',
    thumbnail_url: '',
    file_source: 'file', // 'file' o 'url'
    is_free: false,
    allowed_plans: [] // ['basico', 'premium', 'vip_gold', 'competitivo']
  });
  const [ebooksList, setEbooksList] = useState([]);
  const [loadingEbooks, setLoadingEbooks] = useState(false);
  const [editingEbook, setEditingEbook] = useState(null);
  const [ebookThumbnailFile, setEbookThumbnailFile] = useState(null);
  const [ebookFile, setEbookFile] = useState(null); // Archivo PDF
  const [uploadingEbook, setUploadingEbook] = useState(false);

  // States para Planes
  const [planData, setPlanData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    plan_type: '',
    features: ''
  });
  const [plansList, setPlansList] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  // States para Códigos Promocionales
  const [promoData, setPromoData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    fixed_value_cop: '',
    fixed_value_usd: '',
    max_uses: '',
    expires_at: '',
    applicable_to: 'all',
    is_active: true
  });
  const [promosList, setPromosList] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  // States para Vlogs
  const [vlogData, setVlogData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: ''
  });
  const [vlogsList, setVlogsList] = useState([]);
  const [loadingVlogs, setLoadingVlogs] = useState(false);
  const [editingVlog, setEditingVlog] = useState(null);
  const [vlogFile, setVlogFile] = useState(null);
  const [vlogThumbnailFile, setVlogThumbnailFile] = useState(null);
  const [uploadingVlog, setUploadingVlog] = useState(false);

  // States para Recetarios en Video
  const [recetarioData, setRecetarioData] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '', // Precio gancho (tachado)
    duration: '',
    recipes_count: '',
    video_url: '',
    thumbnail_url: '',
    video_source: 'drive', // 'file', 'youtube', 'drive'
    is_free: false
  });
  const [recetariosList, setRecetariosList] = useState([]);
  const [loadingRecetarios, setLoadingRecetarios] = useState(false);
  const [editingRecetario, setEditingRecetario] = useState(null);
  const [recetarioThumbnailFile, setRecetarioThumbnailFile] = useState(null);
  const [uploadingRecetario, setUploadingRecetario] = useState(false);

  // States para Tienda (Productos)
  const [productoData, setProductoData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '', // Precio gancho (tachado)
    category: 'suplementos',
    stock: '',
    thumbnail_url: '',
    is_free: false
  });
  const [productosList, setProductosList] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [productoThumbnailFile, setProductoThumbnailFile] = useState(null);
  const [uploadingProducto, setUploadingProducto] = useState(false);

  // States para Galería de Medios
  const [mediaFiles, setMediaFiles] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaCategory, setMediaCategory] = useState('general');
  const [mediaSize, setMediaSize] = useState('medium');
  const [mediaPosition, setMediaPosition] = useState('center');
  const [mediaUsedIn, setMediaUsedIn] = useState('');
  const [mediaDescription, setMediaDescription] = useState('');
  const [mediaFilterCategory, setMediaFilterCategory] = useState('all');

  // States para Programas de Entrenamiento (MEJORADO)
  const [programData, setProgramData] = useState({
    name: '',
    description: '',
    short_description: '',
    price: '',
    original_price: '',
    duration_weeks: '',
    level: 'intermedio',
    category: 'fuerza',
    features: '',
    benefits: '',
    what_includes: '',
    requirements: '',
    discount_code: '',
    access_type: 'paid',
    purchase_type: 'one_time',
    access_days: '',
    free_for: [],
    upgrade_required: '',
    is_featured: false,
    is_active: true,
    preview_video_url: ''
  });
  const [programsList, setProgramsList] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programImageFile, setProgramImageFile] = useState(null);
  const [uploadingProgram, setUploadingProgram] = useState(false);

  // States para Gestión de Servicios/Módulos de la Plataforma
  const [platformModules, setPlatformModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  // availablePlans se cargará dinámicamente desde subscription_plans
  const [availablePlans, setAvailablePlans] = useState([]);
  const [moduleData, setModuleData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'FaDumbbell',
    path: '',
    allowed_plans: [],
    is_active: true,
    is_quick_action: false,
    badge: '',
    order: 0,
    category: 'program'
  });

  // States para Planes de Suscripción
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingSubscriptionPlans, setLoadingSubscriptionPlans] = useState(false);
  const [editingSubscriptionPlan, setEditingSubscriptionPlan] = useState(null);
  const [subscriptionPlanData, setSubscriptionPlanData] = useState({
    name: '',
    slug: '',
    price_monthly: '',
    price_bimonthly: '',
    price_quarterly: '',
    price_semiannual: '',
    price_yearly: '',
    price_monthly_cop: '',
    price_bimonthly_cop: '',
    price_quarterly_cop: '',
    price_semiannual_cop: '',
    price_yearly_cop: '',
    // Precios gancho (precio tachado - opcional)
    original_price_monthly: '',
    original_price_bimonthly: '',
    original_price_quarterly: '',
    original_price_semiannual: '',
    original_price_yearly: '',
    original_price_monthly_cop: '',
    original_price_bimonthly_cop: '',
    original_price_quarterly_cop: '',
    original_price_semiannual_cop: '',
    original_price_yearly_cop: '',
    currency: 'USD',
    color: '#FF69B4',
    gradient_from: '#FF69B4',
    gradient_to: 'var(--brand-primary)',
    icon: '💎',
    badge: '',
    popular: false,
    description: '',
    features: [],
    order: 1,
    is_active: true,
    background_image: '', // URL de imagen de fondo para PDFs
    cover_image: '', // URL de imagen de portada para la tarjeta del plan
    // Nuevo: disponible para clientes VIP
    available_for_vip: true,
    // Nuevo: permisos de acceso a servicios
    permissions: {
      videos: true,
      ebooks: true,
      recetarios: true,
      my_diet: true,
      my_workout: true,
      data_progress: true,
      gold_calendar: false,
      recipe_ideas: true,
      appointments: false
    }
  });
  const [newFeature, setNewFeature] = useState('');
  const [uploadingPlanBackground, setUploadingPlanBackground] = useState(false);

  // States para Cuentas de Prueba
  const [testAccountData, setTestAccountData] = useState({
    email: '',
    password: 'test123',
    name: '',
    subscription_plan: 'basic',
    gender: 'female',  // 'male' para Guerrero, 'female' para Guerrera
    is_protected: false  // Cuenta protegida no se puede eliminar
  });

  // States para Progreso de Usuarios
  const [usersProgress, setUsersProgress] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [selectedUserProgress, setSelectedUserProgress] = useState(null);
  const [expandedProgressEntry, setExpandedProgressEntry] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  // Filtro por tipo de programa en la tabla de Progreso
  const [progressFilterProgram, setProgressFilterProgram] = useState('all');

  // States para Usuarios
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // States para Ingresos/Revenue
  const [revenueData, setRevenueData] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [revenueTab, setRevenueTab] = useState('resumen'); // resumen, transacciones, estadisticas, pasarelas, metodos-manuales

  // States para Estadísticas Mensuales
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loadingMonthlyStats, setLoadingMonthlyStats] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  // States para Notificaciones (MEJORADO)
  const [notificationData, setNotificationData] = useState({
    title: '',
    content_type: 'video',
    section: 'entrenamiento',
    access_levels: ['free'],
    price: '',
    message_preview: ''
  });
  const [notificationsList, setNotificationsList] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);

  // States para Configuración del Sitio
  const [siteConfig, setSiteConfig] = useState({
    theme_color: '#FF69B4',
    secondary_color: '#000000',
    hero_image: '',
    logo_url: '',
    about_text: '',
    // Textos del Hero
    hero_title_line1: '',
    hero_title_line2: '',
    hero_title_line3: '',
    hero_subtitle: '',
    hero_highlight: '',
    // Nueva configuración de diseño
    primary_font: 'Oswald',
    body_font: 'DM Sans',
    heading_font: 'Oswald',
    button_style: 'rounded-full',
    card_style: 'rounded-lg',
    bg_color: '#0a0a0a',
    text_color: '#ffffff',
    accent_color: 'var(--brand-primary-strong)',
    gradient_start: '#FF69B4',
    gradient_end: 'var(--brand-primary-strong)',
    // Colores de texto según fondo
    text_on_dark: '#FFFFFF',
    text_on_light: '#1a1a1a',
    // Portadas de programas
    program_diet_image: '',
    program_workout_image: '',
    program_progress_image: '',
    program_videos_image: '',
    program_ebooks_image: '',
    program_recipes_image: '',
    program_menu_image: '',
    program_data_image: '',
    program_recetarios_image: '',
    // Portadas específicas por género (Dashboard) — Mujeres
    program_diet_image_female: '',
    program_workout_image_female: '',
    program_progress_image_female: '',
    program_videos_image_female: '',
    // Portadas específicas por género (Dashboard) — Hombres
    program_diet_image_male: '',
    program_workout_image_male: '',
    program_progress_image_male: '',
    program_videos_image_male: '',
    // ═════ Portadas por CATEGORÍA DE PLAN + GÉNERO (16 campos) ═════
    // Categoría AUTOMÁTICO (Fit Evolution)
    image_automatic_female_diet: '',
    image_automatic_male_diet: '',
    image_automatic_female_workout: '',
    image_automatic_male_workout: '',
    image_automatic_female_progress: '',
    image_automatic_male_progress: '',
    image_automatic_female_videos: '',
    image_automatic_male_videos: '',
    // Categoría PERSONALIZADO (Premium / Competitivo / Gold VIP)
    image_personalized_female_diet: '',
    image_personalized_male_diet: '',
    image_personalized_female_workout: '',
    image_personalized_male_workout: '',
    image_personalized_female_progress: '',
    image_personalized_male_progress: '',
    image_personalized_female_videos: '',
    image_personalized_male_videos: ''
  });
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Lista de fuentes disponibles
  const availableFonts = [
    { name: 'Oswald', category: 'Display', style: 'Bold & Condensed' },
    { name: 'Bebas Neue', category: 'Display', style: 'Bold & Impact' },
    { name: 'Montserrat', category: 'Sans-serif', style: 'Modern & Clean' },
    { name: 'Poppins', category: 'Sans-serif', style: 'Geometric & Friendly' },
    { name: 'DM Sans', category: 'Sans-serif', style: 'Rounded & Legible' },
    { name: 'Roboto', category: 'Sans-serif', style: 'Neutral & Professional' },
    { name: 'Playfair Display', category: 'Serif', style: 'Elegant & Classic' },
    { name: 'Raleway', category: 'Sans-serif', style: 'Thin & Elegant' },
    { name: 'Anton', category: 'Display', style: 'Ultra Bold' },
    { name: 'Archivo Black', category: 'Display', style: 'Heavy & Strong' },
    { name: 'Barlow Condensed', category: 'Display', style: 'Condensed & Modern' },
    { name: 'Fjalla One', category: 'Display', style: 'Tall & Strong' },
    { name: 'Teko', category: 'Display', style: 'Narrow & Bold' },
    { name: 'Staatliches', category: 'Display', style: 'Blocky & Bold' }
  ];

  // States para Formularios de Usuarios
  const [formSubmissions, setFormSubmissions] = useState([]);
  // Selector manual de sexo para regenerar rutina por usuario (evita el bug histórico)
  // Map: { [submissionId]: 'auto' | 'hombre' | 'mujer' }
  const [regenSexOverride, setRegenSexOverride] = useState({});
  const [formStats, setFormStats] = useState({
    total_all: 0,
    total_pending: 0,
    total_released: 0,
    total_workout: 0,
    total_diet: 0,
    by_plan: {}
  });
  const [loadingFormSubmissions, setLoadingFormSubmissions] = useState(false);
  const [formFilter, setFormFilter] = useState('all'); // all, pending_release, released
  const [formTypeFilter, setFormTypeFilter] = useState('all'); // all, workout, diet
  const [releasingForm, setReleasingForm] = useState(null);
  const [expandedForm, setExpandedForm] = useState(null);
  const [formViewMode, setFormViewMode] = useState('pending'); // pending, all
  const [formSectionTab, setFormSectionTab] = useState('responses'); // responses, editor, clinical
  const [pendingReleasesGlobal, setPendingReleasesGlobal] = useState(0); // count global visible desde cualquier sección
  const [formSearchQuery, setFormSearchQuery] = useState(''); // búsqueda por nombre o email del usuario
  const [feProgressByEmail, setFeProgressByEmail] = useState({}); // { email: { overall_pct, is_ready, ... } }
  // 2026-02 · Vista unificada por cliente (formulario inicial + actualizaciones + peso)
  const [profileModalEmail, setProfileModalEmail] = useState(null);
  // 2026-02 · Modal de gestión rápida de suplementación (uses_supplements)
  const [supplementsManagerOpen, setSupplementsManagerOpen] = useState(false);
  
  // States para Formularios Clínicos
  const [clinicalFormAssignments, setClinicalFormAssignments] = useState([]);
  const [clinicalFormStats, setClinicalFormStats] = useState({ total: 0, pending: 0, completed: 0, with_red_flags: 0 });
  const [loadingClinicalForms, setLoadingClinicalForms] = useState(false);
  const [expandedClinicalForm, setExpandedClinicalForm] = useState(null);
  
  // Cargar formularios clínicos
  const fetchPendingReleasesCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/form-submissions?status=pending_release`);
      const subs = res.data?.submissions || [];
      setPendingReleasesGlobal(subs.length);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { fetchPendingReleasesCount(); const iv = setInterval(fetchPendingReleasesCount, 60000); return () => clearInterval(iv); }, [fetchPendingReleasesCount]);

  const fetchClinicalFormAssignments = async () => {
    setLoadingClinicalForms(true);
    try {
      const response = await axios.get(`${API}/clinical-forms/admin/all`);
      if (response.data) {
        setClinicalFormAssignments(response.data.assignments || []);
        setClinicalFormStats(response.data.stats || { total: 0, pending: 0, completed: 0, with_red_flags: 0 });
      }
    } catch (error) {
      console.error('Error fetching clinical forms:', error);
    } finally {
      setLoadingClinicalForms(false);
    }
  };

  const handleDeleteClinicalForm = async (assignmentId) => {
    if (!window.confirm('¿Estás segura de eliminar este formulario clínico?')) return;
    try {
      await axios.delete(`${API}/clinical-forms/admin/assignment/${assignmentId}`);
      toast.success('Formulario clínico eliminado');
      fetchClinicalFormAssignments();
    } catch (error) {
      console.error('Error deleting clinical form:', error);
      toast.error('Error al eliminar el formulario');
    }
  };

  useEffect(() => {
    fetchVideos();
    fetchCustomVideoCategories();
    fetchEbooks();
    fetchPlans();
    fetchPromoCodes();
    fetchVlogs();
    fetchUsers();
    fetchNotifications();
    fetchSiteConfig();
    fetchMedia();
    fetchPrograms();
    fetchDashboardStats();
    fetchSubscriptionPlans();
    fetchModules();
    fetchProductos();
    fetchRecetarios();
  }, []);

  // Cargar progreso cuando se entra a la sección
  useEffect(() => {
    if (activeSection === 'progreso') {
      fetchUsersProgress();
    }
    if (activeSection === 'dashboard') {
      fetchDashboardStats();
    }
    if (activeSection === 'planes-suscripcion') {
      fetchSubscriptionPlans();
    }
    if (activeSection === 'formularios') {
      fetchFormSubmissions();
      fetchClinicalFormAssignments();
    }
    if (activeSection === 'ingresos') {
      fetchRevenueData();
      fetchMonthlyStats();
    }
    if (activeSection === 'servicios') {
      fetchModules();
    }
    if (activeSection === 'tienda') {
      fetchProductos();
    }
  }, [activeSection]);

  // ========== REVENUE / INGRESOS ==========
  const fetchRevenueData = async () => {
    setLoadingRevenue(true);
    try {
      const response = await axios.get(`${API}/admin/revenue`, { withCredentials: false });
      setRevenueData(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
      toast.error('Error al cargar datos de ingresos');
    } finally {
      setLoadingRevenue(false);
    }
  };

  // Cargar estadísticas mensuales
  const fetchMonthlyStats = async () => {
    setLoadingMonthlyStats(true);
    try {
      const response = await axios.get(`${API}/admin/revenue/monthly-stats`, { withCredentials: false });
      setMonthlyStats(response.data);
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      toast.error('Error al cargar estadísticas mensuales');
    } finally {
      setLoadingMonthlyStats(false);
    }
  };

  // Eliminar una transacción individual
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('¿Estás segura de eliminar esta transacción de PRUEBA? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/transactions/${transactionId}`, { withCredentials: false });
      toast.success('Transacción eliminada');
      fetchRevenueData(); // Recargar datos
      fetchMonthlyStats(); // Recargar estadísticas
    } catch (error) {
      console.error('Error deleting transaction:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar transacción';
      toast.error(errorMsg);
    }
  };

  // Marcar transacción como pagada manualmente
  const handleMarkAsPaid = async (transactionId) => {
    if (!window.confirm('¿Confirmar que este pago fue recibido? Se marcará como completado y se enviarán notificaciones.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/transactions/${transactionId}/mark-paid`, {}, { withCredentials: false });
      toast.success(response.data.message || '✅ Transacción marcada como pagada');
      fetchRevenueData(); // Recargar datos
      fetchMonthlyStats(); // Recargar estadísticas
    } catch (error) {
      console.error('Error marking transaction as paid:', error);
      toast.error(error.response?.data?.detail || 'Error al marcar como pagada');
    }
  };

  // Verificar o Rechazar un pago manual (pending_verification)
  const handleMarkPurchase = async (purchaseId, action) => {
    const actionLabel = action === 'verified' ? 'Verificar' : 'Rechazar';
    let reason = '';
    if (action === 'rejected') {
      reason = window.prompt('Motivo del rechazo (opcional):', '') || '';
    } else {
      if (!window.confirm('¿Confirmas que el comprobante es válido? Se activará la cuenta y se enviará email al cliente.')) return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/purchases/${purchaseId}/mark`,
        { action, reason },
        { withCredentials: false }
      );
      if (response.data.success) {
        toast.success(`✅ Pago ${actionLabel === 'Verificar' ? 'verificado' : 'rechazado'} correctamente`);
        fetchRevenueData();
        fetchMonthlyStats();
      }
    } catch (error) {
      console.error('Error marking purchase:', error);
      toast.error(error.response?.data?.detail || `Error al ${actionLabel.toLowerCase()} pago`);
    }
  };

  // ═════ HELPERS panel de Ingresos ═════

  // Etiquetas dinámicas de un video basadas en su URL real y metadata.
  // Devuelve una lista de badges { key, label, color } listos para pintar.
  //   - health-ok / health-error / health-unknown → indicador de reproducción real
  //   - FORMATO .MOV (naranja): extensión del archivo es .mov.
  //   - PESADO (rojo): file_size_mb > 33.6.
  //   - Bunny (ámbar): url de Bunny Stream.
  //   - Drive (info azul): mostrado solo si es una URL de Drive real.
  //   - YouTube (rojo): url apunta a YouTube.
  const getVideoTags = (video) => {
    const url = String(video?.video_url || '').toLowerCase().trim();
    const tags = [];
    if (!url) return tags;
    const isSirv = /sirv\.com/.test(url);
    const isDrive = /drive\.google\.com/.test(url);
    const isYouTube = /(youtube\.com|youtu\.be)/.test(url);
    const isBunny = /\.b-cdn\.net\//i.test(url) || /(player|iframe)\.mediadelivery\.net\//i.test(url);
    const isMov = /\.mov(\?|$|#)/.test(url);
    const sizeMb = Number(video?.file_size_mb) || 0;
    const isHeavy = sizeMb > 30;

    if (isBunny) {
      tags.push({ key: 'source-bunny', label: '🐰 Bunny', color: 'bg-amber-500 text-black' });
    }
    if (isSirv) {
      tags.push({ key: 'source-sirv', label: '☁️ Sirv (legacy)', color: 'bg-slate-500 text-white' });
    }
    if (isDrive) {
      tags.push({ key: 'source-drive', label: '📁 Drive', color: 'bg-blue-600 text-white' });
    }
    if (isMov) {
      tags.push({ key: 'formato-mov', label: '🟠 FORMATO .MOV', color: 'bg-orange-500 text-white' });
    }
    if (isHeavy) {
      tags.push({ key: 'pesado', label: `🔴 PESADO (>30MB · ${sizeMb.toFixed(1)}MB)`, color: 'bg-red-600 text-white' });
    }
    if (isYouTube) {
      tags.push({ key: 'source-youtube', label: '▶️ YouTube', color: 'bg-red-600 text-white' });
    }
    // 2026-02 · Estado real de reproducción (poblado por
    // POST /api/admin/videos/check-health).
    const h = video?.health_status;
    if (h === 'ok') tags.push({ key: 'health-ok', label: '✅ Reproduce OK', color: 'bg-emerald-500 text-white' });
    else if (h === 'error') tags.push({ key: 'health-error', label: '❌ NO reproduce', color: 'bg-red-600 text-white' });
    else if (h === 'unknown') tags.push({ key: 'health-unknown', label: '❔ Sin verificar (Drive)', color: 'bg-gray-500 text-white' });
    // ═════ Auditoría de nombres: NOMBRE ERRADO ═════
    // Un video se etiqueta como NOMBRE ERRADO cuando su título no matchea
    // EXACTAMENTE (ignorando mayúsculas y acentos) el nombre de ningún
    // ejercicio en las rutinas activas de la biblioteca.
    const audit = videoNameAudit[video?.id];
    if (audit && audit.status === 'mismatch') {
      const label = audit.closest_routine_name
        ? `⚠️ NOMBRE ERRADO · Rutina: "${audit.closest_routine_name}" / Biblioteca: "${audit.video_title}"`
        : `⚠️ NOMBRE ERRADO · Sin match en rutinas activas · Biblioteca: "${audit.video_title}"`;
      tags.push({ key: 'nombre-errado', label, color: 'bg-fuchsia-600 text-white' });
    }
    return tags;
  };

  // Traduce el `mp_status_detail` de Mercado Pago a un mensaje claro en español.
  // Basado en: https://www.mercadopago.com.co/developers/es/docs/checkout-api/handling-responses/status-detail-and-errors
  // Recibe el `tx` completo para poder inferir un motivo cuando MP no envió status_detail.
  const translateMpStatusDetail = (tx) => {
    // Soporte legacy: si vino sólo el código como string, envolverlo.
    const code = typeof tx === 'string' ? tx : (tx?.mp_status_detail || '');
    const paymentStatus = typeof tx === 'string' ? '' : (tx?.payment_status || '');
    const hasMpPaymentId = typeof tx === 'string' ? false : Boolean(tx?.mercadopago_payment_id);

    const MAP = {
      // ═════ RECHAZADOS por la tarjeta / banco ═════
      cc_rejected_insufficient_amount: { label: 'Fondos insuficientes', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_bad_filled_security_code: { label: 'CVV incorrecto', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_bad_filled_date: { label: 'Fecha de vencimiento inválida', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_bad_filled_other: { label: 'Datos de tarjeta incorrectos', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_bad_filled_card_number: { label: 'Número de tarjeta inválido', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_call_for_authorize: { label: 'Debe llamar al banco para autorizar', color: 'bg-yellow-500/20 text-yellow-300' },
      cc_rejected_card_disabled: { label: 'Tarjeta deshabilitada por el banco', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_card_error: { label: 'Error de la tarjeta', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_duplicated_payment: { label: 'Pago duplicado', color: 'bg-purple-500/20 text-purple-300' },
      cc_rejected_high_risk: { label: 'Rechazado por riesgo (antifraude)', color: 'bg-rose-500/20 text-rose-300' },
      cc_rejected_max_attempts: { label: 'Máximo de intentos alcanzado', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_other_reason: { label: 'Rechazado por el banco emisor', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_invalid_installments: { label: 'Cantidad de cuotas no válida', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_blacklist: { label: 'Tarjeta bloqueada por Mercado Pago', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_card_type_not_allowed: { label: 'Tipo de tarjeta no aceptado', color: 'bg-orange-500/20 text-orange-300' },
      cc_rejected_by_bank: { label: 'Rechazado por el banco', color: 'bg-red-500/20 text-red-300' },
      cc_rejected_time_out: { label: 'Tiempo de espera agotado con el banco', color: 'bg-yellow-500/20 text-yellow-300' },
      cc_rejected_3ds_challenge: { label: 'Reto 3D Secure no superado', color: 'bg-yellow-500/20 text-yellow-300' },
      cc_rejected_3ds_mandatory: { label: 'Requiere autenticación 3D Secure', color: 'bg-yellow-500/20 text-yellow-300' },
      // ═════ RECHAZOS no CC (transferencia, pse, efectivo) ═════
      bank_error: { label: 'Error del banco', color: 'bg-red-500/20 text-red-300' },
      rejected_by_bank: { label: 'Rechazado por el banco', color: 'bg-red-500/20 text-red-300' },
      rejected_high_risk: { label: 'Rechazado por riesgo', color: 'bg-rose-500/20 text-rose-300' },
      rejected_insufficient_data: { label: 'Datos del pagador incompletos', color: 'bg-orange-500/20 text-orange-300' },
      expired: { label: 'Medio de pago expiró', color: 'bg-gray-500/20 text-gray-300' },
      by_collector: { label: 'Cancelado por el vendedor', color: 'bg-gray-500/20 text-gray-300' },
      by_payer: { label: 'Cancelado por el comprador', color: 'bg-gray-500/20 text-gray-300' },
      // ═════ PENDIENTES ═════
      pending_contingency: { label: 'En procesamiento por el banco', color: 'bg-amber-500/20 text-amber-300' },
      pending_review_manual: { label: 'En revisión antifraude', color: 'bg-amber-500/20 text-amber-300' },
      pending_waiting_transfer: { label: 'Esperando transferencia bancaria', color: 'bg-blue-500/20 text-blue-300' },
      pending_waiting_payment: { label: 'Cliente no completó el pago', color: 'bg-blue-500/20 text-blue-300' },
      pending_challenge: { label: 'Requiere reto de seguridad 3D', color: 'bg-yellow-500/20 text-yellow-300' },
      pending_capture: { label: 'Autorizado — esperando captura', color: 'bg-blue-500/20 text-blue-300' },
      offline_process: { label: 'Se procesa fuera de línea (24-48h)', color: 'bg-blue-500/20 text-blue-300' },
      partially_refunded: { label: 'Reembolsado parcialmente', color: 'bg-purple-500/20 text-purple-300' },
      refunded: { label: 'Reembolsado', color: 'bg-purple-500/20 text-purple-300' },
      // ═════ APROBADOS ═════
      accredited: { label: 'Acreditado', color: 'bg-emerald-500/20 text-emerald-300' },
      partially_accredited: { label: 'Acreditado parcialmente', color: 'bg-emerald-500/20 text-emerald-300' },
    };

    if (code && MAP[code]) return MAP[code];
    if (code) return { label: code.replace(/_/g, ' '), color: 'bg-gray-500/20 text-gray-300' };

    // ═════ Fallback SIN mp_status_detail: inferir por payment_status ═════
    if (paymentStatus === 'pending' || paymentStatus === 'pending_verification') {
      if (paymentStatus === 'pending_verification') {
        return { label: 'En espera de verificación manual', color: 'bg-amber-500/20 text-amber-300' };
      }
      // Si nunca se inició pago en MP → cliente abandonó
      if (!hasMpPaymentId) {
        return { label: 'Cliente no inició el pago', color: 'bg-slate-500/20 text-slate-300' };
      }
      return { label: 'En procesamiento en Mercado Pago', color: 'bg-amber-500/20 text-amber-300' };
    }
    if (paymentStatus === 'rejected' || paymentStatus === 'failed') {
      return { label: 'Rechazado sin detalle del banco', color: 'bg-red-500/20 text-red-300' };
    }
    if (paymentStatus === 'cancelled') {
      return { label: 'Compra cancelada', color: 'bg-gray-500/20 text-gray-300' };
    }
    if (paymentStatus === 'refunded') {
      return { label: 'Reembolsado', color: 'bg-purple-500/20 text-purple-300' };
    }
    if (paymentStatus === 'completed') {
      return { label: 'Aprobado', color: 'bg-emerald-500/20 text-emerald-300' };
    }
    return { label: 'Sin información', color: 'bg-slate-500/20 text-slate-300' };
  };

  // Construye enlace wa.me con mensaje pre-escrito según estado del pago.
  const buildWhatsAppLink = (tx) => {
    const rawPhone = String(tx.guest_phone || '').replace(/\D/g, '');
    if (!rawPhone) return null;
    const prefix = String(tx.guest_country_code || '+57').replace(/\D/g, '');
    const fullNumber = `${prefix}${rawPhone}`;
    const firstName = (tx.user_name || '').split(' ')[0] || 'hola';
    const isRejected = tx.payment_status === 'rejected' || tx.payment_status === 'failed';
    const message = isRejected
      ? `Hola ${firstName}, vi que tuviste un inconveniente con tu pago en Fit Evolution, ¿cómo puedo ayudarte?`
      : `Hola ${firstName}, te escribo desde Fit Evolution para acompañarte con tu suscripción, ¿cómo estás?`;
    return `https://wa.me/${fullNumber}?text=${encodeURIComponent(message)}`;
  };

  // Reactivar suscripción de una transacción completada
  const handleReactivateSubscription = async (transactionId, userEmail) => {
    if (!window.confirm(`¿Reactivar suscripción para ${userEmail}? Esto actualizará su plan y fechas de suscripción.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/transactions/${transactionId}/reactivate-subscription`, {}, { withCredentials: false });
      toast.success(response.data.message || '✅ Suscripción reactivada correctamente');
      
      // Mostrar detalles
      if (response.data.details) {
        const d = response.data.details;
        toast.info(`Plan: ${d.plan} | Período: ${d.period} | Hasta: ${new Date(d.end_date).toLocaleDateString('es-CO')}`);
      }
      
      fetchRevenueData(); // Recargar datos
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.response?.data?.detail || 'Error al reactivar suscripción');
    }
  };

  // Eliminar todas las transacciones pendientes
  const handleBulkDeleteTransactions = async (status) => {
    const statusLabels = { pending: 'pendientes', failed: 'fallidas', cancelled: 'canceladas' };
    if (!window.confirm(`¿Eliminar TODAS las transacciones ${statusLabels[status]}? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/transactions/bulk-delete?status=${status}`, { withCredentials: false });
      toast.success(response.data.message);
      fetchRevenueData(); // Recargar datos
      fetchMonthlyStats(); // Recargar estadísticas
    } catch (error) {
      console.error('Error bulk deleting transactions:', error);
      toast.error('Error al eliminar transacciones');
    }
  };

  // NUEVO: Eliminar ABSOLUTAMENTE TODAS las transacciones (incluyendo completadas)
  const handleWipeAllTransactions = async () => {
    const confirmText = 'BORRAR TODO';
    const userInput = window.prompt(`⚠️ PELIGRO: Esta acción eliminará TODAS las transacciones incluyendo pagos completados.\n\nEscribe "${confirmText}" para confirmar:`);
    
    if (userInput !== confirmText) {
      toast.error('Operación cancelada - confirmación incorrecta');
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/transactions/wipe-all?confirm=DELETE_ALL_TRANSACTIONS`, { withCredentials: false });
      toast.success(response.data.message);
      fetchRevenueData(); // Recargar datos
      fetchMonthlyStats(); // Recargar estadísticas
    } catch (error) {
      console.error('Error wiping all transactions:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar transacciones');
    }
  };

  // ========== DASHBOARD STATS ==========
  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats`, { withCredentials: false });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // ========== GALERÍA DE MEDIOS ==========
  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await axios.get(`${API}/admin/media`, { withCredentials: false });
      setMediaList(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleUploadMedia = async (e) => {
    e.preventDefault();
    if (!mediaFiles || mediaFiles.length === 0) {
      toast.error('Selecciona al menos una imagen');
      return;
    }

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      Array.from(mediaFiles).forEach(file => {
        formData.append('files', file);
      });
      formData.append('category', mediaCategory);
      formData.append('size', mediaSize);
      formData.append('position', mediaPosition);
      formData.append('used_in', mediaUsedIn || '');
      formData.append('description', mediaDescription || '');

      await axios.post(`${API}/admin/upload-media`, formData, {
        withCredentials: false,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`${mediaFiles.length} imagen(es) subida(s) exitosamente`);
      setMediaFiles(null);
      setMediaUsedIn('');
      setMediaDescription('');
      document.getElementById('media-upload').value = '';
      fetchMedia();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al subir imágenes');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, { withCredentials: false });
      toast.success('Imagen eliminada');
      fetchMedia();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiada al portapapeles');
  };

  // ========== PROGRAMAS DE ENTRENAMIENTO ==========
  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const response = await axios.get(`${API}/admin/programs`, { withCredentials: false });
      setProgramsList(response.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleSubmitProgram = async (e) => {
    e.preventDefault();
    setUploadingProgram(true);
    try {
      const formData = new FormData();
      formData.append('name', programData.name);
      formData.append('description', programData.description);
      formData.append('short_description', programData.short_description || '');
      formData.append('price', programData.price);
      formData.append('original_price', programData.original_price || '');
      formData.append('duration_weeks', programData.duration_weeks);
      formData.append('level', programData.level);
      formData.append('category', programData.category || 'fuerza');
      formData.append('features', programData.features);
      formData.append('benefits', programData.benefits || '');
      formData.append('what_includes', programData.what_includes || '');
      formData.append('requirements', programData.requirements || '');
      formData.append('discount_code', programData.discount_code || '');
      formData.append('access_type', programData.access_type || 'paid');
      formData.append('purchase_type', programData.purchase_type || 'one_time');
      formData.append('access_days', programData.access_days || '');
      formData.append('free_for', programData.free_for?.join(',') || '');
      formData.append('upgrade_required', programData.upgrade_required || '');
      formData.append('is_featured', programData.is_featured);
      formData.append('is_active', programData.is_active);
      formData.append('preview_video_url', programData.preview_video_url || '');
      
      if (programImageFile) {
        formData.append('image_file', programImageFile);
      }

      if (editingProgram) {
        await axios.put(`${API}/admin/programs/${editingProgram.id}`, formData, {
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Programa actualizado');
        setEditingProgram(null);
      } else {
        await axios.post(`${API}/admin/programs`, formData, {
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Programa creado exitosamente');
      }

      setProgramData({
        name: '', description: '', short_description: '', price: '', original_price: '',
        duration_weeks: '', level: 'intermedio', category: 'fuerza', features: '',
        benefits: '', what_includes: '', requirements: '', discount_code: '',
        access_type: 'paid', purchase_type: 'one_time', access_days: '',
        free_for: [], upgrade_required: '', is_featured: false, is_active: true, preview_video_url: ''
      });
      setProgramImageFile(null);
      fetchPrograms();
    } catch (error) {
      toast.error('Error al guardar programa');
      console.error(error);
    } finally {
      setUploadingProgram(false);
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setProgramData({
      name: program.name || '',
      description: program.description || '',
      short_description: program.short_description || '',
      price: program.price?.toString() || '',
      original_price: program.original_price?.toString() || '',
      duration_weeks: program.duration_weeks?.toString() || '',
      level: program.level || 'intermedio',
      category: program.category || 'fuerza',
      features: program.features?.join('\n') || '',
      benefits: program.benefits?.join('\n') || '',
      what_includes: program.what_includes?.join('\n') || '',
      requirements: program.requirements?.join('\n') || '',
      discount_code: program.discount_code || '',
      access_type: program.access_type || 'paid',
      purchase_type: program.purchase_type || 'one_time',
      access_days: program.access_days?.toString() || '',
      free_for: program.free_for || [],
      upgrade_required: program.upgrade_required || '',
      is_featured: program.is_featured || false,
      is_active: program.is_active !== false,
      preview_video_url: program.preview_video_url || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('¿Eliminar este programa?')) return;
    try {
      await axios.delete(`${API}/admin/programs/${programId}`, { withCredentials: false });
      toast.success('Programa eliminado');
      fetchPrograms();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // ========== PLATFORM MODULES CRUD ==========
  const fetchModules = async () => {
    setLoadingModules(true);
    try {
      const response = await axios.get(`${API}/admin/modules`, { withCredentials: false });
      setPlatformModules(response.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Si no hay módulos, inicializar los defaults
      if (error.response?.status === 404) {
        initDefaultModules();
      }
    } finally {
      setLoadingModules(false);
    }
  };

  const initDefaultModules = async () => {
    try {
      await axios.post(`${API}/admin/modules/init-defaults`, {}, { withCredentials: false });
      fetchModules();
      toast.success('Módulos por defecto creados');
    } catch (error) {
      console.error('Error initializing modules:', error);
    }
  };

  const handleSubmitModule = async (e) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await axios.put(`${API}/admin/modules/${editingModule.id}`, moduleData, { withCredentials: false });
        toast.success('Módulo actualizado');
      } else {
        await axios.post(`${API}/admin/modules`, moduleData, { withCredentials: false });
        toast.success('Módulo creado');
      }
      fetchModules();
      resetModuleForm();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar módulo');
    }
  };

  const resetModuleForm = () => {
    setModuleData({
      name: '',
      slug: '',
      description: '',
      icon: 'FaDumbbell',
      path: '',
      allowed_plans: [],
      is_active: true,
      is_quick_action: false,
      badge: '',
      order: 0,
      category: 'program'
    });
    setEditingModule(null);
  };

  const handleEditModule = (module) => {
    setModuleData({
      name: module.name,
      slug: module.slug,
      description: module.description,
      icon: module.icon,
      path: module.path,
      allowed_plans: module.allowed_plans || [],
      is_active: module.is_active,
      is_quick_action: module.is_quick_action,
      badge: module.badge || '',
      order: module.order,
      category: module.category
    });
    setEditingModule(module);
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('¿Estás seguro de eliminar este módulo?')) return;
    
    try {
      await axios.delete(`${API}/admin/modules/${moduleId}`, { withCredentials: false });
      toast.success('Módulo eliminado');
      fetchModules();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Error al eliminar módulo');
    }
  };

  const togglePlanAccess = (planId) => {
    setModuleData(prev => ({
      ...prev,
      allowed_plans: prev.allowed_plans.includes(planId)
        ? prev.allowed_plans.filter(p => p !== planId)
        : [...prev.allowed_plans, planId]
    }));
  };

  // ========== VIDEO CATEGORIES (custom) ==========
  const fetchCustomVideoCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/video-categories`);
      setCustomVideoCategories(res.data?.items || []);
    } catch (e) {
      // silencioso — si el endpoint aún no está listo, el selector sólo
      // muestra las categorías cableadas por defecto.
    }
  };

  // ========== VIDEOS CRUD ==========
  const fetchVideos = async () => {
    setLoadingVideos(true);
    try {
      const response = await axios.get(`${API}/videos?sort=updated_at_desc`, { withCredentials: false });
      setVideosList(response.data);
      // Auditoría de nombres en paralelo (silenciosa — si falla, la etiqueta simplemente no aparece).
      try {
        const audit = await axios.get(`${API}/admin/videos/name-audit`);
        setVideoNameAudit(audit.data?.results || {});
      } catch (e) {
        console.warn('name-audit no disponible:', e?.response?.status);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleSubmitVideo = async (e) => {
    e.preventDefault();
    
    // Si hay archivo de video, usar FormData para subir archivo
    if (videoFile) {
      setUploadingVideo(true);
      try {
        const formData = new FormData();
        formData.append('video_file', videoFile);
        if (thumbnailFile) {
          formData.append('thumbnail_file', thumbnailFile);
        }
        formData.append('title', videoData.title);
        formData.append('description', videoData.description);
        formData.append('category', videoData.category);
        formData.append('is_free', videoData.is_free);
        // === FIT EVOLUTION tags ===
        formData.append('exercise_type', videoData.exercise_type || '');
        formData.append('muscle_group', videoData.muscle_group || '');
        formData.append('muscle_group_especifico', videoData.muscle_group_especifico || '');
        formData.append('ejercicio_base', videoData.ejercicio_base || '');
        formData.append('movement_pattern', videoData.movement_pattern || '');
        formData.append('recommended_sex', videoData.recommended_sex || 'ambos');
        formData.append('location', videoData.location || 'ambos');
        formData.append('grip', videoData.grip || '');
        formData.append('is_mandatory', String(!!videoData.is_mandatory));
        formData.append('is_welcome_video', String(!!videoData.is_welcome_video));

        await axios.post(`${API}/admin/upload-video`, formData, {
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success('Video subido exitosamente');
        setVideoData({ title: '', description: '', video_url: '', thumbnail_url: '', category: '', is_free: false, video_source: 'drive', exercise_type: '', muscle_group: '', recommended_sex: 'ambos', location: 'ambos', is_mandatory: false, is_welcome_video: false });
        setVideoFile(null);
        setThumbnailFile(null);
        fetchVideos();
      } catch (error) {
        toast.error('Error al subir video');
      } finally {
        setUploadingVideo(false);
      }
    } else if (videoData.video_url || editingVideo) {
      // Usar URL (YouTube, Drive, o edición)
      setUploadingVideo(true);
      try {
        const formData = new FormData();
        formData.append('title', videoData.title);
        formData.append('description', videoData.description);
        formData.append('category', videoData.category);
        formData.append('is_free', String(videoData.is_free));
        formData.append('allowed_plans', (videoData.allowed_plans || []).join(','));
        formData.append('video_url', videoData.video_url || '');
        formData.append('video_source', videoData.video_source || 'url');
        // === FIT EVOLUTION tags ===
        formData.append('exercise_type', videoData.exercise_type || '');
        formData.append('muscle_group', videoData.muscle_group || '');
        formData.append('muscle_group_especifico', videoData.muscle_group_especifico || '');
        formData.append('ejercicio_base', videoData.ejercicio_base || '');
        formData.append('movement_pattern', videoData.movement_pattern || '');
        formData.append('recommended_sex', videoData.recommended_sex || 'ambos');
        formData.append('location', videoData.location || 'ambos');
        formData.append('grip', videoData.grip || '');
        formData.append('is_mandatory', String(!!videoData.is_mandatory));
        formData.append('is_welcome_video', String(!!videoData.is_welcome_video));
        formData.append('replacement_video_ids', (videoData.replacement_video_ids || []).join(','));
        
        // Debug log
        console.log('[DEBUG] Enviando video update:', {
          title: videoData.title,
          is_free: videoData.is_free,
          allowed_plans: videoData.allowed_plans,
          allowed_plans_string: (videoData.allowed_plans || []).join(',')
        });
        
        // Enviar thumbnail_url si existe (prioridad sobre archivo)
        if (videoData.thumbnail_url) {
          formData.append('thumbnail_url', videoData.thumbnail_url);
        }
        
        if (thumbnailFile) {
          formData.append('thumbnail_file', thumbnailFile);
        }
        
        if (editingVideo) {
          const response = await axios.put(`${API}/admin/videos/${editingVideo.id}`, formData, {
            withCredentials: false,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('[DEBUG] Response:', response.data);
          toast.success('Video actualizado exitosamente');
          setEditingVideo(null);
        } else {
          await axios.post(`${API}/admin/video-from-url`, formData, {
            withCredentials: false,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Video creado exitosamente');
        }
        
        setVideoData({ title: '', description: '', video_url: '', thumbnail_url: '', category: '', is_free: false, allowed_plans: [], video_source: 'drive', exercise_type: '', muscle_group: '', recommended_sex: 'ambos', location: 'ambos', is_mandatory: false, is_welcome_video: false, replacement_video_ids: [] });
        setThumbnailFile(null);
        fetchVideos();
      } catch (error) {
        console.error('Error video:', error.response?.data || error);
        const errorMsg = error.response?.data?.detail || (editingVideo ? 'Error al actualizar video' : 'Error al crear video');
        toast.error(errorMsg);
      } finally {
        setUploadingVideo(false);
      }
    } else {
      toast.error('Debes subir un archivo de video o proporcionar una URL');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoData({
      title: video.title,
      description: video.description,
      video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || '',
      category: video.category,
      is_free: video.is_free || false,
      allowed_plans: video.allowed_plans || [],
      video_source: video.video_source || 'drive',
      exercise_type: video.exercise_type || '',
      muscle_group: video.muscle_group || '',
      recommended_sex: video.recommended_sex || 'ambos',
      location: video.location || 'ambos',
      is_mandatory: !!video.is_mandatory,
      is_welcome_video: !!video.is_welcome_video,
      replacement_video_ids: Array.isArray(video.replacement_video_ids) ? video.replacement_video_ids : []
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('¿Estás segura de eliminar este video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, { withCredentials: false });
      toast.success('Video eliminado exitosamente');
      fetchVideos();
    } catch (error) {
      toast.error('Error al eliminar video');
    }
  };

  const handleCancelEditVideo = () => {
    setEditingVideo(null);
    setVideoData({ title: '', description: '', video_url: '', thumbnail_url: '', category: '', is_free: false, allowed_plans: [], video_source: 'drive', exercise_type: '', muscle_group: '', recommended_sex: 'ambos', location: 'ambos', is_mandatory: false, is_welcome_video: false, replacement_video_ids: [] });
  };

  // ========== EBOOKS CRUD ==========
  const fetchEbooks = async () => {
    setLoadingEbooks(true);
    try {
      const response = await axios.get(`${API}/ebooks`, { withCredentials: false });
      setEbooksList(response.data);
    } catch (error) {
      console.error('Error fetching ebooks:', error);
    } finally {
      setLoadingEbooks(false);
    }
  };

  const handleSubmitEbook = async (e) => {
    e.preventDefault();
    setUploadingEbook(true);
    try {
      const formData = new FormData();
      formData.append('title', ebookData.title);
      formData.append('description', ebookData.description);
      formData.append('price', ebookData.price);
      formData.append('original_price', ebookData.original_price || '');
      formData.append('category', ebookData.category);
      formData.append('is_free', ebookData.is_free ? 'true' : 'false');
      formData.append('allowed_plans', (ebookData.allowed_plans || []).join(','));
      
      // Si hay archivo PDF, subirlo
      if (ebookFile) {
        formData.append('ebook_file', ebookFile);
      } else if (ebookData.file_url) {
        formData.append('file_url', ebookData.file_url);
      }
      
      // Thumbnail: prioridad URL > archivo
      if (ebookData.thumbnail_url) {
        formData.append('thumbnail_url', ebookData.thumbnail_url);
      }
      if (ebookThumbnailFile) {
        formData.append('thumbnail_file', ebookThumbnailFile);
      }
      
      if (editingEbook) {
        await axios.put(`${API}/admin/upload-ebook/${editingEbook.id}`, formData, { 
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Ebook actualizado exitosamente');
        setEditingEbook(null);
      } else {
        await axios.post(`${API}/admin/upload-ebook`, formData, { 
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Ebook creado exitosamente');
      }
      
      setEbookData({ title: '', description: '', price: '', original_price: '', category: 'nutricion', file_url: '', thumbnail_url: '', file_source: 'file', is_free: false, allowed_plans: [] });
      setEbookThumbnailFile(null);
      setEbookFile(null);
      fetchEbooks();
    } catch (error) {
      console.error('Error:', error);
      toast.error(editingEbook ? 'Error al actualizar ebook' : 'Error al crear ebook');
    } finally {
      setUploadingEbook(false);
    }
  };

  const handleEditEbook = (ebook) => {
    setEditingEbook(ebook);
    setEbookData({
      title: ebook.title,
      description: ebook.description,
      price: ebook.price?.toString() || '',
      original_price: ebook.original_price?.toString() || '',
      category: ebook.category || 'nutricion',
      file_url: ebook.file_url || '',
      thumbnail_url: ebook.thumbnail_url || '',
      file_source: 'url',
      is_free: ebook.is_free || false,
      allowed_plans: ebook.allowed_plans || []
    });
    setEbookThumbnailFile(null);
    setEbookFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteEbook = async (ebookId) => {
    if (!window.confirm('¿Estás segura de eliminar este ebook?')) return;
    try {
      await axios.delete(`${API}/admin/ebooks/${ebookId}`, { withCredentials: false });
      toast.success('Ebook eliminado exitosamente');
      fetchEbooks();
    } catch (error) {
      toast.error('Error al eliminar ebook');
    }
  };

  const handleCancelEditEbook = () => {
    setEditingEbook(null);
    setEbookData({ title: '', description: '', price: '', category: '', file_url: '', thumbnail_url: '', is_free: false, allowed_plans: [] });
    setEbookThumbnailFile(null);
  };

  // ========== PLANS CRUD ==========
  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/plans`, { withCredentials: false });
      setPlansList(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSubmitPlan = async (e) => {
    e.preventDefault();
    try {
      const features = planData.features.split('\n').filter(f => f.trim());
      if (editingPlan) {
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, {
          ...planData,
          price: parseFloat(planData.price),
          features
        }, { withCredentials: false });
        toast.success('Plan actualizado exitosamente');
        setEditingPlan(null);
      } else {
        await axios.post(`${API}/admin/content`, {
          content_type: 'plan',
          data: {
            ...planData,
            price: parseFloat(planData.price),
            features
          }
        });
        toast.success('Plan creado exitosamente');
      }
      setPlanData({ name: '', description: '', price: '', duration: '', plan_type: '', features: '' });
      fetchPlans();
    } catch (error) {
      toast.error(editingPlan ? 'Error al actualizar plan' : 'Error al crear plan');
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanData({
      name: plan.name,
      description: plan.description,
      price: plan.price?.toString() || '',
      duration: plan.duration,
      plan_type: plan.plan_type,
      features: plan.features?.join('\n') || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('¿Estás segura de eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/admin/plans/${planId}`, { withCredentials: false });
      toast.success('Plan eliminado exitosamente');
      fetchPlans();
    } catch (error) {
      toast.error('Error al eliminar plan');
    }
  };

  const handleCancelEditPlan = () => {
    setEditingPlan(null);
    setPlanData({ name: '', description: '', price: '', duration: '', plan_type: '', features: '' });
  };

  // ========== PROMO CODES CRUD ==========
  const fetchPromoCodes = async () => {
    setLoadingPromos(true);
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, { withCredentials: false });
      setPromosList(response.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoadingPromos(false);
    }
  };

  const handleSubmitPromoCode = async (e) => {
    e.preventDefault();
    try {
      // Construir payload con fixed_values si es tipo fijo
      const payload = { ...promoData };
      if (payload.discount_type === 'fixed') {
        payload.fixed_values = {
          COP: parseFloat(payload.fixed_value_cop) || 0,
          USD: parseFloat(payload.fixed_value_usd) || 0
        };
        payload.discount_value = 0; // No se usa para tipo fijo
      }
      delete payload.fixed_value_cop;
      delete payload.fixed_value_usd;
      
      if (editingPromo) {
        await axios.put(`${API}/admin/promo-codes/${editingPromo.id}`, payload, { withCredentials: false });
        toast.success('Código actualizado');
        setEditingPromo(null);
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: false });
        toast.success('Código creado');
      }
      setPromoData({ code: '', discount_type: 'percentage', discount_value: '', fixed_value_cop: '', fixed_value_usd: '', max_uses: '', expires_at: '', applicable_to: 'all', is_active: true });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar código');
    }
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    const expiresDate = promo.expires_at ? new Date(promo.expires_at).toISOString().slice(0, 16) : '';
    const fv = promo.fixed_values || {};
    setPromoData({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: (promo.discount_value || 0).toString(),
      fixed_value_cop: (fv.COP || '').toString(),
      fixed_value_usd: (fv.USD || '').toString(),
      max_uses: promo.max_uses.toString(),
      expires_at: expiresDate,
      applicable_to: promo.applicable_to,
      is_active: promo.is_active
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('¿Eliminar este código promocional?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, { withCredentials: false });
      toast.success('Código eliminado');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCancelEditPromo = () => {
    setEditingPromo(null);
    setPromoData({ code: '', discount_type: 'percentage', discount_value: '', fixed_value_cop: '', fixed_value_usd: '', max_uses: '', expires_at: '', applicable_to: 'all', is_active: true });
  };

  const togglePromoStatus = async (promo) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.id}`, {
        ...promo,
        is_active: !promo.is_active
      }, { withCredentials: false });
      toast.success(`Código ${!promo.is_active ? 'activado' : 'desactivado'}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // ========== VLOGS CRUD ==========
  const fetchVlogs = async () => {
    setLoadingVlogs(true);
    try {
      const response = await axios.get(`${API}/admin/vlogs`, { withCredentials: false });
      setVlogsList(response.data);
    } catch (error) {
      console.error('Error fetching vlogs:', error);
    } finally {
      setLoadingVlogs(false);
    }
  };

  const handleSubmitVlog = async (e) => {
    e.preventDefault();
    setUploadingVlog(true);
    try {
      const formData = new FormData();
      formData.append('title', vlogData.title);
      formData.append('description', vlogData.description);
      formData.append('duration', vlogData.duration);
      
      if (editingVlog) {
        // Actualizar vlog existente
        if (vlogThumbnailFile) {
          formData.append('thumbnail_file', vlogThumbnailFile);
        }
        await axios.put(`${API}/admin/vlogs/${editingVlog.id}`, formData, { 
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Vlog actualizado exitosamente');
        setEditingVlog(null);
      } else {
        // Crear nuevo vlog con archivo
        if (!vlogFile) {
          toast.error('Debes seleccionar un archivo de video');
          setUploadingVlog(false);
          return;
        }
        formData.append('vlog_file', vlogFile);
        if (vlogThumbnailFile) {
          formData.append('thumbnail_file', vlogThumbnailFile);
        }
        await axios.post(`${API}/admin/upload-vlog`, formData, { 
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Vlog subido exitosamente');
      }
      
      // Limpiar formulario
      setVlogData({ title: '', description: '', video_url: '', thumbnail_url: '', duration: '' });
      setVlogFile(null);
      setVlogThumbnailFile(null);
      fetchVlogs();
    } catch (error) {
      console.error('Error con vlog:', error);
      toast.error(editingVlog ? 'Error al actualizar vlog' : 'Error al subir vlog');
    } finally {
      setUploadingVlog(false);
    }
  };

  const handleEditVlog = (vlog) => {
    setEditingVlog(vlog);
    setVlogData({
      title: vlog.title,
      description: vlog.description,
      video_url: vlog.video_url,
      thumbnail_url: vlog.thumbnail_url || '',
      duration: vlog.duration
    });
    setVlogFile(null);
    setVlogThumbnailFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteVlog = async (vlogId) => {
    if (!window.confirm('¿Estás segura de eliminar este vlog?')) return;
    try {
      await axios.delete(`${API}/admin/vlogs/${vlogId}`, { withCredentials: false });
      toast.success('Vlog eliminado exitosamente');
      fetchVlogs();
    } catch (error) {
      toast.error('Error al eliminar vlog');
    }
  };

  const handleCancelEditVlog = () => {
    setEditingVlog(null);
    setVlogData({ title: '', description: '', video_url: '', thumbnail_url: '', duration: '' });
    setVlogFile(null);
    setVlogThumbnailFile(null);
  };

  // ========== PRODUCTOS TIENDA CRUD ==========
  const fetchProductos = async () => {
    setLoadingProductos(true);
    try {
      const response = await axios.get(`${API}/admin/productos`, { withCredentials: false });
      setProductosList(response.data.productos || []);
    } catch (error) {
      console.error('Error fetching productos:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoadingProductos(false);
    }
  };

  const handleSaveProducto = async (e) => {
    e.preventDefault();
    setUploadingProducto(true);
    
    try {
      const formData = new FormData();
      formData.append('name', productoData.name);
      formData.append('description', productoData.description);
      formData.append('price', productoData.price);
      formData.append('original_price', productoData.original_price || '');
      formData.append('category', productoData.category);
      formData.append('stock', productoData.stock || '0');
      formData.append('is_free', productoData.is_free);
      
      if (productoThumbnailFile) {
        formData.append('thumbnail_file', productoThumbnailFile);
      }
      
      if (editingProducto) {
        await axios.put(`${API}/admin/productos/${editingProducto.id}`, formData, {
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producto actualizado exitosamente');
        setEditingProducto(null);
      } else {
        await axios.post(`${API}/admin/productos`, formData, {
          withCredentials: false,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producto creado exitosamente');
      }
      
      setProductoData({ name: '', description: '', price: '', original_price: '', category: 'suplementos', stock: '', thumbnail_url: '', is_free: false });
      setProductoThumbnailFile(null);
      fetchProductos();
    } catch (error) {
      console.error('Error saving producto:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    } finally {
      setUploadingProducto(false);
    }
  };

  const handleEditProducto = (producto) => {
    setEditingProducto(producto);
    setProductoData({
      name: producto.name || '',
      description: producto.description || '',
      price: producto.price?.toString() || '',
      original_price: producto.original_price?.toString() || '',
      category: producto.category || 'suplementos',
      stock: producto.stock?.toString() || '',
      thumbnail_url: producto.thumbnail_url || '',
      is_free: producto.is_free || false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProducto = async (productoId) => {
    if (!window.confirm('¿Estás segura de eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/admin/productos/${productoId}`, { withCredentials: false });
      toast.success('Producto eliminado exitosamente');
      fetchProductos();
    } catch (error) {
      console.error('Error deleting producto:', error);
      toast.error('Error al eliminar producto');
    }
  };

  const handleCancelEditProducto = () => {
    setEditingProducto(null);
    setProductoData({ name: '', description: '', price: '', original_price: '', category: 'suplementos', stock: '', thumbnail_url: '', is_free: false });
    setProductoThumbnailFile(null);
  };

  // ========== RECETARIOS CRUD ==========
  const fetchRecetarios = async () => {
    setLoadingRecetarios(true);
    try {
      const response = await axios.get(`${API}/admin/recetarios`, { withCredentials: false });
      setRecetariosList(response.data.recetarios || []);
    } catch (error) {
      console.error('Error fetching recetarios:', error);
      toast.error('Error al cargar recetarios');
    } finally {
      setLoadingRecetarios(false);
    }
  };

  const handleSaveRecetario = async () => {
    if (!recetarioData.title || !recetarioData.price) {
      toast.error('El título y precio son obligatorios');
      return;
    }
    
    setUploadingRecetario(true);
    try {
      const payload = {
        title: recetarioData.title,
        description: recetarioData.description || '',
        price: parseFloat(recetarioData.price) || 0,
        original_price: recetarioData.original_price ? parseFloat(recetarioData.original_price) : null,
        duration: recetarioData.duration || '2h',
        recipes_count: parseInt(recetarioData.recipes_count) || 10,
        video_url: recetarioData.video_url || '',
        thumbnail_url: recetarioData.thumbnail_url || '',
        is_active: true,
        category: 'general'
      };
      
      if (editingRecetario) {
        await axios.put(`${API}/admin/recetarios/${editingRecetario.id}`, payload, { withCredentials: false });
        toast.success('Recetario actualizado exitosamente');
        setEditingRecetario(null);
      } else {
        await axios.post(`${API}/admin/recetarios`, payload, { withCredentials: false });
        toast.success('Recetario creado exitosamente');
      }
      
      setRecetarioData({ title: '', description: '', price: '', original_price: '', duration: '', recipes_count: '', video_url: '', thumbnail_url: '', video_source: 'drive', is_free: false });
      setRecetarioThumbnailFile(null);
      fetchRecetarios();
    } catch (error) {
      console.error('Error saving recetario:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar recetario');
    } finally {
      setUploadingRecetario(false);
    }
  };

  const handleEditRecetario = (recetario) => {
    setEditingRecetario(recetario);
    setRecetarioData({
      title: recetario.title || '',
      description: recetario.description || '',
      price: recetario.price?.toString() || '',
      original_price: recetario.original_price?.toString() || '',
      duration: recetario.duration || '',
      recipes_count: recetario.recipes_count?.toString() || '',
      video_url: recetario.video_url || '',
      thumbnail_url: recetario.thumbnail_url || '',
      video_source: 'drive',
      is_free: recetario.is_free || false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteRecetario = async (recetarioId) => {
    if (!window.confirm('¿Estás segura de eliminar este recetario?')) return;
    try {
      await axios.delete(`${API}/admin/recetarios/${recetarioId}`, { withCredentials: false });
      toast.success('Recetario eliminado exitosamente');
      fetchRecetarios();
    } catch (error) {
      console.error('Error deleting recetario:', error);
      toast.error('Error al eliminar recetario');
    }
  };

  const handleCancelEditRecetario = () => {
    setEditingRecetario(null);
    setRecetarioData({ title: '', description: '', price: '', original_price: '', duration: '', recipes_count: '', video_url: '', thumbnail_url: '', video_source: 'drive', is_free: false });
    setRecetarioThumbnailFile(null);
  };

  // ========== CUENTAS DE PRUEBA ==========
  const handleCreateTestAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/test-account`, testAccountData, { withCredentials: false });
      toast.success('Cuenta de prueba creada exitosamente');
      setTestAccountData({
        email: '',
        password: 'test123',
        name: '',
        subscription_plan: 'basic'
      });
      fetchUsers(); // Actualizar lista de usuarios
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    }
  };

  // ========== USUARIOS CRUD ==========
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: false });
      setUsersList(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás segura de eliminar la cuenta ${userEmail}?\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: false });
      toast.success('Usuario eliminado exitosamente');
      fetchUsers();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  // ========== PLANES DE SUSCRIPCIÓN ==========
  const fetchSubscriptionPlans = async () => {
    setLoadingSubscriptionPlans(true);
    try {
      // Cache-buster para asegurar que el navegador NO use una respuesta
      // vieja de `/admin/subscription-plans` (evita ver descripción anterior).
      const response = await axios.get(`${API}/admin/subscription-plans?_ts=${Date.now()}`, {
        withCredentials: false,
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      const plans = response.data.plans || [];
      setSubscriptionPlans(plans);
      
      // También cargar planes únicos de usuarios para el Gestor de Servicios
      let userPlansResponse;
      try {
        userPlansResponse = await axios.get(`${API}/admin/user-plans-list`, { withCredentials: false });
      } catch (e) {
        console.log('No user plans endpoint, using subscription plans only');
      }
      
      // Combinar planes de suscripción con planes únicos de usuarios
      const allPlanNames = new Set(['Gratuito', 'free']);
      
      // Agregar nombres de planes de suscripción
      plans.forEach(p => {
        if (p.name) allPlanNames.add(p.name);
      });
      
      // Agregar planes únicos de usuarios (si el endpoint existe)
      if (userPlansResponse?.data?.plans) {
        userPlansResponse.data.plans.forEach(p => {
          if (p && p !== 'free' && p !== 'admin') allPlanNames.add(p);
        });
      }
      
      // Convertir a formato de availablePlans
      const dynamicPlans = Array.from(allPlanNames).map(name => ({
        id: name.toLowerCase().replace(/\s+/g, '_'),
        name: name
      }));
      
      setAvailablePlans(dynamicPlans);
      console.log('Plans loaded for services:', dynamicPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    } finally {
      setLoadingSubscriptionPlans(false);
    }
  };

  const handleSubmitSubscriptionPlan = async (e) => {
    e.preventDefault();
    try {
      const planData = {
        ...subscriptionPlanData,
        // ═════ Descripción: forzar string y preservar saltos de línea ═════
        // El backend guarda tal cual venga; enviamos String(...) para evitar
        // que un valor `undefined`/`null` bloquee el update en Mongo.
        description: String(subscriptionPlanData.description ?? ''),
        price_monthly: parseFloat(subscriptionPlanData.price_monthly) || 0,
        price_bimonthly: parseFloat(subscriptionPlanData.price_bimonthly) || 0,
        price_quarterly: parseFloat(subscriptionPlanData.price_quarterly) || 0,
        price_semiannual: parseFloat(subscriptionPlanData.price_semiannual) || 0,
        price_yearly: parseFloat(subscriptionPlanData.price_yearly) || 0,
        price_monthly_cop: parseFloat(subscriptionPlanData.price_monthly_cop) || 0,
        price_bimonthly_cop: parseFloat(subscriptionPlanData.price_bimonthly_cop) || 0,
        price_quarterly_cop: parseFloat(subscriptionPlanData.price_quarterly_cop) || 0,
        price_semiannual_cop: parseFloat(subscriptionPlanData.price_semiannual_cop) || 0,
        price_yearly_cop: parseFloat(subscriptionPlanData.price_yearly_cop) || 0,
        // Precios gancho (opcionales)
        original_price_monthly: subscriptionPlanData.original_price_monthly ? parseFloat(subscriptionPlanData.original_price_monthly) : null,
        original_price_bimonthly: subscriptionPlanData.original_price_bimonthly ? parseFloat(subscriptionPlanData.original_price_bimonthly) : null,
        original_price_quarterly: subscriptionPlanData.original_price_quarterly ? parseFloat(subscriptionPlanData.original_price_quarterly) : null,
        original_price_semiannual: subscriptionPlanData.original_price_semiannual ? parseFloat(subscriptionPlanData.original_price_semiannual) : null,
        original_price_yearly: subscriptionPlanData.original_price_yearly ? parseFloat(subscriptionPlanData.original_price_yearly) : null,
        original_price_monthly_cop: subscriptionPlanData.original_price_monthly_cop ? parseFloat(subscriptionPlanData.original_price_monthly_cop) : null,
        original_price_bimonthly_cop: subscriptionPlanData.original_price_bimonthly_cop ? parseFloat(subscriptionPlanData.original_price_bimonthly_cop) : null,
        original_price_quarterly_cop: subscriptionPlanData.original_price_quarterly_cop ? parseFloat(subscriptionPlanData.original_price_quarterly_cop) : null,
        original_price_semiannual_cop: subscriptionPlanData.original_price_semiannual_cop ? parseFloat(subscriptionPlanData.original_price_semiannual_cop) : null,
        original_price_yearly_cop: subscriptionPlanData.original_price_yearly_cop ? parseFloat(subscriptionPlanData.original_price_yearly_cop) : null,
        order: parseInt(subscriptionPlanData.order) || 1,
        // Asegurar que background_image se incluya explícitamente
        background_image: subscriptionPlanData.background_image || '',
        cover_image: subscriptionPlanData.cover_image || '',
        available_for_vip: subscriptionPlanData.available_for_vip
      };

      if (editingSubscriptionPlan) {
        // ═════ Optimistic UI: reflejar el cambio en la tarjeta ANTES de refetch ═════
        // Evita ver la descripción vieja durante los ~500ms del round-trip a MongoDB.
        setSubscriptionPlans((prev) => prev.map((p) =>
          p.id === editingSubscriptionPlan.id ? { ...p, ...planData } : p
        ));
        await axios.put(`${API}/admin/subscription-plans/${editingSubscriptionPlan.id}`, planData, { withCredentials: false });
        toast.success('Plan actualizado — descripción y campos guardados');
        setEditingSubscriptionPlan(null);
      } else {
        await axios.post(`${API}/admin/subscription-plans`, planData, { withCredentials: false });
        toast.success('Plan creado exitosamente');
      }

      resetSubscriptionPlanForm();
      // ═════ Refetch garantizado desde la DB (fuerza no-cache) ═════
      await fetchSubscriptionPlans();
    } catch (error) {
      console.error('[SubscriptionPlan] Save error:', error?.response?.data || error);
      toast.error(error?.response?.data?.detail || 'Error al guardar el plan');
    }
  };

  const handleEditSubscriptionPlan = (plan) => {
    setEditingSubscriptionPlan(plan);
    setSubscriptionPlanData({
      name: plan.name || '',
      slug: plan.slug || '',
      price_monthly: plan.price_monthly?.toString() || '',
      price_bimonthly: plan.price_bimonthly?.toString() || '',
      price_quarterly: plan.price_quarterly?.toString() || '',
      price_semiannual: plan.price_semiannual?.toString() || '',
      price_yearly: plan.price_yearly?.toString() || '',
      price_monthly_cop: plan.price_monthly_cop?.toString() || '',
      price_bimonthly_cop: plan.price_bimonthly_cop?.toString() || '',
      price_quarterly_cop: plan.price_quarterly_cop?.toString() || '',
      price_semiannual_cop: plan.price_semiannual_cop?.toString() || '',
      price_yearly_cop: plan.price_yearly_cop?.toString() || '',
      // Precios gancho
      original_price_monthly: plan.original_price_monthly?.toString() || '',
      original_price_bimonthly: plan.original_price_bimonthly?.toString() || '',
      original_price_quarterly: plan.original_price_quarterly?.toString() || '',
      original_price_semiannual: plan.original_price_semiannual?.toString() || '',
      original_price_yearly: plan.original_price_yearly?.toString() || '',
      original_price_monthly_cop: plan.original_price_monthly_cop?.toString() || '',
      original_price_bimonthly_cop: plan.original_price_bimonthly_cop?.toString() || '',
      original_price_quarterly_cop: plan.original_price_quarterly_cop?.toString() || '',
      original_price_semiannual_cop: plan.original_price_semiannual_cop?.toString() || '',
      original_price_yearly_cop: plan.original_price_yearly_cop?.toString() || '',
      currency: plan.currency || 'USD',
      color: plan.color || '#FF69B4',
      gradient_from: plan.gradient_from || '#FF69B4',
      gradient_to: plan.gradient_to || 'var(--brand-primary)',
      icon: plan.icon || '💎',
      badge: plan.badge || '',
      popular: plan.popular || false,
      description: plan.description || '',
      features: plan.features || [],
      order: plan.order || 1,
      is_active: plan.is_active !== false,
      // CRITICAL: Load permissions and VIP availability
      permissions: plan.permissions || {},
      available_for_vip: plan.available_for_vip !== undefined ? plan.available_for_vip : true,
      // Load background image for PDFs
      background_image: plan.background_image || '',
      cover_image: plan.cover_image || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteSubscriptionPlan = async (planId) => {
    if (!window.confirm('¿Estás segura de eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/admin/subscription-plans/${planId}`, { withCredentials: false });
      toast.success('Plan eliminado');
      fetchSubscriptionPlans();
    } catch (error) {
      toast.error('Error al eliminar el plan');
    }
  };

  const resetSubscriptionPlanForm = () => {
    setEditingSubscriptionPlan(null);
    setSubscriptionPlanData({
      name: '',
      slug: '',
      price_monthly: '',
      price_bimonthly: '',
      price_quarterly: '',
      price_semiannual: '',
      price_yearly: '',
      price_monthly_cop: '',
      price_bimonthly_cop: '',
      price_quarterly_cop: '',
      price_semiannual_cop: '',
      price_yearly_cop: '',
      // Precios gancho (tachados)
      original_price_monthly: '',
      original_price_bimonthly: '',
      original_price_quarterly: '',
      original_price_semiannual: '',
      original_price_yearly: '',
      original_price_monthly_cop: '',
      original_price_bimonthly_cop: '',
      original_price_quarterly_cop: '',
      original_price_semiannual_cop: '',
      original_price_yearly_cop: '',
      currency: 'USD',
      color: '#FF69B4',
      gradient_from: '#FF69B4',
      gradient_to: 'var(--brand-primary)',
      icon: '💎',
      badge: '',
      popular: false,
      description: '',
      features: [],
      order: 1,
      is_active: true,
      // Include permissions and VIP availability in reset
      permissions: {
        videos: true,
        ebooks: true,
        recetarios: true,
        my_diet: true,
        my_workout: true,
        data_progress: true,
        gold_calendar: false,
        recipe_ideas: true,
        appointments: false
      },
      available_for_vip: false
    });
    setNewFeature('');
  };

  const addFeatureToSubscriptionPlan = () => {
    if (newFeature.trim()) {
      setSubscriptionPlanData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeatureFromSubscriptionPlan = (index) => {
    setSubscriptionPlanData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // ========== PROGRESO DE USUARIOS ==========
  const fetchUsersProgress = async () => {
    setLoadingProgress(true);
    try {
      const response = await axios.get(`${API}/admin/progress/all`, { withCredentials: false });
      setUsersProgress(response.data.users || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Error al cargar progreso de usuarios');
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleAddComment = async (entryId) => {
    if (!newComment.trim()) {
      toast.error('Escribe un comentario');
      return;
    }
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/admin/progress/comment`, {
        entry_id: entryId,
        comment: newComment
      }, { withCredentials: false });
      toast.success('Comentario enviado');
      setNewComment('');
      fetchUsersProgress();
    } catch (error) {
      toast.error('Error al enviar comentario');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteProgressEntry = async (entryId) => {
    if (!window.confirm('¿Estás segura de eliminar esta entrada de progreso? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/progress/entry/${entryId}`, { withCredentials: false });
      toast.success('Entrada eliminada correctamente');
      fetchUsersProgress();
    } catch (error) {
      console.error('Error deleting progress entry:', error);
      toast.error('Error al eliminar la entrada');
    }
  };

  const handleDeleteAllUserProgress = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás segura de eliminar TODO el progreso de ${userEmail}? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/progress/user/${userId}`, { withCredentials: false });
      toast.success('Progreso del usuario eliminado');
      fetchUsersProgress();
    } catch (error) {
      console.error('Error deleting user progress:', error);
      toast.error('Error al eliminar el progreso');
    }
  };

  // ========== FORMULARIOS DE USUARIOS ==========
  const fetchFormSubmissions = async () => {
    setLoadingFormSubmissions(true);
    try {
      let url = `${API}/admin/form-submissions`;
      const params = new URLSearchParams();
      if (formFilter !== 'all') params.append('status', formFilter);
      if (formTypeFilter !== 'all') params.append('form_type', formTypeFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { withCredentials: false });
      const subs = response.data.submissions || [];
      setFormSubmissions(subs);
      if (response.data.stats) {
        setFormStats(response.data.stats);
      }
      // Cargar progreso Fit Evolution para cada submission tipo 'diet' pendiente
      const pendingDietEmails = Array.from(new Set(
        subs.filter(s => s.form_type === 'diet' && s.status === 'pending_release' && s.user_email)
            .map(s => (s.user_email || '').toLowerCase())
      ));
      if (pendingDietEmails.length > 0) {
        const results = await Promise.all(pendingDietEmails.map(async (email) => {
          try {
            const r = await axios.get(`${API}/personalized-plans/fit-evolution/generation-progress/${encodeURIComponent(email)}`);
            return [email, r.data];
          } catch (e) { return [email, null]; }
        }));
        const map = {};
        for (const [email, data] of results) { if (data) map[email] = data; }
        setFeProgressByEmail(prev => ({ ...prev, ...map }));
      }
    } catch (error) {
      console.error('Error fetching form submissions:', error);
      toast.error('Error al cargar formularios');
    } finally {
      setLoadingFormSubmissions(false);
    }
  };

  // Poller: refrescar progreso Fit Evolution cada 8s para submissions pendientes
  useEffect(() => {
    if (activeSection !== 'formularios') return;
    const pendingDietEmails = Array.from(new Set(
      (formSubmissions || [])
        .filter(s => s.form_type === 'diet' && s.status === 'pending_release' && s.user_email)
        .map(s => (s.user_email || '').toLowerCase())
    ));
    if (pendingDietEmails.length === 0) return;
    const tick = async () => {
      const updates = await Promise.all(pendingDietEmails.map(async (email) => {
        try {
          const r = await axios.get(`${API}/personalized-plans/fit-evolution/generation-progress/${encodeURIComponent(email)}`);
          return [email, r.data];
        } catch (e) { return [email, null]; }
      }));
      const map = {};
      for (const [email, data] of updates) { if (data) map[email] = data; }
      setFeProgressByEmail(prev => ({ ...prev, ...map }));
    };
    const iv = setInterval(tick, 8000);
    return () => clearInterval(iv);
  }, [activeSection, formSubmissions]);

  const handleReleaseForm = async (submissionId) => {
    setReleasingForm(submissionId);
    try {
      await axios.post(`${API}/admin/form-submissions/${submissionId}/release`, {}, { withCredentials: false });
      toast.success('¡Plan liberado exitosamente! El usuario ya puede ver su rutina.');
      fetchFormSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al liberar el plan');
    } finally {
      setReleasingForm(null);
    }
  };

  const handleDeleteFormSubmission = async (submissionId) => {
    if (!window.confirm('¿Estás segura de eliminar este formulario?')) return;
    try {
      await axios.delete(`${API}/admin/form-submissions/${submissionId}`, { withCredentials: false });
      toast.success('Formulario eliminado');
      fetchFormSubmissions();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Efecto para recargar cuando cambian los filtros
  useEffect(() => {
    if (activeSection === 'formularios') {
      fetchFormSubmissions();
    }
  }, [formFilter, formTypeFilter]);

  // Formatear datos del formulario para mostrar
  const formatFormData = (formData, formType) => {
    if (formType === 'workout') {
      return {
        '💪 Nivel': formData?.level || formData?.fitness_level || 'N/A',
        '📅 Días de entrenamiento': formData?.trainingDays || formData?.training_days || 'N/A',
        '⏱️ Duración sesión': formData?.sessionDuration || formData?.session_duration || 'N/A',
        '🎯 Objetivo': formData?.goal || 'N/A',
        '🏋️ Grupos musculares': Array.isArray(formData?.muscleGroups) ? formData.muscleGroups.join(', ') : (formData?.muscleGroups || 'N/A'),
        '⚠️ Ejercicios problemáticos': formData?.problematicExercises || formData?.problematic_exercises || 'Ninguno',
        '🏃 Horarios cardio': Array.isArray(formData?.cardioTimes) ? formData.cardioTimes.join(', ') : (formData?.cardioTimes || 'Sin cardio'),
        '📍 Lugar entrenamiento': formData?.training_location || formData?.trainingLocation || 'N/A'
      };
    }
    if (formType === 'diet') {
      const goalLabels = {
        'lose_weight': 'Perder peso',
        'perder_grasa': 'Perder grasa',
        'perder_peso': 'Perder peso',
        'gain_muscle': 'Ganar músculo',
        'ganar_musculo': 'Ganar músculo',
        'maintain': 'Mantener peso',
        'mantener': 'Mantener peso',
        'recomposicion': 'Recomposición corporal',
      };
      const activityLabels = {
        'sedentary': 'Sedentario',
        'sedentario': 'Sedentario',
        'light': 'Ligero',
        'ligero': 'Ligero',
        'moderate': 'Moderado',
        'moderado': 'Moderado',
        'active': 'Activo',
        'activo': 'Activo',
        'very_active': 'Muy activo',
        'muy_activo': 'Muy activo',
      };
      const trainingLocationLabels = {
        'casa': 'En Casa',
        'gym': 'Gimnasio',
        'ambos': 'Ambos (Casa + Gym)',
      };
      const genderLabel = formData?.gender === 'male' || formData?.gender === 'masculino' ? 'Masculino' : (formData?.gender ? 'Femenino' : 'N/A');
      const tp = formData?.training_profile || {};
      const mp = formData?.metabolic_profile || {};
      const macros = formData?.macros || {};
      const excludedFoods = Array.isArray(formData?.excluded_foods) && formData.excluded_foods.length > 0
        ? formData.excluded_foods.join(', ')
        : (formData?.disliked_foods && formData.disliked_foods.trim() && formData.disliked_foods.trim().toLowerCase() !== 'ninguno' && formData.disliked_foods.trim().toLowerCase() !== 'ninguna'
            ? formData.disliked_foods
            : 'Ninguno');
      const allergies = formData?.allergies && formData.allergies.trim() && !['ninguna','ninguno','no'].includes(formData.allergies.trim().toLowerCase())
        ? formData.allergies
        : 'Ninguna';
      return {
        '👤 Género': genderLabel,
        '📆 Edad': formData?.age ? `${formData.age} años` : 'N/A',
        '⚖️ Peso': formData?.weight ? `${formData.weight} kg` : 'N/A',
        '📏 Altura': formData?.height ? `${formData.height} cm` : 'N/A',
        '🏃 Nivel actividad': activityLabels[formData?.activity_level] || formData?.activity_level || 'N/A',
        '🎯 Objetivo': goalLabels[formData?.goal] || formData?.goal || goalLabels[mp?.goal] || mp?.goal || 'N/A',
        '🍽️ Comidas por día': formData?.meals_per_day || formData?.meals_count || 'N/A',
        '💪 Comida post-entreno': formData?.post_workout_meal ? `Comida #${formData.post_workout_meal}` : 'N/A',
        '🍎 Dieta recomendada': mp?.recommended_diet_label || mp?.recommended_diet || 'N/A',
        '🔥 Calorías objetivo (al enviar el formulario)': macros?.calories ? `${macros.calories} kcal` : 'N/A',
        '🥩 Macros (al enviar el formulario)': (macros?.protein && macros?.carbs && macros?.fats) ? `P ${macros.protein}g · C ${macros.carbs}g · G ${macros.fats}g` : 'N/A',
        '🚫 Alérgenos': allergies,
        '🚫 Alimentos excluidos': excludedFoods,
        '🏋️ Nivel entreno': tp?.level || 'N/A',
        '📅 Días entreno / sem': tp?.training_days || 'N/A',
        '📍 Lugar entreno': trainingLocationLabels[tp?.training_location] || tp?.training_location || 'N/A',
        '⏱️ Duración sesión': tp?.session_duration ? `${tp.session_duration} min` : 'N/A',
        '⚠️ Ejercicios problemáticos': tp?.problematic_exercises || 'Ninguno',
      };
    }
    // Actualización mensual
    if (formType === 'monthly_update') {
      return {
        '⚖️ Peso actual': formData?.current_weight ? `${formData.current_weight} kg` : 'N/A',
        '🩺 Nuevas patologías': formData?.skip_pathologies 
          ? 'Sin cambios' 
          : (Array.isArray(formData?.new_pathologies) && formData.new_pathologies.length > 0 
              ? formData.new_pathologies.join(', ') 
              : 'Ninguna'),
        '➕ Alimentos a agregar': Array.isArray(formData?.foods_to_add) && formData.foods_to_add.length > 0 
          ? formData.foods_to_add.join(', ') 
          : 'Ninguno',
        '➖ Alimentos a quitar': Array.isArray(formData?.foods_to_remove) && formData.foods_to_remove.length > 0 
          ? formData.foods_to_remove.join(', ') 
          : 'Ninguno',
        '🥗 Calificación dieta': formData?.diet_rating ? `${formData.diet_rating}/10` : 'N/A',
        '💪 Calificación rutina': formData?.workout_rating ? `${formData.workout_rating}/10` : 'N/A',
        '💬 Comentarios': formData?.comments || 'Sin comentarios'
      };
    }
    if (formType === 'workout_continuation') {
      const ps = formData?.progress_signal || {};
      return {
        '⚖️ Peso actual': formData?.current_weight ? `${formData.current_weight} kg` : 'N/A',
        '💬 Cómo se sintió': formData?.how_they_felt || 'N/A',
        '🎯 Nuevo objetivo': formData?.new_goal || 'N/A',
        '⚠️ Ejercicios problemáticos': Array.isArray(formData?.problematic_exercises) ? formData.problematic_exercises.join(', ') : (formData?.problematic_exercises || 'Ninguno'),
        '📅 Cambio días de entreno': formData?.training_days_change || 'N/A',
        '🔁 Músculos cambiados': Array.isArray(formData?.changed_muscles) ? formData.changed_muscles.join(', ') : (formData?.changed_muscles || 'N/A'),
        '📈 Resultado percibido': ps?.result_feeling || 'N/A',
        '⚖️ Cambio de peso': (ps?.weight_change !== null && ps?.weight_change !== undefined) ? `${ps.weight_change} kg` : 'N/A',
        '🔥 Quiere más intensidad': ps?.wants_more_intensity ? 'Sí' : 'No',
      };
    }
    if (formType === 'diet_continuation') {
      return {
        '⚖️ Peso actual': formData?.current_weight ? `${formData.current_weight} kg` : 'N/A',
        '💬 Cómo se sintió con la dieta': formData?.diet_feeling || 'N/A',
        '🎯 Nuevo objetivo': formData?.new_goal || 'N/A',
        '🚫 Nuevas alergias': formData?.new_allergies || 'Ninguna',
        '🍽️ Comidas por día': formData?.meals_per_day || 'N/A',
      };
    }
    // Para otros tipos de formularios, mostrar todos los campos
    return Object.entries(formData || {}).reduce((acc, [key, value]) => {
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      acc[formattedKey] = Array.isArray(value) ? value.join(', ') : (value || 'N/A');
      return acc;
    }, {});
  };

  // ========== NOTIFICACIONES ==========
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API}/admin/notifications`, { withCredentials: false });
      // El backend retorna {notifications: [], unread_count: X}
      const data = response.data;
      setNotificationsList(data.notifications || data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotificationsList([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setSendingNotification(true);
    try {
      const response = await axios.post(`${API}/admin/notifications`, {
        title: notificationData.title,
        content_type: notificationData.content_type,
        section: notificationData.section,
        access_levels: notificationData.access_levels,
        price: notificationData.price ? parseFloat(notificationData.price) : null,
        message_preview: notificationData.message_preview
      }, { withCredentials: false });
      
      const { emails_sent, emails_failed, total_users } = response.data;
      toast.success(`✅ Notificación enviada a ${emails_sent}/${total_users} usuarios${emails_failed > 0 ? ` (${emails_failed} fallidos)` : ''}`);
      
      setNotificationData({
        title: '',
        content_type: 'video',
        section: 'entrenamiento',
        access_levels: ['free'],
        price: '',
        message_preview: ''
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar notificación');
    } finally {
      setSendingNotification(false);
    }
  };

  // Eliminar una notificación del historial
  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('¿Estás segura de que quieres eliminar esta notificación del historial?')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, { withCredentials: false });
      toast.success('🗑️ Notificación eliminada');
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Error al eliminar la notificación');
    }
  };

  // ========== CONFIGURACIÓN DEL SITIO ==========
  const fetchSiteConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await axios.get(`${API}/admin/site-config`);
      setSiteConfig(response.data);
    } catch (error) {
      console.error('Error fetching site config:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleUpdateSiteConfig = async (e) => {
    if (e) e.preventDefault();
    try {
      await axios.put(`${API}/admin/site-config`, siteConfig, { withCredentials: false });
      toast.success('Configuración actualizada exitosamente. Los cambios se aplicarán en toda la página.');
      // Refrescar la configuración global para que se aplique inmediatamente
      refreshConfig();
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  // Sidebar menu items - todos con el mismo estilo rosado - ORDEN ALFABÉTICO
  const menuItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard', active: activeSection === 'dashboard' },
    { id: 'calendario-gold', icon: '📅', label: 'Calendario Gold', active: activeSection === 'calendario-gold' },
    { id: 'vip-clients', icon: '👑', label: 'Clientes VIP', active: activeSection === 'vip-clients' },
    { id: 'codigos', icon: '🏷️', label: 'Códigos Descuento', active: activeSection === 'codigos' },
    { id: 'configuracion', icon: '⚙️', label: 'Configuración Sitio', active: activeSection === 'configuracion' },
    { id: 'cuentas-prueba', icon: '🧪', label: 'Cuentas de Prueba', active: activeSection === 'cuentas-prueba' },
    { id: 'diagnosticos', icon: '🩺', label: 'Diagnósticos Clínicos', active: activeSection === 'diagnosticos' },
    { id: 'fit-evolution', icon: '🚀', label: 'Fit Evolution', active: activeSection === 'fit-evolution' },
    { id: 'fe-templates', icon: '🧩', label: 'Plantillas Entrenamiento', active: activeSection === 'fe-templates' },
    { id: 'formularios', icon: '📋', label: 'Formularios', active: activeSection === 'formularios', badge: pendingReleasesGlobal },
    { id: 'galeria', icon: '🖼️', label: 'Galería de Medios', active: activeSection === 'galeria' },
    { id: 'offline-generator', icon: '📄', label: 'Generador Offline', active: activeSection === 'offline-generator' },
    { id: 'food-manager', icon: '🍽️', label: 'Gestión Alimentos', active: activeSection === 'food-manager' },
    { id: 'training-manager', icon: '🏋️', label: 'Gestor Entrenamiento', active: activeSection === 'training-manager' },
    { id: 'servicios', icon: '🧩', label: 'Gestión Servicios', active: activeSection === 'servicios' },
    { id: 'videos', icon: '📹', label: 'Gestor Contenido', active: activeSection === 'videos' || activeSection === 'ebooks' || activeSection === 'recetarios' },
    { id: 'missing-videos', icon: '🔎', label: 'Ejercicios sin video', active: activeSection === 'missing-videos' },
    { id: 'ingresos', icon: '💰', label: 'Ingresos', active: activeSection === 'ingresos' },
    { id: 'notificaciones', icon: '🔔', label: 'Notificaciones', active: activeSection === 'notificaciones' },
    { id: 'pending-plans', icon: '📋', label: 'Planes Pendientes', active: activeSection === 'pending-plans' },
    { id: 'planes-suscripcion', icon: '💎', label: 'Planes Suscripción', active: activeSection === 'planes-suscripcion' },
    { id: 'programas', icon: '💪', label: 'Programas', active: activeSection === 'programas' },
    { id: 'progreso', icon: '📈', label: 'Progreso Usuarios', active: activeSection === 'progreso' },
    { id: 'recipe-library', icon: '📚', label: 'Biblioteca Recetas', active: activeSection === 'recipe-library' },
    { id: 'recipe-videos', icon: '🍳', label: 'Recetarios Video', active: activeSection === 'recipe-videos' },
    { id: 'coach-messages', icon: '💬', label: 'Mensajes Coach', active: activeSection === 'coach-messages' },
    { id: 'recipe-prompts', icon: '✍️', label: 'Estilo Recetas', active: activeSection === 'recipe-prompts' },
    { id: 'tienda', icon: '🛒', label: 'Tienda', active: activeSection === 'tienda' },
    { id: 'usuarios', icon: '👥', label: 'Usuarios', active: activeSection === 'usuarios' },
    { id: 'vlogs', icon: '🎬', label: 'Vlogs', active: activeSection === 'vlogs' },
  ];

  return (
    <div className="flex h-screen admin-electric" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Sidebar - ELECTRIC OBSIDIAN */}
      <div className="w-64 text-white flex flex-col fixed h-full" style={{ background: 'linear-gradient(180deg, #0a0a0a, #111111)' }}>
        {/* Header */}
        <div className="p-6 flex justify-between items-center border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <h1 
            className="text-2xl font-bold bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)', fontFamily: 'Poppins, sans-serif' }}
          >
            Admin Panel
          </h1>
          <div className="flex items-center gap-2">
            <NotificationBell panelPosition="left" />
            <button className="text-gray-400 text-xl hover:text-white transition-colors">✕</button>
          </div>
        </div>

        {/* Menu Items - ELECTRIC OBSIDIAN STYLE */}
        <div className="flex-1 overflow-y-auto py-4 px-3">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all rounded-xl mb-1 ${
                item.active
                  ? 'text-white shadow-lg'
                  : 'text-gray-300 hover:text-white'
              }`}
              style={item.active ? {
                background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                boxShadow: '0 0 20px rgba(var(--brand-primary-glow), 0.4)'
              } : {
                background: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
              {item.badge > 0 && (
                <span className="animate-pulse inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-black text-white" style={{ background: 'linear-gradient(135deg, #ef4444, #b91c1c)', boxShadow: '0 0 12px rgba(239,68,68,0.7)' }} data-testid={`sidebar-badge-${item.id}`}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <button
            onClick={() => window.open('/', '_blank')}
            className="w-full flex items-center gap-2 px-4 py-3 text-white rounded-xl mb-2 text-sm transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          >
            👁️ Ver sitio web
          </button>
          <button
            onClick={() => {
              // Cerrar sesión de ADMIN - limpia localStorage primero
              localStorage.removeItem('deisycfit_admin_token');
              localStorage.removeItem('deisycfit_admin_user');
              localStorage.removeItem('deisycfit_admin_session_ts');
              // Llamar al backend para limpiar cookie
              axios.post(`${API}/auth/admin-logout`, {}, { withCredentials: true })
                .catch(() => {}) // Ignorar errores
                .finally(() => {
                  // Usar onLogout si existe, sino redirigir al login unificado
                  if (onLogout) {
                    onLogout();
                  } else {
                    window.location.href = '/login';
                  }
                });
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-white bg-red-500/80 hover:bg-red-500 rounded-xl text-sm transition-all"
          >
            🚪 Cerrar Sesión Admin
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 ml-64 overflow-y-auto">
        {/* Top Bar - DARK THEME */}
        <div 
          className="border-b px-8 py-4 flex justify-between items-center sticky top-0 z-40 backdrop-blur-xl"
          style={{ 
            background: 'rgba(10, 10, 10, 0.9)',
            borderColor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <div>
            <h2 
              className="text-2xl font-bold text-white uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {activeSection === 'videos' ? 'Gestor de Contenido' : 
               activeSection === 'ebooks' ? 'Gestor de Contenido' :
               activeSection === 'recetarios' ? 'Gestor de Contenido' :
               activeSection === 'tienda' ? 'Gestión de Tienda' :
               activeSection === 'planes' ? 'Planes' :
               activeSection === 'codigos' ? 'Códigos Promocionales' :
               activeSection === 'vlogs' ? 'Vlogs' :
               activeSection === 'programas' ? 'Programas de Entrenamiento' :
               activeSection === 'galeria' ? 'Galería de Medios' :
               activeSection === 'cuentas-prueba' ? 'Cuentas de Prueba' :
               activeSection === 'diagnosticos' ? 'Diagnósticos Clínicos' :
               activeSection === 'fit-evolution' ? 'Fit Evolution — Progreso' :
               activeSection === 'fe-templates' ? 'Plantillas de Entrenamiento' :
              activeSection === 'recipe-library' ? '📚 Biblioteca de Recetas' :
              activeSection === 'coach-messages' ? '💬 Mensajes de tu Coach' :
              activeSection === 'recipe-prompts' ? '✍️ Estilo de Recetas' :
               activeSection === 'formularios' ? 'Formularios' :
               activeSection === 'progreso' ? 'Progreso de Usuarios' :
               activeSection === 'usuarios' ? 'Usuarios' :
               activeSection === 'notificaciones' ? 'Notificaciones' :
               activeSection === 'planes-suscripcion' ? 'Planes de Suscripción' :
               activeSection === 'offline-generator' ? 'Generador Offline' :
               activeSection === 'food-manager' ? 'Gestión de Alimentos' :
               activeSection === 'training-manager' ? 'Gestor de Entrenamiento' :
               activeSection === 'vip-clients' ? 'Clientes VIP / Offline' :
               activeSection === 'pending-plans' ? 'Planes Personalizados Pendientes' :
               activeSection === 'ingresos' ? 'Gestión de Ingresos' :
               activeSection === 'servicios' ? 'Gestión de Servicios' :
               activeSection === 'calendario-gold' ? 'Calendario Gold' :
               activeSection === 'configuracion' ? 'Configuración del Sitio' : 'Dashboard'}
            </h2>
            <p className="text-sm text-gray-400">DeisyCFit - Panel de Administración</p>
          </div>
          <button 
            onClick={() => window.open('/', '_blank')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <span className="text-sm">Ver sitio web</span>
            <span>👁️</span>
          </button>
        </div>

        {/* Content - DARK BACKGROUND */}
        <div className="p-8" style={{ minHeight: 'calc(100vh - 80px)' }}>
          {/* Banner rojo global: planes pendientes de liberar */}
          {pendingReleasesGlobal > 0 && activeSection !== 'formularios' && (
            <div
              data-testid="pending-releases-banner"
              className="mb-6 rounded-2xl p-5 flex items-center justify-between gap-4 animate-pulse-slow"
              style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.20), rgba(185,28,28,0.15))', border: '2px solid rgba(239,68,68,0.5)', boxShadow: '0 0 24px rgba(239,68,68,0.25)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ background: 'rgba(239,68,68,0.3)' }}>
                  🔥
                </div>
                <div>
                  <p className="text-white font-black text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {pendingReleasesGlobal} {pendingReleasesGlobal === 1 ? 'plan Fit Evolution esperando' : 'planes Fit Evolution esperando'} tu liberación
                  </p>
                  <p className="text-red-200 text-sm">
                    Tus clientas ya llenaron su formulario. Ve a Formularios y presiona &ldquo;Liberar Plan&rdquo; para que reciban su dieta y rutina.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveSection('formularios')}
                data-testid="banner-goto-formularios"
                className="px-5 py-3 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #ef4444, #b91c1c)' }}
              >
                Ir a liberar →
              </button>
            </div>
          )}

          {/* SECCIÓN PLANES PERSONALIZADOS PENDIENTES */}
          {activeSection === 'pending-plans' && (
            <PendingPlansManager />
          )}

          {/* SECCIÓN FIT EVOLUTION - PROGRESO INDIVIDUAL */}
          {activeSection === 'fit-evolution' && (
            <FitEvolutionAdminManager />
          )}

          {/* SECCIÓN FIT EVOLUTION - PLANTILLAS DE ENTRENAMIENTO */}
          {activeSection === 'fe-templates' && (
            <FitEvolutionTemplateManager />
          )}

          {/* SECCIÓN INGRESOS */}
          {activeSection === 'ingresos' && (
            <div className="space-y-6">
              {/* Header con tabs */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button 
                  onClick={() => setRevenueTab('resumen')}
                  className={revenueTab === 'resumen' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'text-gray-300 hover:text-white'}
                  style={revenueTab !== 'resumen' ? { backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                >
                  📊 Resumen
                </Button>
                <Button 
                  onClick={() => setRevenueTab('estadisticas')}
                  className={revenueTab === 'estadisticas' ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' : 'text-gray-300 hover:text-white'}
                  style={revenueTab !== 'estadisticas' ? { backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                >
                  📅 Estadísticas Mensuales
                </Button>
                <Button 
                  onClick={() => setRevenueTab('transacciones')}
                  className={revenueTab === 'transacciones' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white' : 'text-gray-300 hover:text-white'}
                  style={revenueTab !== 'transacciones' ? { backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                >
                  📋 Transacciones
                </Button>
                <Button 
                  onClick={() => setRevenueTab('pasarelas')}
                  className={revenueTab === 'pasarelas' ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white' : 'text-gray-300 hover:text-white'}
                  style={revenueTab !== 'pasarelas' ? { backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                >
                  💳 Pasarelas de Pago
                </Button>
                <Button 
                  onClick={() => setRevenueTab('metodos-manuales')}
                  className={revenueTab === 'metodos-manuales' ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white' : 'text-gray-300 hover:text-white'}
                  style={revenueTab !== 'metodos-manuales' ? { backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                >
                  📱 Métodos Manuales
                </Button>
                <div className="flex-1"></div>
                <Button onClick={() => { fetchRevenueData(); fetchMonthlyStats(); }} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  🔄 Actualizar
                </Button>
              </div>

              {loadingRevenue ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
                </div>
              ) : revenueData ? (
                <>
                  {/* Tab: Resumen */}
                  {revenueTab === 'resumen' && (
                    <div className="space-y-6">
                      {/* Tarjetas de resumen principal - SEPARADO COP Y USD */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Ingresos en COP (Mercado Pago) */}
                        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-green-100 text-sm font-medium">Ingresos Totales COP</p>
                                <h3 className="text-3xl font-bold mt-1">${(revenueData.summary.total_cop || 0)?.toLocaleString()}</h3>
                                <p className="text-green-200 text-xs mt-1">Mercado Pago (principal)</p>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-green-400/30">
                              <p className="text-green-200 text-sm">
                                Neto: ${(revenueData.summary.total_cop_net || 0)?.toLocaleString()} COP
                              </p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Ingresos Históricos USD */}
                        <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-amber-100 text-sm font-medium">Total USD</p>
                                <h3 className="text-3xl font-bold mt-1">${(revenueData.summary.total_usd || 0)?.toLocaleString()}</h3>
                                <p className="text-amber-200 text-xs mt-1">PayPal (anteriores)</p>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-amber-400/30">
                              <p className="text-amber-200 text-sm">
                                Neto: ${(revenueData.summary.total_usd_net || 0)?.toLocaleString()} USD
                              </p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Este Mes - Separado */}
                        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-blue-100 text-sm font-medium">📅 Este Mes</p>
                                <div className="mt-1 space-y-1">
                                  <p className="text-xl font-bold">${revenueData.summary.monthly_usd?.toLocaleString()} USD</p>
                                  <p className="text-lg font-semibold text-blue-200">${revenueData.summary.monthly_cop?.toLocaleString()} COP</p>
                                </div>
                              </div>
                              <div className="text-5xl opacity-30">📅</div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-blue-400/30">
                              <p className="text-blue-200 text-xs">
                                Neto: ${revenueData.summary.monthly_usd_net?.toLocaleString()} USD / ${revenueData.summary.monthly_cop_net?.toLocaleString()} COP
                              </p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Ventas Completadas */}
                        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-purple-100 text-sm font-medium">Ventas Completadas</p>
                                <h3 className="text-3xl font-bold mt-1">{revenueData.summary.completed_transactions}</h3>
                                <p className="text-purple-200 text-xs mt-1">transacciones</p>
                              </div>
                              <div className="text-5xl opacity-30">✅</div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-purple-400/30">
                              <p className="text-purple-200 text-sm">
                                {revenueData.summary.pending_transactions} pendientes
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Desglose por método de pago */}
                      <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-white">
                            💳 Ingresos por Método de Pago
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Mercado Pago - COP */}
                            <div className="p-4 rounded-xl border border-white/10" style={{ background: 'rgba(0, 158, 227, 0.15)' }}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 bg-[#009ee3] rounded-xl flex items-center justify-center text-white font-bold">MP</div>
                                <div>
                                  <h4 className="font-semibold text-white">Mercado Pago</h4>
                                  <p className="text-sm text-gray-400">{(revenueData.by_method.mercadopago?.count || 0)} transacciones</p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-2xl font-bold text-green-400">${(revenueData.by_method.mercadopago?.total_cop || 0).toLocaleString()} COP</p>
                                <p className="text-sm text-gray-400">
                                  Neto: <span className="text-green-300 font-semibold">${(revenueData.by_method.mercadopago?.total_cop_net || 0).toLocaleString()} COP</span>
                                </p>
                              </div>
                            </div>

                            {/* Otros/Manual */}
                            <div className="p-4 rounded-xl border border-white/10" style={{ background: 'rgba(255,255,255,0.05)' }}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 bg-gray-600 rounded-xl flex items-center justify-center text-white font-bold text-xs">OTR</div>
                                <div>
                                  <h4 className="font-semibold text-white">Otros (Manual, Nequi, etc.)</h4>
                                  <p className="text-sm text-gray-400">{(revenueData.by_method.other?.count || 0) + (revenueData.by_method.wompi?.count || 0) + (revenueData.by_method.paypal?.count || 0)} transacciones</p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-lg font-bold text-amber-400">${((revenueData.by_method.other?.total_cop || 0) + (revenueData.by_method.wompi?.total_cop || 0)).toLocaleString()} COP</p>
                                {(revenueData.by_method.paypal?.total_usd || 0) > 0 && (
                                  <p className="text-lg font-bold text-blue-400">${(revenueData.by_method.paypal?.total_usd || 0).toLocaleString()} USD</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Desglose por tipo de producto - SEPARADO COP Y USD */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            📦 Ventas por Tipo de Producto
                          </CardTitle>
                          <CardDescription>Ingresos separados por moneda (COP y USD) - Bruto y Neto</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-4 rounded-xl border border-white/10" style={{ background: 'rgba(var(--brand-primary-glow), 0.1)' }}>
                              <p className="text-3xl mb-2">💎</p>
                              <p className="font-semibold text-white">Suscripciones</p>
                              <p className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>{revenueData.by_product.subscription.count}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p className="text-green-400">💵 ${(revenueData.by_product.subscription.total_usd || 0).toLocaleString()} USD</p>
                                <p className="text-amber-400">🇨🇴 ${(revenueData.by_product.subscription.total_cop || 0).toLocaleString()} COP</p>
                              </div>
                            </div>
                            <div className="text-center p-4 rounded-xl border border-white/10" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                              <p className="text-3xl mb-2">📚</p>
                              <p className="font-semibold text-white">Ebooks</p>
                              <p className="text-2xl font-bold text-blue-400">{revenueData.by_product.ebook.count}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p className="text-green-400">💵 ${(revenueData.by_product.ebook.total_usd || 0).toLocaleString()} USD</p>
                                <p className="text-amber-400">🇨🇴 ${(revenueData.by_product.ebook.total_cop || 0).toLocaleString()} COP</p>
                              </div>
                            </div>
                            <div className="text-center p-4 rounded-xl border border-white/10" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                              <p className="text-3xl mb-2">🎬</p>
                              <p className="font-semibold text-white">Recetarios</p>
                              <p className="text-2xl font-bold text-green-400">{revenueData.by_product.recetario.count}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p className="text-green-400">💵 ${(revenueData.by_product.recetario.total_usd || 0).toLocaleString()} USD</p>
                                <p className="text-amber-400">🇨🇴 ${(revenueData.by_product.recetario.total_cop || 0).toLocaleString()} COP</p>
                              </div>
                            </div>
                            <div className="text-center p-4 rounded-xl border border-white/10" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                              <p className="text-3xl mb-2">🛍️</p>
                              <p className="font-semibold text-white">Productos</p>
                              <p className="text-2xl font-bold text-amber-400">{revenueData.by_product.producto.count}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p className="text-green-400">💵 ${(revenueData.by_product.producto.total_usd || 0).toLocaleString()} USD</p>
                                <p className="text-amber-400">🇨🇴 ${(revenueData.by_product.producto.total_cop || 0).toLocaleString()} COP</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Tab: Estadísticas Mensuales */}
                  {revenueTab === 'estadisticas' && (
                    <div className="space-y-6">
                      {loadingMonthlyStats ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
                        </div>
                      ) : monthlyStats && monthlyStats.months.length > 0 ? (
                        <>
                          {/* Info de comisiones */}
                          <Card className="border border-yellow-500/30" style={{ background: 'rgba(234, 179, 8, 0.1)' }}>
                            <CardContent className="py-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">💱</span>
                                  <div>
                                    <p className="text-sm text-amber-300 font-medium">Tasa de Cambio (USD → COP)</p>
                                    <p className="text-xl font-bold text-white">${monthlyStats.exchange_rate?.toLocaleString()} COP</p>
                                  </div>
                                </div>
                                <Badge className="bg-green-500">En tiempo real</Badge>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Lista de meses */}
                          <div className="space-y-4">
                            {monthlyStats.months.map((month) => (
                              <Card 
                                key={month.month_key} 
                                className={`cursor-pointer transition-all duration-300 ${expandedMonth === month.month_key ? 'ring-2 ring-amber-400 shadow-lg' : 'hover:shadow-md'}`}
                                onClick={() => setExpandedMonth(expandedMonth === month.month_key ? null : month.month_key)}
                              >
                                <CardHeader className="pb-2">
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                      <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                                        <span className="text-xl font-bold">{month.month_key.split('-')[1]}</span>
                                      </div>
                                      <div>
                                        <CardTitle className="text-lg capitalize">{month.month_label}</CardTitle>
                                        <CardDescription>{month.transaction_count} transacciones</CardDescription>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      {/* Mostrar USD y COP por separado */}
                                      {month.total_usd > 0 && (
                                        <div>
                                          <p className="text-xl font-bold text-green-400">${month.total_usd.toLocaleString()} USD</p>
                                          <p className="text-xs text-green-300">Neto: ${month.total_usd_net?.toLocaleString()} USD</p>
                                        </div>
                                      )}
                                      {month.total_cop > 0 && (
                                        <div className="mt-1">
                                          <p className="text-xl font-bold text-amber-400">${month.total_cop.toLocaleString()} COP</p>
                                          <p className="text-xs text-amber-300">Neto: ${month.total_cop_net?.toLocaleString()} COP</p>
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-gray-400 text-2xl ml-4">
                                      {expandedMonth === month.month_key ? '▲' : '▼'}
                                    </span>
                                  </div>
                                </CardHeader>

                                {expandedMonth === month.month_key && (
                                  <CardContent className="pt-4 border-t">
                                    {/* Resumen del mes por método - SEPARADO */}
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                                      <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(0, 158, 227, 0.15)' }}>
                                        <p className="text-[#009ee3] text-sm font-medium">Mercado Pago (COP)</p>
                                        <p className="text-xl font-bold text-white">${(month.by_method?.mercadopago?.cop || 0).toLocaleString()}</p>
                                        <p className="text-xs text-green-400">Neto: ${(month.by_method?.mercadopago?.cop_net || 0).toLocaleString()}</p>
                                      </div>
                                      <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                                        <p className="text-gray-400 text-sm font-medium">Otros/Anteriores</p>
                                        <p className="text-xl font-bold text-amber-400">${((month.by_method?.wompi?.cop || 0) + (month.by_method?.other?.cop || 0)).toLocaleString()} COP</p>
                                      </div>
                                      <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                                        <p className="text-gray-400 text-sm font-medium">Otros/Manual</p>
                                        <p className="text-xl font-bold text-white">${(month.by_method?.paypal?.usd || 0).toLocaleString()} USD</p>
                                      </div>
                                    </div>

                                    {/* Por tipo de producto */}
                                    <div className="grid grid-cols-4 gap-3 mb-6">
                                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.15)' }}>
                                        <p className="text-xs text-gray-400">Suscripciones</p>
                                        <p className="font-bold text-green-400">${(month.by_product?.subscription?.usd || 0).toLocaleString()} USD</p>
                                        <p className="font-bold text-amber-400">${(month.by_product?.subscription?.cop || 0).toLocaleString()} COP</p>
                                      </div>
                                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}>
                                        <p className="text-xs text-gray-400">Ebooks</p>
                                        <p className="font-bold text-green-400">${(month.by_product?.ebook?.usd || 0).toLocaleString()} USD</p>
                                        <p className="font-bold text-amber-400">${(month.by_product?.ebook?.cop || 0).toLocaleString()} COP</p>
                                      </div>
                                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                                        <p className="text-xs text-gray-400">Recetarios</p>
                                        <p className="font-bold text-green-400">${(month.by_product?.recetario?.usd || 0).toLocaleString()} USD</p>
                                        <p className="font-bold text-amber-400">${(month.by_product?.recetario?.cop || 0).toLocaleString()} COP</p>
                                      </div>
                                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)' }}>
                                        <p className="text-xs text-gray-400">Productos</p>
                                        <p className="font-bold text-green-400">${(month.by_product?.producto?.usd || 0).toLocaleString()} USD</p>
                                        <p className="font-bold text-amber-400">${(month.by_product?.producto?.cop || 0).toLocaleString()} COP</p>
                                      </div>
                                    </div>

                                    {/* Transacciones del mes - Comisión debajo del Monto Neto */}
                                    <h4 className="font-semibold text-white mb-3">Detalle de transacciones</h4>
                                    <div className="max-h-64 overflow-y-auto">
                                      <table className="w-full text-sm">
                                        <thead className="sticky top-0" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                          <tr className="text-gray-300">
                                            <th className="px-3 py-2 text-left">Usuario</th>
                                            <th className="px-3 py-2 text-right">Monto Bruto</th>
                                            <th className="px-3 py-2 text-right">Monto Neto</th>
                                            <th className="px-3 py-2 text-left">Método</th>
                                            <th className="px-3 py-2 text-left">Producto</th>
                                            <th className="px-3 py-2 text-left">Fecha</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {month.transactions.map((tx, idx) => (
                                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                              <td className="px-3 py-2">
                                                <p className="font-medium text-white">{tx.user_name}</p>
                                                <p className="text-xs text-gray-500">{tx.user_email}</p>
                                              </td>
                                              <td className="px-3 py-2 text-right">
                                                <span className={tx.currency === 'USD' ? 'text-green-400' : 'text-amber-400'}>
                                                  ${tx.amount?.toLocaleString()} {tx.currency}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2 text-right">
                                                <div>
                                                  <span className={`font-bold ${tx.currency === 'USD' ? 'text-green-300' : 'text-amber-300'}`}>
                                                    ${tx.net_amount?.toLocaleString()} {tx.currency}
                                                  </span>
                                                  {tx.fee > 0 && (
                                                    <p className="text-xs text-red-400 mt-1">
                                                      Comisión: -${tx.fee?.toLocaleString()} {tx.currency}
                                                    </p>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2">
                                                <Badge variant="outline" className="text-xs border-white/20 text-gray-300">{tx.payment_method}</Badge>
                                              </td>
                                              <td className="px-3 py-2 capitalize text-gray-300">{tx.product_type}</td>
                                              <td className="px-3 py-2 text-gray-500">
                                                {tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-CO') : 'N/A'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </CardContent>
                                )}
                              </Card>
                            ))}
                          </div>
                        </>
                      ) : (
                        <Card>
                          <CardContent className="py-12 text-center">
                            <p className="text-5xl mb-4">📊</p>
                            <p className="text-gray-500">No hay estadísticas disponibles</p>
                            <p className="text-sm text-gray-400 mt-2">Las estadísticas aparecerán cuando tengas transacciones completadas</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Tab: Transacciones */}
                  {revenueTab === 'transacciones' && (
                    <>
                    {/* Widget: Ventas por Recuperar (rechazos del día) */}
                    {revenueData.summary.rejected_today_count > 0 && (
                      <Card
                        className="border border-orange-500/40 mb-4"
                        style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(239,68,68,0.10))' }}
                        data-testid="widget-por-recuperar"
                      >
                        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
                          <div>
                            <p className="text-orange-200 text-xs font-semibold uppercase tracking-wide">🎯 Ventas por Recuperar (hoy)</p>
                            <h3 className="text-3xl font-black text-white mt-1">
                              ${Math.round(revenueData.summary.rejected_today_cop || 0).toLocaleString('es-CO')}
                              <span className="text-sm font-normal text-orange-200 ml-2">COP</span>
                              {revenueData.summary.rejected_today_usd > 0 && (
                                <span className="text-lg font-semibold text-orange-100 ml-3">
                                  + ${revenueData.summary.rejected_today_usd.toFixed(2)} USD
                                </span>
                              )}
                            </h3>
                            <p className="text-orange-100/70 text-sm mt-1">
                              <span className="font-bold text-orange-100">{revenueData.summary.rejected_today_count}</span> pago{revenueData.summary.rejected_today_count === 1 ? '' : 's'} rechazado{revenueData.summary.rejected_today_count === 1 ? '' : 's'} hoy · contactalas por WhatsApp
                            </p>
                          </div>
                          <div className="text-6xl opacity-40">💸</div>
                        </CardContent>
                      </Card>
                    )}
                    <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-white">📋 Últimas Transacciones</CardTitle>
                            <CardDescription className="text-gray-400">Historial de las últimas 20 transacciones con montos separados</CardDescription>
                          </div>
                          <div className="flex gap-2">
                            {revenueData.summary.pending_transactions > 0 && (
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleBulkDeleteTransactions('pending')}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                🗑️ Eliminar pendientes ({revenueData.summary.pending_transactions})
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={handleWipeAllTransactions}
                              className="border-red-500 text-red-400 hover:bg-red-500/20"
                              data-testid="wipe-all-transactions-btn"
                            >
                              ⚠️ Limpiar TODO
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {revenueData.recent_transactions.length === 0 ? (
                          <div className="text-center py-12 text-gray-400">
                            <p className="text-5xl mb-4">📭</p>
                            <p>No hay transacciones registradas</p>
                          </div>
                        ) : (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                                  <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Usuario</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-amber-400">Monto Bruto</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-green-400">💰 Monto Neto</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Método</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Producto</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Estado</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-orange-300">Motivo Rechazo</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Fecha</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-emerald-300">WhatsApp</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">Acciones</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {revenueData.recent_transactions.map((tx) => {
                                    // Calcular Monto Bruto, Comisión y Monto Neto según método de pago
                                    let montoBruto = 0;
                                    let comision = 0;
                                    let montoNeto = 0;
                                    let currency = 'COP';
                                    
                                    if (tx.payment_method === 'mercadopago') {
                                      // Mercado Pago = COP
                                      // Comisión: 2.99% + 800 COP + IVA (19%)
                                      montoBruto = tx.amount_cop || tx.amount || 0;
                                      const feeBase = (montoBruto * 0.0299) + 800;
                                      comision = Math.round(feeBase * 1.19);
                                      montoNeto = montoBruto - comision;
                                      currency = 'COP';
                                    } else if (tx.payment_method === 'wompi') {
                                      // Wompi = COP (histórico)
                                      montoBruto = tx.amount_cop || tx.amount || 0;
                                      const feeBase = (montoBruto * 0.0265) + 700;
                                      comision = Math.round(feeBase * 1.19);
                                      montoNeto = montoBruto - comision;
                                      currency = 'COP';
                                    } else if (tx.payment_method === 'paypal') {
                                      // PayPal = USD (histórico)
                                      montoBruto = tx.amount_usd || tx.amount || 0;
                                      comision = (montoBruto * 0.054) + 0.30;
                                      comision = Math.round(comision * 100) / 100;
                                      montoNeto = Math.round((montoBruto - comision) * 100) / 100;
                                      currency = 'USD';
                                    } else {
                                      // Otros (nequi, bancolombia, manual) - sin comisión de pasarela
                                      montoBruto = tx.amount_cop || tx.amount || 0;
                                      comision = 0;
                                      montoNeto = montoBruto;
                                      currency = tx.amount_cop ? 'COP' : 'USD';
                                    }
                                    
                                    // Formatear según moneda
                                    const formatAmount = (amount, curr) => {
                                      if (curr === 'COP') {
                                        return `$${Math.round(amount).toLocaleString('es-CO')} COP`;
                                      }
                                      return `$${amount.toFixed(2)} USD`;
                                    };
                                    
                                    return (
                                      <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                                        <td className="px-4 py-3">
                                          <div>
                                            <p className="font-medium text-white">{tx.user_name}</p>
                                            <p className="text-xs text-gray-500">{tx.user_email}</p>
                                            {tx.is_guest && <Badge className="bg-purple-500/20 text-purple-400 text-xs mt-1">Invitado</Badge>}
                                          </div>
                                        </td>
                                        {/* Monto Bruto */}
                                        <td className="px-4 py-3 text-right">
                                          <span className={`font-bold ${currency === 'USD' ? 'text-green-400' : 'text-amber-400'}`}>
                                            {formatAmount(montoBruto, currency)}
                                          </span>
                                        </td>
                                        {/* Monto Neto + Comisión debajo */}
                                        <td className="px-4 py-3 text-right">
                                          <div>
                                            <span className={`font-bold ${currency === 'USD' ? 'text-green-300' : 'text-amber-300'}`}>
                                              {formatAmount(montoNeto, currency)}
                                            </span>
                                            {comision > 0 && (
                                              <p className="text-xs text-red-400 mt-1">
                                                Comisión: -{formatAmount(comision, currency)}
                                              </p>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          <Badge className={
                                            tx.payment_method === 'mercadopago' ? 'bg-cyan-500/20 text-cyan-400' :
                                            tx.payment_method === 'paypal' ? 'bg-blue-500/20 text-blue-400' :
                                            tx.payment_method === 'wompi' ? 'bg-purple-500/20 text-purple-400' :
                                            tx.payment_method === 'nequi' ? 'bg-pink-500/20 text-pink-400' :
                                            tx.payment_method === 'bancolombia' ? 'bg-yellow-500/20 text-yellow-400' :
                                            'bg-gray-500/20 text-gray-400'
                                          }>
                                            {tx.payment_method === 'mercadopago' ? 'MERCADO PAGO' : tx.payment_method?.toUpperCase()}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="text-sm capitalize text-gray-300">{tx.product_type}</span>
                                          {tx.product_name && <p className="text-xs text-gray-500">{tx.product_name}</p>}
                                          {tx.has_discount && <Badge className="bg-orange-500/20 text-orange-400 text-xs mt-1">Descuento</Badge>}
                                        </td>
                                        <td className="px-4 py-3">
                                          <Badge className={
                                            tx.payment_status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                            tx.payment_status === 'pending_verification' ? 'bg-amber-500/20 text-amber-300' :
                                            tx.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                            tx.payment_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                            'bg-red-500/20 text-red-400'
                                          }>
                                            {tx.payment_status === 'completed' ? '✅ Verificado' :
                                             tx.payment_status === 'pending_verification' ? '⏳ Por Verificar' :
                                             tx.payment_status === 'pending' ? 'Pendiente' :
                                             tx.payment_status === 'rejected' ? '❌ Rechazado' : 'Fallido'}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {(() => {
                                            const detail = translateMpStatusDetail(tx);
                                            return (
                                              <Badge
                                                className={`${detail.color} text-xs`}
                                                title={tx.mp_status_detail || `Estado: ${tx.payment_status || '—'}`}
                                                data-testid={`mp-reason-${tx.id}`}
                                              >
                                                {detail.label}
                                              </Badge>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-400">
                                          {tx.created_at ? new Date(tx.created_at).toLocaleString('es-CO') : 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          {(() => {
                                            const link = buildWhatsAppLink(tx);
                                            if (!link) return <span className="text-gray-600 text-xs">Sin tel</span>;
                                            return (
                                              <a
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`whatsapp-btn-${tx.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors"
                                                title={`Escribir a ${tx.guest_country_code || '+57'} ${tx.guest_phone || ''}`}
                                              >
                                                <FaWhatsapp className="text-sm" /> Chat
                                              </a>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex gap-1 flex-wrap">
                                            {/* ═════ Botones Verificar / Rechazar para pagos manuales ═════ */}
                                            {tx.payment_status === 'pending_verification' && (
                                              <>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                                                  onClick={() => handleMarkPurchase(tx.id, 'verified')}
                                                  data-testid={`verify-payment-${tx.id}`}
                                                  title="Comprobante correcto — activa la cuenta"
                                                >
                                                  ✓ Verificar
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                                  onClick={() => handleMarkPurchase(tx.id, 'rejected')}
                                                  data-testid={`reject-payment-${tx.id}`}
                                                  title="Comprobante inválido / no coincide"
                                                >
                                                  ✗ Rechazar
                                                </Button>
                                              </>
                                            )}
                                            {/* Botón Marcar como Pagado - solo para pendientes */}
                                            {tx.payment_status === 'pending' && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                                                onClick={() => handleMarkAsPaid(tx.id)}
                                                title="Marcar como pagado"
                                              >
                                                Pagar
                                              </Button>
                                            )}
                                            {/* Botón Eliminar - solo para pendientes/fallidos */}
                                            {tx.payment_status !== 'completed' && tx.payment_status !== 'pending_verification' && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                                onClick={() => handleDeleteTransaction(tx.id)}
                                                title="Eliminar transacción de prueba"
                                              >
                                                X
                                              </Button>
                                            )}
                                            {/* Botón Reactivar Suscripción - solo se muestra si la suscripción ya venció */}
                                            {tx.payment_status === 'completed' && tx.product_type === 'subscription' && (() => {
                                              if (!tx.subscription_end_date) return false;
                                              try {
                                                return new Date(tx.subscription_end_date) < new Date();
                                              } catch (e) {
                                                return false;
                                              }
                                            })() && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid="reactivate-subscription-btn"
                                                className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20"
                                                onClick={() => handleReactivateSubscription(tx.id, tx.user_email)}
                                                title="Reactivar suscripción (esta suscripción ya expiró)"
                                              >
                                                Reactivar
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                {/* TOTALES separados para COP y USD */}
                                <tfoot style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                                  {/* Fila de totales COP */}
                                  {(() => {
                                    // Calcular totales COP (Mercado Pago, Wompi, Nequi, Bancolombia, Manual)
                                    let totalBrutoCOP = 0;
                                    let totalComisionCOP = 0;
                                    let totalNetoCOP = 0;
                                    
                                    revenueData.recent_transactions.forEach(tx => {
                                      if (tx.payment_method === 'mercadopago' || tx.payment_method === 'wompi' || tx.payment_method === 'nequi' || tx.payment_method === 'bancolombia' || tx.payment_method === 'manual') {
                                        const bruto = tx.amount_cop || tx.amount || 0;
                                        let comision = 0;
                                        if (tx.payment_method === 'mercadopago') {
                                          const feeBase = (bruto * 0.0299) + 800;
                                          comision = Math.round(feeBase * 1.19);
                                        } else if (tx.payment_method === 'wompi') {
                                          const feeBase = (bruto * 0.0265) + 700;
                                          comision = Math.round(feeBase * 1.19);
                                        }
                                        totalBrutoCOP += bruto;
                                        totalComisionCOP += comision;
                                        totalNetoCOP += (bruto - comision);
                                      }
                                    });
                                    
                                    // Calcular totales USD (PayPal)
                                    let totalBrutoUSD = 0;
                                    let totalComisionUSD = 0;
                                    let totalNetoUSD = 0;
                                    
                                    revenueData.recent_transactions.forEach(tx => {
                                      if (tx.payment_method === 'paypal') {
                                        const bruto = tx.amount_usd || tx.amount || 0;
                                        const comision = (bruto * 0.054) + 0.30;
                                        totalBrutoUSD += bruto;
                                        totalComisionUSD += comision;
                                        totalNetoUSD += (bruto - comision);
                                      }
                                    });
                                    
                                    return (
                                      <>
                                        {/* Fila COP */}
                                        <tr className="font-bold border-b border-white/10">
                                          <td className="px-4 py-3 text-amber-400">TOTAL COP:</td>
                                          <td className="px-4 py-3 text-right">
                                            <span className="text-amber-400">
                                              ${Math.round(totalBrutoCOP).toLocaleString('es-CO')} COP
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-right">
                                            <div>
                                              <span className="text-amber-300">
                                                ${Math.round(totalNetoCOP).toLocaleString('es-CO')} COP
                                              </span>
                                              {totalComisionCOP > 0 && (
                                                <p className="text-xs text-red-400 mt-1">
                                                  Comisión: -${Math.round(totalComisionCOP).toLocaleString('es-CO')} COP
                                                </p>
                                              )}
                                            </div>
                                          </td>
                                          <td colSpan="7" className="px-4 py-3 text-right text-gray-400 text-sm">
                                            {revenueData.recent_transactions.filter(tx => 
                                              (tx.payment_method === 'mercadopago' || tx.payment_method === 'wompi' || tx.payment_method === 'nequi' || tx.payment_method === 'bancolombia' || tx.payment_method === 'manual') && 
                                              tx.payment_status === 'completed'
                                            ).length} transacciones COP completadas
                                          </td>
                                        </tr>
                                        {/* Fila USD */}
                                        <tr className="font-bold">
                                          <td className="px-4 py-3 text-green-400">TOTAL USD:</td>
                                          <td className="px-4 py-3 text-right">
                                            <span className="text-green-400">
                                              ${totalBrutoUSD.toFixed(2)} USD
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-right">
                                            <div>
                                              <span className="text-green-300">
                                                ${totalNetoUSD.toFixed(2)} USD
                                              </span>
                                              {totalComisionUSD > 0 && (
                                                <p className="text-xs text-red-400 mt-1">
                                                  Comisión: -${totalComisionUSD.toFixed(2)} USD
                                                </p>
                                              )}
                                            </div>
                                          </td>
                                          <td colSpan="7" className="px-4 py-3 text-right text-gray-400 text-sm">
                                            {revenueData.recent_transactions.filter(tx => 
                                              tx.payment_method === 'paypal' && 
                                              tx.payment_status === 'completed'
                                            ).length} transacciones USD completadas
                                          </td>
                                        </tr>
                                      </>
                                    );
                                  })()}
                                </tfoot>
                              </table>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                    </>
                  )}

                  {/* Tab: Pasarelas */}
                  {revenueTab === 'pasarelas' && (
                    <div className="space-y-6">
                      <Card className="border border-white/10" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <CardHeader>
                          <CardTitle className="text-white" data-testid="pasarelas-title">Estado de Pasarelas de Pago</CardTitle>
                          <CardDescription className="text-gray-400">Configuración y estado actual de tus métodos de pago</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Mercado Pago */}
                            <div className="p-6 rounded-2xl border-2" style={{ borderColor: 'rgba(0, 158, 227, 0.5)', backgroundColor: 'rgba(0, 158, 227, 0.15)' }}>
                              <div className="flex items-center justify-between mb-4">
                                <div className="w-16 h-16 bg-[#009ee3] rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                                  MP
                                </div>
                                <Badge className="bg-green-500 text-white" data-testid="mp-status-badge">Activo</Badge>
                              </div>
                              <h3 className="text-xl font-bold mb-1 text-white">Mercado Pago</h3>
                              <p className="text-gray-400 text-sm mb-4">Tarjetas, PSE, efectivo, Nequi (COP)</p>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-green-400">Configurado y activo</span>
                                </div>
                                <p className="text-xs text-gray-500">Comisión: 4.99% + IVA (19%)</p>
                              </div>
                            </div>

                            {/* Info */}
                            <div className="p-6 rounded-2xl border-2 border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                              <div className="flex items-center justify-between mb-4">
                                <div className="w-16 h-16 bg-gray-700 rounded-2xl flex items-center justify-center text-gray-400 font-bold text-sm shadow-lg">
                                  INFO
                                </div>
                              </div>
                              <h3 className="text-xl font-bold mb-1 text-white">Pasarelas Anteriores</h3>
                              <p className="text-gray-400 text-sm mb-4">Wompi y PayPal fueron desactivados</p>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Las transacciones anteriores se conservan en el historial</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* TAB: Métodos Manuales */}
                  {revenueTab === 'metodos-manuales' && (
                    <ManualPaymentMethodsManager />
                  )}

                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-5xl mb-4">📊</p>
                    <p className="text-gray-500">No se pudieron cargar los datos de ingresos</p>
                    <Button onClick={fetchRevenueData} className="mt-4">Reintentar</Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* DASHBOARD PRINCIPAL */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {loadingStats ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
                </div>
              ) : dashboardStats ? (
                <>
                  {/* Primera fila - Métricas principales */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">👥</div>
                        <div className="text-3xl font-bold text-pink-600">{dashboardStats.users.total}</div>
                        <div className="text-sm text-gray-600">Usuarios</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">🎬</div>
                        <div className="text-3xl font-bold text-green-600">{dashboardStats.content.videos}</div>
                        <div className="text-sm text-gray-600">Videos</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">💪</div>
                        <div className="text-3xl font-bold text-purple-600">{dashboardStats.content.programs}</div>
                        <div className="text-sm text-gray-600">Programas</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">📹</div>
                        <div className="text-3xl font-bold text-blue-600">{dashboardStats.content.vlogs}</div>
                        <div className="text-sm text-gray-600">Vlogs</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">📚</div>
                        <div className="text-3xl font-bold text-orange-600">{dashboardStats.content.ebooks}</div>
                        <div className="text-sm text-gray-600">Ebooks</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">📁</div>
                        <div className="text-3xl font-bold text-amber-600">{dashboardStats.content.total}</div>
                        <div className="text-sm text-gray-600">Total Contenido</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Segunda fila - Actividad */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">🏋️</div>
                        <div className="text-3xl font-bold text-rose-600">{dashboardStats.activity.workout_plans}</div>
                        <div className="text-sm text-gray-600">Rutinas Creadas</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">🥗</div>
                        <div className="text-3xl font-bold text-teal-600">{dashboardStats.activity.meal_plans}</div>
                        <div className="text-sm text-gray-600">Planes Nutrición</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">📈</div>
                        <div className="text-3xl font-bold text-indigo-600">{dashboardStats.activity.progress_entries}</div>
                        <div className="text-sm text-gray-600">Registros Progreso</div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl mb-1">🖼️</div>
                        <div className="text-3xl font-bold text-cyan-600">{dashboardStats.content.media}</div>
                        <div className="text-sm text-gray-600">Galería Medios</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tercera fila - Usuarios por Plan */}
                  <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <CardHeader style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                      <CardTitle className="flex items-center gap-2 text-white">
                        👑 Usuarios por Nivel de Suscripción
                      </CardTitle>
                      <CardDescription className="text-gray-400">Distribución de usuarios según su plan</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                          <div className="w-4 h-4 rounded-full bg-gray-400 mx-auto mb-2"></div>
                          <div className="text-2xl font-bold text-gray-300">{dashboardStats.users.by_plan.sin_plan}</div>
                          <div className="text-sm text-gray-500">Sin Plan</div>
                        </div>
                        
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                          <div className="w-4 h-4 rounded-full bg-blue-500 mx-auto mb-2"></div>
                          <div className="text-2xl font-bold text-blue-400">{dashboardStats.users.by_plan.basic}</div>
                          <div className="text-sm text-gray-400">Fit Evolution</div>
                        </div>
                        
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(168, 85, 247, 0.15)' }}>
                          <div className="w-4 h-4 rounded-full bg-purple-500 mx-auto mb-2"></div>
                          <div className="text-2xl font-bold text-purple-400">{dashboardStats.users.by_plan.premium}</div>
                          <div className="text-sm text-gray-400">Premium</div>
                        </div>
                        
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
                          <div className="w-4 h-4 rounded-full bg-amber-500 mx-auto mb-2"></div>
                          <div className="text-2xl font-bold text-amber-400">{dashboardStats.users.by_plan.gold}</div>
                          <div className="text-sm text-gray-400">Gold</div>
                        </div>
                        
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(var(--brand-primary-glow), 0.15)' }}>
                          <div className="w-4 h-4 rounded-full mx-auto mb-2" style={{ background: 'var(--brand-primary)' }}></div>
                          <div className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>{dashboardStats.users.by_plan.admin}</div>
                          <div className="text-sm text-gray-400">Admin</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Cuarta fila - Acciones Rápidas */}
                  <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-white">
                        ⚡ Acciones Rápidas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <button 
                          onClick={() => setActiveSection('videos')}
                          className="p-4 rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
                          style={{ background: 'rgba(34, 197, 94, 0.15)' }}
                        >
                          <span className="text-3xl">📹</span>
                          <span className="font-semibold text-green-400">Subir Video</span>
                        </button>
                        
                        <button 
                          onClick={() => setActiveSection('progreso')}
                          className="p-4 rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
                          style={{ background: 'rgba(99, 102, 241, 0.15)' }}
                        >
                          <span className="text-3xl">📈</span>
                          <span className="font-semibold text-indigo-400">Ver Progreso</span>
                        </button>
                        
                        <button 
                          onClick={() => setActiveSection('notificaciones')}
                          className="p-4 rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
                          style={{ background: 'rgba(245, 158, 11, 0.15)' }}
                        >
                          <span className="text-3xl">🔔</span>
                          <span className="font-semibold text-amber-400">Notificaciones</span>
                        </button>
                        
                        <button 
                          onClick={() => setActiveSection('cuentas-prueba')}
                          className="p-4 rounded-xl hover:shadow-lg transition-all flex flex-col items-center gap-2"
                          style={{ background: 'rgba(168, 85, 247, 0.15)' }}
                        >
                          <span className="text-3xl">🧪</span>
                          <span className="font-semibold text-purple-400">Crear Cuenta</span>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <p>Error al cargar estadísticas</p>
                  <Button onClick={fetchDashboardStats} className="mt-4 text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>
                    Reintentar
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* SECCIÓN CALENDARIO GOLD */}
          {activeSection === 'calendario-gold' && (
            <AdminGoldCalendar />
          )}

          {/* SECCIÓN EJERCICIOS SIN VIDEO (migración nombre → video_id) */}
          {activeSection === 'missing-videos' && (
            <AdminMissingVideos />
          )}

          {/* SECCIÓN GESTIÓN DE SERVICIOS */}
          {activeSection === 'servicios' && (
            <div className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Formulario para crear/editar módulo */}
                <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                  <CardHeader className="border-b border-white/10" style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(var(--brand-primary-glow), 0.1))' }}>
                    <CardTitle className="flex items-center gap-2 text-white">
                      <span className="text-2xl">🧩</span>
                      {editingModule ? 'Editar Servicio' : 'Crear Nuevo Servicio'}
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Define qué servicios tienen acceso los usuarios según su plan
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmitModule} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre del Servicio *</Label>
                          <Input
                            value={moduleData.name}
                            onChange={(e) => setModuleData({...moduleData, name: e.target.value})}
                            placeholder="Ej: Data Progresión"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Slug (identificador único) *</Label>
                          <Input
                            value={moduleData.slug}
                            onChange={(e) => setModuleData({...moduleData, slug: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                            placeholder="Ej: data_progresion"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <Input
                          value={moduleData.description}
                          onChange={(e) => setModuleData({...moduleData, description: e.target.value})}
                          placeholder="Descripción breve del servicio"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Ruta en la App *</Label>
                          <Input
                            value={moduleData.path}
                            onChange={(e) => setModuleData({...moduleData, path: e.target.value})}
                            placeholder="/data-progresion"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Icono</Label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={moduleData.icon}
                            onChange={(e) => setModuleData({...moduleData, icon: e.target.value})}
                          >
                            <option value="FaFire">🔥 Fuego (FaFire)</option>
                            <option value="FaDumbbell">💪 Mancuerna (FaDumbbell)</option>
                            <option value="FaAppleAlt">🍎 Manzana (FaAppleAlt)</option>
                            <option value="FaVideo">📹 Video (FaVideo)</option>
                            <option value="FaBook">📚 Libro (FaBook)</option>
                            <option value="FaCalendar">📅 Calendario (FaCalendar)</option>
                            <option value="FaChartLine">📈 Gráfico (FaChartLine)</option>
                            <option value="FaStar">⭐ Estrella (FaStar)</option>
                            <option value="FaHeart">❤️ Corazón (FaHeart)</option>
                            <option value="FaCrown">👑 Corona (FaCrown)</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Badge (opcional)</Label>
                          <Input
                            value={moduleData.badge}
                            onChange={(e) => setModuleData({...moduleData, badge: e.target.value})}
                            placeholder="NUEVO, POPULAR, etc."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Orden de aparición</Label>
                          <Input
                            type="number"
                            value={moduleData.order}
                            onChange={(e) => setModuleData({...moduleData, order: parseInt(e.target.value) || 0})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Categoría</Label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={moduleData.category}
                            onChange={(e) => setModuleData({...moduleData, category: e.target.value})}
                          >
                            <option value="program">Programa Principal</option>
                            <option value="quick_action">Acción Rápida</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Opciones</Label>
                          <div className="flex gap-4 pt-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={moduleData.is_active}
                                onChange={(e) => setModuleData({...moduleData, is_active: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Activo</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={moduleData.is_quick_action}
                                onChange={(e) => setModuleData({...moduleData, is_quick_action: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Acción Rápida</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Planes con acceso */}
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <span className="text-lg">🔐</span> Planes con Acceso
                        </Label>
                        <p className="text-xs text-gray-400">Selecciona los planes que tendrán acceso a este servicio. Solo usuarios con estos planes exactos podrán verlo.</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                          {availablePlans.map((plan) => (
                            <label 
                              key={plan.id}
                              className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer border-2 transition-all ${
                                moduleData.allowed_plans.includes(plan.name) 
                                  ? 'bg-green-500/20 border-green-500 text-green-400' 
                                  : 'bg-white/5 border-white/20 text-gray-300 hover:border-white/40'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={moduleData.allowed_plans.includes(plan.name)}
                                onChange={() => togglePlanAccess(plan.name)}
                                className="w-5 h-5 accent-green-500"
                              />
                              <span className="font-medium">{plan.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button 
                          type="submit" 
                          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                        >
                          {editingModule ? '💾 Actualizar Servicio' : '➕ Crear Servicio'}
                        </Button>
                        {editingModule && (
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={resetModuleForm}
                          >
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Lista de módulos existentes */}
                <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                  <CardHeader className="border-b border-white/10" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(168, 85, 247, 0.1))' }}>
                    <CardTitle className="flex items-center justify-between text-white">
                      <span className="flex items-center gap-2">
                        <span className="text-2xl">📋</span>
                        Servicios de la Plataforma
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={initDefaultModules}
                        className="text-sm text-white border-white/20 hover:bg-white/10"
                      >
                        ⚡ Cargar Defaults
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loadingModules ? (
                      <div className="text-center py-12">
                        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-gray-400 mt-2">Cargando servicios...</p>
                      </div>
                    ) : platformModules.length === 0 ? (
                      <div className="text-center py-12 px-6">
                        <p className="text-gray-400 mb-4">No hay servicios configurados</p>
                        <Button onClick={initDefaultModules} style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} className="text-white">
                          ⚡ Crear servicios por defecto
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y divide-white/10">
                        {platformModules.map((module) => (
                          <div key={module.id} className="p-4 hover:bg-white/5 transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${module.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                    {module.is_active ? '✓ Activo' : '○ Inactivo'}
                                  </span>
                                  {module.badge && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                                      {module.badge}
                                    </span>
                                  )}
                                  {module.is_quick_action && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                                      Acción Rápida
                                    </span>
                                  )}
                                </div>
                                <h4 className="font-semibold text-gray-900">{module.name}</h4>
                                <p className="text-sm text-gray-500">{module.description}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  Ruta: {module.path} | Orden: {module.order}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {module.allowed_plans?.map((plan) => (
                                    <span 
                                      key={plan} 
                                      className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700"
                                    >
                                      {plan}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditModule(module)}
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                >
                                  ✏️
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteModule(module.id)}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  🗑️
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Panel de Secciones en Construcción */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader className="border-b border-white/10" style={{ background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.1), rgba(var(--brand-primary-glow), 0.1))' }}>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <span className="text-2xl">🚧</span>
                    Secciones en Construcción
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Activa o desactiva la pantalla "En Construcción" para cada sección
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <SectionsUnderConstructionPanel />
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === 'videos' && (
            <div className="space-y-6">
              {/* Banner Fit Evolution — CTA muy visible para etiquetado masivo */}
              <div
                className="rounded-2xl p-5 flex items-center gap-4 flex-wrap"
                style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.15), rgba(52, 211, 153, 0.12))', border: '2px solid rgba(52, 211, 153, 0.4)' }}
                data-testid="bulk-tag-banner"
              >
                <div className="text-4xl">🏷️</div>
                <div className="flex-1 min-w-[240px]">
                  <p className="font-black text-white text-lg leading-tight">Etiqueta tus videos para Fit Evolution</p>
                  <p className="text-sm text-gray-300">Sin etiquetas, la Generación Automática de rutinas no puede armar los splits. Hazlo <b className="text-emerald-300">en lote</b> en un solo click.</p>
                </div>
                <Button
                  data-testid="bulk-tag-banner-btn"
                  onClick={() => setShowBulkTagEditor(true)}
                  className="text-white font-black text-base px-6 py-6"
                  style={{ background: 'linear-gradient(135deg, #34D399, #059669)' }}
                >
                  ⚡ Etiquetar en lote →
                </Button>
              </div>

              {/* Tabs para diferentes tipos de contenido */}
              <div className="flex gap-2 border-b border-gray-200 mb-6">
                <button className="pb-3 px-4 border-b-2 border-[#FF69B4] text-[#FF69B4] font-semibold">
                  📹 Videos
                </button>
                <button 
                  onClick={() => setActiveSection('ebooks')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📚 Ebooks
                </button>
                <button 
                  onClick={() => setActiveSection('recetarios')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🍳 Recetarios
                </button>
                <button 
                  onClick={() => setActiveSection('planes')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📋 Planes
                </button>
                <button 
                  onClick={() => setActiveSection('codigos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🏷️ Códigos
                </button>
              </div>

              {/* Formulario de Video */}
              <Card>
                <CardHeader>
                  <CardTitle>{editingVideo ? '✏️ Editar Video' : '➕ Crear Nuevo Video'}</CardTitle>
                  <CardDescription>
                    {editingVideo ? 'Actualiza la información del video' : 'Agrega un nuevo video desde archivo, YouTube o Google Drive'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitVideo} className="space-y-4">
                    {/* Selector de Fuente del Video */}
                    {!editingVideo && (
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">📹 Fuente del Video</Label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            type="button"
                            onClick={() => setVideoData({...videoData, video_source: 'youtube', video_url: ''})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              videoData.video_source === 'youtube'
                                ? 'border-red-500 bg-red-50 shadow-md'
                                : 'border-gray-200 hover:border-red-300'
                            }`}
                            data-testid="video-source-youtube"
                          >
                            <div className="text-2xl mb-2">▶️</div>
                            <div className="font-semibold">YouTube</div>
                            <div className="text-xs text-gray-500">Pega el link del video</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVideoData({...videoData, video_source: 'drive', video_url: ''})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              videoData.video_source === 'drive'
                                ? 'border-blue-500 bg-blue-500/20 shadow-md'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                            data-testid="video-source-drive"
                          >
                            <div className="text-2xl mb-2">📁</div>
                            <div className="font-semibold">Google Drive</div>
                            <div className="text-xs text-gray-500">Pega el link compartido</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVideoData({...videoData, video_source: 'bunny', video_url: ''})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              videoData.video_source === 'bunny'
                                ? 'border-amber-500 bg-amber-50 shadow-md'
                                : 'border-gray-200 hover:border-amber-300'
                            }`}
                            data-testid="video-source-bunny"
                          >
                            <div className="text-2xl mb-2">🐰</div>
                            <div className="font-semibold">Bunny Stream</div>
                            <div className="text-xs text-gray-500">Pega la URL directa MP4</div>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="video_title">Título</Label>
                        <Input
                          id="video_title"
                          value={videoData.title}
                          onChange={(e) => setVideoData({...videoData, title: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="video_category">Categoría</Label>
                          <button
                            type="button"
                            onClick={() => setShowVideoCategoryManager(true)}
                            className="text-xs font-semibold text-pink-600 hover:text-pink-800 underline"
                            data-testid="video-categories-manage-btn"
                          >
                            ⚙️ Gestionar categorías
                          </button>
                        </div>
                        <select
                          id="video_category"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={videoData.category}
                          onChange={(e) => setVideoData({...videoData, category: e.target.value})}
                        >
                          <option value="">— Sin categoría (opcional) —</option>
                          <optgroup label="Especiales">
                            <option value="bienvenida">🎬 Bienvenida</option>
                          </optgroup>
                          <optgroup label="Por Género">
                            <option value="videos_hombres">💪 Entrenamiento para Hombres (Guerreros)</option>
                            <option value="videos_mujeres">🌸 Entrenamiento para Mujeres (Guerreras)</option>
                          </optgroup>
                          <optgroup label="General">
                            <option value="metodos_entrenamiento">📁 Métodos de Entrenamiento Explicados</option>
                            <option value="tips_nutricion">📁 Tips de Nutrición</option>
                            <option value="recetas_saludables">📁 Recetas Saludables</option>
                            <option value="motivacion">📁 Motivación</option>
                          </optgroup>
                          {customVideoCategories.length > 0 && (
                            <optgroup label="Personalizadas">
                              {customVideoCategories.map((c) => (
                                <option key={c.id} value={c.slug}>
                                  {(c.emoji ? `${c.emoji} ` : '📁 ')}{c.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* === FIT EVOLUTION — Etiquetas para generación automática de rutinas === */}
                    <div className="p-4 rounded-lg border-2 border-pink-200 bg-pink-50/50 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-pink-600 font-bold text-sm">🏷️ Etiquetas Fit Evolution</span>
                        <span className="text-xs text-gray-500">(usado por la Generación Automática de rutinas)</span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_exercise_type" className="text-xs font-semibold text-gray-700">Tipo de ejercicio</Label>
                          <select
                            id="fe_exercise_type"
                            data-testid="fe-exercise-type"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.exercise_type || ''}
                            onChange={(e) => setVideoData({...videoData, exercise_type: e.target.value})}
                          >
                            <option value="">— Sin etiquetar —</option>
                            <option value="activador">🔥 Activador</option>
                            <option value="principal">⭐ Principal</option>
                            <option value="secundario">➕ Secundario</option>
                            <option value="complementario">🎯 Complementario</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_muscle_group" className="text-xs font-semibold text-gray-700">Grupo muscular</Label>
                          <select
                            id="fe_muscle_group"
                            data-testid="fe-muscle-group"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.muscle_group || ''}
                            onChange={(e) => setVideoData({...videoData, muscle_group: e.target.value})}
                          >
                            <option value="">— Sin etiquetar —</option>
                            <optgroup label="Tren inferior">
                              <option value="gluteo">Glúteo</option>
                              <option value="isquios">Isquios / Femorales</option>
                              <option value="cuadriceps">Cuádriceps</option>
                              <option value="aductor">Aductor</option>
                              <option value="abductor">Abductor</option>
                              <option value="pantorrilla">Pantorrilla</option>
                            </optgroup>
                            <optgroup label="Tren superior">
                              <option value="espalda">Espalda</option>
                              <option value="hombros">Hombros</option>
                              <option value="pecho">Pecho</option>
                              <option value="biceps">Bíceps</option>
                              <option value="triceps">Tríceps</option>
                            </optgroup>
                            <optgroup label="Core y otros">
                              <option value="lumbar">Lumbar</option>
                              <option value="core">Core / Abdomen</option>
                              <option value="cardio">Cardio</option>
                            </optgroup>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_muscle_group_especifico" className="text-xs font-semibold text-gray-700">Músculo específico (opcional)</Label>
                          <Input
                            id="fe_muscle_group_especifico"
                            data-testid="fe-muscle-group-especifico"
                            placeholder="ej: hombro anterior, gluteo medio…"
                            value={videoData.muscle_group_especifico || ''}
                            onChange={(e) => setVideoData({...videoData, muscle_group_especifico: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_ejercicio_base" className="text-xs font-semibold text-gray-700">Ejercicio base (opcional)</Label>
                          <Input
                            id="fe_ejercicio_base"
                            data-testid="fe-ejercicio-base"
                            placeholder="ej: hip thrust (raíz sin la variante)"
                            value={videoData.ejercicio_base || ''}
                            onChange={(e) => setVideoData({...videoData, ejercicio_base: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_movement_pattern" className="text-xs font-semibold text-gray-700">Patrón de movimiento</Label>
                          <select
                            id="fe_movement_pattern"
                            data-testid="fe-movement-pattern"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.movement_pattern || ''}
                            onChange={(e) => setVideoData({...videoData, movement_pattern: e.target.value})}
                          >
                            <option value="">— Sin especificar —</option>
                            <option value="push">Push (empuje)</option>
                            <option value="pull">Pull (jalón)</option>
                            <option value="legs">Legs (pierna)</option>
                            <option value="full_body">Full body</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_recommended_sex" className="text-xs font-semibold text-gray-700">Sexo recomendado</Label>
                          <select
                            id="fe_recommended_sex"
                            data-testid="fe-recommended-sex"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.recommended_sex || 'ambos'}
                            onChange={(e) => setVideoData({...videoData, recommended_sex: e.target.value})}
                          >
                            <option value="ambos">👥 Ambos</option>
                            <option value="mujer">🌸 Mujer</option>
                            <option value="hombre">💪 Hombre</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_location" className="text-xs font-semibold text-gray-700">Lugar de entrenamiento</Label>
                          <select
                            id="fe_location"
                            data-testid="fe-location"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.location || 'ambos'}
                            onChange={(e) => setVideoData({...videoData, location: e.target.value})}
                          >
                            <option value="ambos">🏠🏋️ Casa y Gym</option>
                            <option value="casa">🏠 Solo Casa</option>
                            <option value="gym">🏋️ Solo Gym</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fe_grip" className="text-xs font-semibold text-gray-700">Agarre (para bloqueos precisos)</Label>
                          <select
                            id="fe_grip"
                            data-testid="fe-grip"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={videoData.grip || ''}
                            onChange={(e) => setVideoData({...videoData, grip: e.target.value})}
                          >
                            <option value="">— Sin especificar —</option>
                            <option value="supino">🖐️ Supino (palmas arriba)</option>
                            <option value="prono">🤚 Prono (palmas abajo)</option>
                            <option value="neutro">✋ Neutro (palmas al centro)</option>
                            <option value="mixto">🔀 Mixto (una y otra)</option>
                          </select>
                        </div>
                      </div>
                      {/* ⭐ Ejercicio OBLIGATORIO — Siempre incluir en la rutina si el músculo se entrena ese día */}
                      <div className="mt-3 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(251, 191, 36, 0.10)', border: '1px solid rgba(251, 191, 36, 0.35)' }}>
                        <input
                          id="fe_is_mandatory"
                          data-testid="fe-is-mandatory"
                          type="checkbox"
                          className="mt-0.5 w-4 h-4 accent-amber-500"
                          checked={!!videoData.is_mandatory}
                          onChange={(e) => setVideoData({ ...videoData, is_mandatory: e.target.checked })}
                        />
                        <div className="flex-1">
                          <Label htmlFor="fe_is_mandatory" className="text-xs font-bold text-amber-700 cursor-pointer">
                            ⭐ Ejercicio OBLIGATORIO (Siempre incluir)
                          </Label>
                          <p className="text-[11px] text-amber-800/80 leading-snug mt-0.5">
                            Cuando este grupo muscular se entrene en un día, este ejercicio se incluirá <b>SIEMPRE</b>. Los espacios restantes se llenan con otros ejercicios rotativos. Ejemplo: marca <b>Hip Thrust</b> para que aparezca siempre en días de glúteo.
                          </p>
                        </div>
                      </div>
                      {/* 🎬 Video de BIENVENIDA — Se muestra en el modal automático cuando una clienta entra por primera vez */}
                      <div className="mt-3 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(var(--brand-primary-glow), 0.10)', border: '1px solid rgba(var(--brand-primary-glow), 0.35)' }}>
                        <input
                          id="fe_is_welcome_video"
                          data-testid="fe-is-welcome-video"
                          type="checkbox"
                          className="mt-0.5 w-4 h-4 accent-pink-500"
                          checked={!!videoData.is_welcome_video}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setVideoData((prev) => ({
                              ...prev,
                              is_welcome_video: checked,
                              // Auto-selecciona la categoría "bienvenida" al marcar;
                              // si la desmarca y estaba en "bienvenida", la vacía.
                              category: checked ? 'bienvenida' : (prev.category === 'bienvenida' ? '' : prev.category),
                            }));
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor="fe_is_welcome_video" className="text-xs font-bold text-pink-700 cursor-pointer">
                            🎬 Video de BIENVENIDA (Se muestra a nuevas clientas)
                          </Label>
                          <p className="text-[11px] text-pink-800/80 leading-snug mt-0.5">
                            Cuando una clienta entre por primera vez a su Dashboard verá este video en un modal automático de bienvenida. La selección respeta el <b>sexo recomendado</b> (mujer/hombre) y los <b>planes permitidos</b> (basic/premium), así puedes tener videos diferentes por perfil. Sugerencia: sube el mismo video a esta biblioteca marcado como "Video de bienvenida" y filtra por plan/sexo si quieres variantes.
                          </p>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                        Estos campos permiten al generador armar splits como <b>Push/Pull/Legs</b> (hombres) o <b>Glúteo/Superior/Cuádriceps</b> (mujeres), y elegir 1 activador + 5 principales + 1 complementario por día automáticamente.
                      </p>
                    </div>

                    {/* URL del video — SIEMPRE visible, sin importar la fuente.
                        Deisy edita videos que ya están en Sirv, Cloudinary o Drive
                        y necesita poder pegar el nuevo link en cualquier caso. */}
                    <div className="space-y-2">
                      <Label htmlFor="video-url">🔗 URL del video</Label>
                      <Input
                        id="video-url"
                        data-testid="video-url-input"
                        placeholder="https://…b-cdn.net/... · https://…sirv.com/videos/... · https://drive.google.com/file/d/... · YouTube"
                        value={videoData.video_url || ''}
                        onChange={(e) => setVideoData({ ...videoData, video_url: e.target.value })}
                      />
                      <p className="text-xs text-gray-400 leading-snug">
                        Pegá cualquier URL directa al video: Bunny Stream (pull zone <code>*.b-cdn.net</code>), Sirv, Cloudinary, YouTube o Google Drive público.
                      </p>
                      {videoData.video_source === 'youtube' && (
                        <p className="text-[11px] text-gray-500">Ej YouTube: <code>https://www.youtube.com/watch?v=dQw4w9WgXcQ</code></p>
                      )}
                      {videoData.video_source === 'drive' && (
                        <p className="text-[11px] text-yellow-400 leading-snug">
                          ⚠️ Si usás Google Drive, el archivo debe estar compartido públicamente (cualquiera con el enlace).
                        </p>
                      )}
                      {videoData.video_source === 'bunny' && (
                        <p className="text-[11px] text-amber-400 leading-snug">
                          🐰 Bunny Stream: pegá la URL pública directa MP4 (dominio <code>*.b-cdn.net</code>). Se reproduce en HTML5 nativo — igual que Sirv, sin iframe.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="video_description">Descripción</Label>
                      <Textarea
                        id="video_description"
                        value={videoData.description}
                        onChange={(e) => setVideoData({...videoData, description: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>🖼️ Portada del Video</Label>
                      <div className="space-y-3">
                        {/* Opción 1: URL de imagen (Google Drive o cualquier URL) */}
                        <div>
                          <Label htmlFor="thumbnail_url" className="text-xs text-gray-400">Opción 1: URL de imagen (recomendado - no se borra al desplegar)</Label>
                          <Input
                            id="thumbnail_url"
                            type="text"
                            placeholder="https://drive.google.com/uc?id=... o URL directa de imagen"
                            value={videoData.thumbnail_url || ''}
                            onChange={(e) => setVideoData({...videoData, thumbnail_url: e.target.value})}
                            className="mt-1"
                          />
                          <p className="text-xs text-green-400 mt-1">
                            💡 Para Google Drive: Subir imagen → Compartir → Copiar enlace → Reemplazar por: https://drive.google.com/uc?id=ID_DEL_ARCHIVO
                          </p>
                        </div>
                        
                        {/* Opción 2: Subir archivo (se borra al desplegar) */}
                        <div>
                          <Label htmlFor="thumbnail_file" className="text-xs text-gray-400">Opción 2: Subir archivo (⚠️ se borra al desplegar)</Label>
                          <Input
                            id="thumbnail_file"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setThumbnailFile(e.target.files[0])}
                            className="cursor-pointer mt-1"
                          />
                          {thumbnailFile && <p className="text-xs text-yellow-500 mt-1">⚠️ Archivo seleccionado: {thumbnailFile.name} - Se perderá en el próximo deploy</p>}
                        </div>
                      </div>
                      
                      {/* Vista previa */}
                      {(videoData.thumbnail_url || editingVideo?.thumbnail_url) && !thumbnailFile && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
                          <img 
                            src={videoData.thumbnail_url || editingVideo?.thumbnail_url} 
                            alt="Portada actual" 
                            className="w-32 h-20 object-cover rounded border"
                            onError={(e) => {e.target.style.display = 'none'}}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Control de Acceso */}
                    <div className="space-y-3 p-4 rounded-lg border-2 border-pink-500/30 bg-pink-500/10">
                      <Label className="text-pink-400 font-bold">🔐 Control de Acceso</Label>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="video_free"
                          checked={videoData.is_free}
                          onChange={(e) => setVideoData({...videoData, is_free: e.target.checked, allowed_plans: e.target.checked ? [] : videoData.allowed_plans})}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="video_free" className="text-sm text-gray-200">✨ Video Gratuito para TODOS</Label>
                      </div>
                      
                      {!videoData.is_free && (
                        <div className="space-y-2 mt-3">
                          <Label className="text-sm text-gray-300">¿Quiénes obtienen este video GRATIS? (Los no seleccionados deben pagar)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {PLAN_OPTIONS.map((plan) => (
                              <label 
                                key={plan.id} 
                                className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                                  videoData.allowed_plans?.includes(plan.id) 
                                    ? 'border-pink-500 bg-pink-500/20' 
                                    : 'border-gray-600 hover:border-pink-400 bg-gray-800/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={videoData.allowed_plans?.includes(plan.id) || false}
                                  onChange={(e) => {
                                    const newPlans = e.target.checked
                                      ? [...(videoData.allowed_plans || []), plan.id]
                                      : (videoData.allowed_plans || []).filter(p => p !== plan.id);
                                    setVideoData({...videoData, allowed_plans: newPlans});
                                  }}
                                  className="w-4 h-4"
                                />
                                <span className={`w-3 h-3 rounded-full ${plan.color}`}></span>
                                <span className="text-sm text-gray-200">{plan.name}</span>
                              </label>
                            ))}
                          </div>
                          {(!videoData.allowed_plans || videoData.allowed_plans.length === 0) && (
                            <p className="text-xs text-amber-400 mt-1">ℹ️ Ningún plan seleccionado = Todos deben pagar para acceder</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 🔁 Ejercicios de reemplazo — definidos por admin para que la clienta
                        pueda cambiar el ejercicio en su rutina de Fit Evolution. */}
                    <ReplacementExercisesField
                      videoData={videoData}
                      setVideoData={setVideoData}
                      videosList={videosList}
                      currentVideoId={editingVideo?.id}
                    />
                    
                    <div className="flex gap-3">
                      <Button 
                        type="submit" 
                        style={{ backgroundColor: '#FF69B4' }}
                        disabled={uploadingVideo}
                      >
                        {uploadingVideo ? '⏳ Procesando...' : editingVideo ? '💾 Guardar Cambios' : '📤 Crear Video'}
                      </Button>
                      {editingVideo && (
                        <Button type="button" variant="outline" onClick={handleCancelEditVideo}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                    {uploadingVideo && (
                      <div className="bg-blue-900/30 border border-blue-500/30 rounded p-3">
                        <p className="text-sm text-blue-300">⏳ Procesando video... Esto puede tardar varios minutos si estás subiendo un archivo</p>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>

              {/* Lista de Videos - Grid de Cards */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                      <CardTitle>📹 Videos ({(() => {
                        const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const q = norm(videoSearchTerm.trim());
                        return videosList.filter(v => {
                          const matchesText = !q || (
                            norm(v.title).includes(q) ||
                            norm(v.description).includes(q) ||
                            norm(v.category).includes(q) ||
                            norm(v.exercise_type).includes(q) ||
                            norm(v.muscle_group).includes(q) ||
                            norm(v.body_part).includes(q) ||
                            norm(v.location).includes(q) ||
                            norm(v.grip).includes(q) ||
                            norm(v.recommended_sex).includes(q) ||
                            norm(v.content_type).includes(q) ||
                            // Búsqueda por etiquetas visibles (permite escribir "sirv", "drive", ".mov", "pesado")
                            getVideoTags(v).some(t => norm(t.label).includes(q))
                          );
                          const matchesTag = !videoTagFilter || getVideoTags(v).some(t => t.key === videoTagFilter);
                          return matchesText && matchesTag;
                        }).length;
                      })()})</CardTitle>
                      <CardDescription>Gestiona tus videos de contenido</CardDescription>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Input
                        data-testid="video-search-input"
                        type="text"
                        placeholder="🔍 Buscar (título, etiqueta, sirv, drive, .mov, pesado…)"
                        value={videoSearchTerm}
                        onChange={(e) => setVideoSearchTerm(e.target.value)}
                        className="w-64 bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                      />
                      {/* Chips de filtro rápido por etiqueta */}
                      <div className="flex items-center gap-1 flex-wrap" data-testid="video-tag-filters">
                        {[
                          { key: '', label: 'Todos', color: 'bg-white/10 text-white' },
                          { key: 'health-ok', label: '✅ Reproduce OK', color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
                          { key: 'health-error', label: '❌ No reproduce', color: 'bg-red-500/20 text-red-300 border border-red-500/40' },
                          { key: 'source-bunny', label: '🐰 Bunny', color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
                          { key: 'source-sirv', label: '☁️ Sirv (legacy)', color: 'bg-slate-500/20 text-slate-300 border border-slate-500/40' },
                          { key: 'formato-mov', label: '🟠 .MOV', color: 'bg-orange-500/20 text-orange-300 border border-orange-500/40' },
                          { key: 'pesado', label: '🔴 Pesado', color: 'bg-red-500/20 text-red-300 border border-red-500/40' },
                          { key: 'nombre-errado', label: '⚠️ Nombre Errado', color: 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40' },
                        ].map(f => (
                          <button
                            key={f.key || 'all'}
                            type="button"
                            onClick={() => setVideoTagFilter(f.key)}
                            data-testid={`video-tag-filter-${f.key || 'all'}`}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all ${
                              videoTagFilter === f.key ? f.color + ' ring-2 ring-white/30' : 'bg-white/5 text-white/60 hover:bg-white/10'
                            }`}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                      <Button
                        data-testid="open-bulk-tag-editor"
                        onClick={() => setShowBulkTagEditor(true)}
                        variant="outline"
                        size="sm"
                        className="bg-emerald-600/20 border-emerald-500 hover:bg-emerald-600/40 text-emerald-200"
                      >
                        🏷️ Etiquetar en lote
                      </Button>
                      {/* 2026-02 · Migrar todos los videos de Sirv a Bunny Stream matcheando por título. */}
                      <Button
                        data-testid="migrate-sirv-to-bunny-btn"
                        variant="outline"
                        size="sm"
                        className="bg-amber-500/20 border-amber-500 hover:bg-amber-500/40 text-amber-100"
                        onClick={async () => {
                          if (!window.confirm('¿Migrar TODOS los videos con URL de Sirv a Bunny Stream (library 761934)? Se hará match por título; los que no aparezcan en Bunny quedarán sin cambios.')) return;
                          const t = toast.loading('🐰 Consultando biblioteca de Bunny y migrando…');
                          try {
                            const { data } = await axios.post(`${API}/admin/videos/migrate-sirv-to-bunny`, {}, { withCredentials: false });
                            const missed = (data?.unmatched_titles_sample || []).length;
                            const repaired = data?.repaired_existing_bunny_urls || 0;
                            toast.success(
                              `✅ Migración: ${data?.migrated || 0}/${data?.sirv_videos_found || 0} Sirv→Bunny · ${repaired} URLs viejas reparadas a /embed/ · biblioteca Bunny tiene ${data?.bunny_library_items || 0} items` +
                              (missed ? ` · ${missed} sin match (consola).` : ''),
                              { id: t, duration: 9000 }
                            );
                            if (missed) console.warn('[migrate-sirv-to-bunny] títulos sin match:', data.unmatched_titles_sample);
                            fetchVideos();
                          } catch (err) {
                            toast.error(`No se pudo migrar: ${err?.response?.data?.detail || err.message}`, { id: t });
                          }
                        }}
                      >
                        🐰 Migrar Sirv → Bunny
                      </Button>
                      {/* 2026-02 · Verifica que cada URL responda 200 (HEAD/GET range). Guarda health_status en cada video. */}
                      <Button
                        data-testid="check-videos-health-btn"
                        variant="outline"
                        size="sm"
                        className="bg-emerald-600/20 border-emerald-500 hover:bg-emerald-600/40 text-emerald-100"
                        onClick={async () => {
                          const t = toast.loading('🩺 Verificando reproducibilidad de cada video…');
                          try {
                            const { data } = await axios.post(`${API}/admin/videos/check-health`, {}, { withCredentials: false });
                            toast.success(
                              `✅ Health check: ${data?.ok || 0} OK · ❌ ${data?.error || 0} rotos · ❔ ${data?.skipped || 0} sin verificar (Drive) · Total ${data?.total || 0}`,
                              { id: t, duration: 8000 }
                            );
                            fetchVideos();
                          } catch (err) {
                            toast.error(`Error verificando: ${err?.response?.data?.detail || err.message}`, { id: t });
                          }
                        }}
                      >
                        🩺 Verificar reproducibilidad
                      </Button>
                      {/* 2026-02 · Sincronizar video_url + video_source de todas las
                          rutinas activas con los links vigentes del catálogo. Útil
                          después de mover videos a Bunny/Sirv. Endpoint existente:
                          POST /api/admin/workouts/sync-video-urls. */}
                      <Button
                        data-testid="sync-workout-video-urls-btn"
                        variant="outline"
                        size="sm"
                        className="bg-amber-500/20 border-amber-500 hover:bg-amber-500/40 text-amber-100"
                        onClick={async () => {
                          if (!window.confirm('¿Sincronizar las URLs de video de TODAS las rutinas activas con los links actuales del gestor de videos?')) return;
                          try {
                            const { data } = await axios.post(`${API}/admin/workouts/sync-video-urls`, {}, { withCredentials: false });
                            const unmatched = (data?.unmatched_exercise_names_sample || []).length;
                            toast.success(
                              `✅ Sincronización: ${data?.plans_updated || 0}/${data?.total_active_plans || 0} rutinas actualizadas · ${data?.exercises_synced || 0} ejercicios re-linkeados` +
                              (unmatched ? ` · ${unmatched} sin match (revisá la consola).` : '')
                            );
                            if (unmatched) console.warn('Ejercicios sin match:', data.unmatched_exercise_names_sample);
                          } catch (err) {
                            const msg = err?.response?.data?.detail || err.message || 'Error';
                            toast.error(`No se pudo sincronizar: ${msg}`);
                          }
                        }}
                      >
                        🔄 Sincronizar URLs de rutinas
                      </Button>
                      <SortSelector 
                        type="videos" 
                        options={[
                          { field: 'title', label: 'Nombre' },
                          { field: 'created_at', label: 'Fecha' },
                          { field: 'category', label: 'Categoría' }
                        ]} 
                      />
                      <Button 
                        onClick={async () => {
                          try {
                            const [field, order] = (sortConfig.videos?.field + '-' + sortConfig.videos?.order).split('-');
                            const sortField = field === 'created_at' ? 'date' : field;
                            await axios.post(`${API}/admin/videos/sort?sort_by=${sortField}&sort_order=${order}`, {}, { withCredentials: false });
                            toast.success(`✅ Videos ordenados por ${sortField} y guardado en la base de datos`);
                            fetchVideos();
                          } catch (error) {
                            toast.error('Error al guardar orden');
                          }
                        }}
                        variant="outline" 
                        size="sm"
                        className="bg-pink-600/20 border-pink-500 hover:bg-pink-600/40"
                      >
                        💾 Guardar Orden
                      </Button>
                      <Button
                        data-testid="btn-sync-video-urls"
                        onClick={async () => {
                          if (!window.confirm('Se recorrerán TODAS las rutinas activas y se actualizarán los enlaces de video obsoletos con los actuales de la biblioteca. ¿Continuar?')) return;
                          try {
                            const r = await axios.post(`${API}/admin/workouts/sync-video-urls`);
                            const unmatched = (r.data.unmatched_exercise_names_sample || []).length;
                            toast.success(`✅ Sincronización lista · ${r.data.plans_updated}/${r.data.total_active_plans} rutinas · ${r.data.exercises_synced} enlaces actualizados${unmatched ? ` · ${unmatched} ejercicios sin video en la biblioteca` : ''}`, { duration: 6000 });
                            if (unmatched) {
                              console.warn('[sync-video-urls] Ejercicios sin match:', r.data.unmatched_exercise_names_sample);
                            }
                          } catch (error) {
                            toast.error('Error sincronizando: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="bg-cyan-600/20 border-cyan-500 hover:bg-cyan-600/40 text-cyan-200"
                      >
                        🔗 Sync enlaces
                      </Button>
                      <Button
                        data-testid="btn-clean-video-titles"
                        onClick={async () => {
                          if (!window.confirm('Se limpiarán los espacios extras al inicio/final de los títulos y descripciones de todos los videos. ¿Continuar?')) return;
                          try {
                            const r = await axios.post(`${API}/admin/videos/clean-titles`);
                            const dup = Object.keys(r.data.duplicates || {}).length;
                            toast.success(`✅ Limpieza lista · ${r.data.updated}/${r.data.total} videos ajustados · ${r.data.drive_urls} aún en Drive${dup ? ` · ${dup} duplicados detectados` : ''}`);
                            fetchVideos();
                          } catch (error) {
                            toast.error('Error limpiando títulos: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="bg-amber-600/20 border-amber-500 hover:bg-amber-600/40 text-amber-200"
                      >
                        🧹 Limpiar títulos
                      </Button>
                      <Button onClick={fetchVideos} variant="outline" size="sm">
                        🔄 Actualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingVideos ? (
                    <div className="text-center py-8">Cargando videos...</div>
                  ) : videosList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-lg mb-2">No hay videos aún</p>
                      <p className="text-sm">Crea tu primer video usando el formulario arriba</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(() => {
                        const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const q = norm(videoSearchTerm.trim());
                        const filtered = videosList.filter(v => {
                          const matchesText = !q || (
                            norm(v.title).includes(q) ||
                            norm(v.description).includes(q) ||
                            norm(v.category).includes(q) ||
                            norm(v.exercise_type).includes(q) ||
                            norm(v.muscle_group).includes(q) ||
                            norm(v.body_part).includes(q) ||
                            norm(v.location).includes(q) ||
                            norm(v.grip).includes(q) ||
                            norm(v.recommended_sex).includes(q) ||
                            norm(v.content_type).includes(q) ||
                            getVideoTags(v).some(t => norm(t.label).includes(q))
                          );
                          const matchesTag = !videoTagFilter || getVideoTags(v).some(t => t.key === videoTagFilter);
                          return matchesText && matchesTag;
                        });
                        if ((q || videoTagFilter) && filtered.length === 0) {
                          return (
                            <div className="col-span-full text-center py-8 text-gray-400" data-testid="video-search-empty">
                              No se encontraron videos que coincidan con {videoTagFilter ? `la etiqueta seleccionada` : `"${videoSearchTerm}"`}
                            </div>
                          );
                        }
                        return sortItems(filtered, 'videos').map((video) => (
                        <div 
                          key={video.id} 
                          className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-gray-100">
                            {video.thumbnail_url ? (
                              <img 
                                src={video.thumbnail_url} 
                                alt={video.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                                <span className="text-5xl">🎬</span>
                              </div>
                            )}
                            {/* Badges dinámicos de estado real del video (Sirv/Drive/MOV/Pesado) */}
                            {(() => {
                              const tags = getVideoTags(video);
                              if (tags.length === 0) return null;
                              return (
                                <div className="absolute top-2 right-2 flex flex-col gap-1 items-end max-w-[70%]"
                                     data-testid={`video-status-tags-${video.id}`}>
                                  {tags.map(t => (
                                    <span
                                      key={t.key}
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold shadow ${t.color}`}
                                      title={t.label}
                                    >
                                      {t.label}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Badge de gratis */}
                            {video.is_free && (
                              <div className="absolute top-2 left-2 px-2 py-1 bg-green-500 text-white rounded text-xs font-bold">
                                ✅ Gratis
                              </div>
                            )}
                            {/* 2026-02 · Indicador visual grande del estado de reproducción
                                (poblado por POST /api/admin/videos/check-health). Dot
                                verde/rojo/gris arriba a la izquierda debajo del badge Gratis. */}
                            {video.health_status && (
                              <div
                                data-testid={`video-health-dot-${video.id}`}
                                className={`absolute ${video.is_free ? 'top-10' : 'top-2'} left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black shadow-lg ${
                                  video.health_status === 'ok' ? 'bg-emerald-500 text-white'
                                  : video.health_status === 'error' ? 'bg-red-600 text-white'
                                  : 'bg-gray-500 text-white'
                                }`}
                                title={
                                  video.health_status === 'ok' ? 'Se reproduce correctamente'
                                  : video.health_status === 'error' ? 'El enlace NO reproduce (404 / timeout)'
                                  : 'No verificable (Drive u origen sin HEAD)'
                                }
                              >
                                <span className={`w-2 h-2 rounded-full ${
                                  video.health_status === 'ok' ? 'bg-emerald-100'
                                  : video.health_status === 'error' ? 'bg-red-100' : 'bg-gray-200'
                                }`} />
                                {video.health_status === 'ok' ? 'OK'
                                  : video.health_status === 'error' ? 'ROTO' : '—'}
                              </div>
                            )}
                          </div>
                          
                          {/* Contenido */}
                          <div className="p-4">
                            <h4 className="font-bold text-lg text-gray-900 line-clamp-1 mb-1">{video.title}</h4>
                            {typeof video.file_size_mb === 'number' && video.file_size_mb > 0 && (
                              <div
                                className={`text-xs font-semibold mb-1 ${video.file_size_mb > 30 ? 'text-red-600' : 'text-gray-500'}`}
                                data-testid={`video-size-${video.id}`}
                              >
                                📦 {video.file_size_mb.toFixed(1)} MB
                              </div>
                            )}
                            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{video.description}</p>
                            
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                {video.category?.replace(/_/g, ' ')}
                              </span>
                              <span>
                                {video.created_at ? new Date(video.created_at).toLocaleDateString('es-CO') : ''}
                              </span>
                            </div>

                            {/* === FIT EVOLUTION — Badges de etiquetas === */}
                            {(video.exercise_type || video.muscle_group || video.is_mandatory || video.is_welcome_video || (video.recommended_sex && video.recommended_sex !== 'ambos')) ? (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {video.is_welcome_video && (
                                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full" style={{ background: 'linear-gradient(90deg,#ec4899,#f472b6)', color: 'white' }} title="Video de Bienvenida · modal automático para nuevas clientas">
                                    🎬 BIENVENIDA
                                  </span>
                                )}
                                {video.is_mandatory && (
                                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full" style={{ background: 'linear-gradient(90deg,#f59e0b,#d97706)', color: 'white' }} title="Obligatorio · siempre incluido en la rutina">
                                    ⭐ OBLIGATORIO
                                  </span>
                                )}
                                {video.exercise_type && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ background: '#FFE4EF', color: '#C21876' }}>
                                    {video.exercise_type === 'activador' ? '🔥 Activador' : video.exercise_type === 'principal' ? '⭐ Principal' : video.exercise_type === 'secundario' ? '➕ Secundario' : '🎯 Complementario'}
                                  </span>
                                )}
                                {video.muscle_group && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-700 capitalize">
                                    {video.muscle_group}
                                  </span>
                                )}
                                {video.recommended_sex && video.recommended_sex !== 'ambos' && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700 capitalize">
                                    {video.recommended_sex === 'mujer' ? '🌸 Mujer' : '💪 Hombre'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="mb-3">
                                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500 border border-dashed border-gray-300">
                                  ⚠️ Sin etiquetar
                                </span>
                              </div>
                            )}
                            
                            {/* Botones */}
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="flex-1"
                                onClick={() => window.open(video.video_url, '_blank')}
                              >
                                👁️ Ver
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEditVideo(video)}
                              >
                                ✏️
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteVideo(video.id)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ));
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === 'ebooks' && (
            <div className="space-y-6">
              {/* Tabs para diferentes tipos de contenido */}
              <div className="flex gap-2 border-b border-gray-200 mb-6">
                <button 
                  onClick={() => setActiveSection('videos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📹 Videos
                </button>
                <button className="pb-3 px-4 border-b-2 border-[#FF69B4] text-[#FF69B4] font-semibold">
                  📚 Ebooks
                </button>
                <button 
                  onClick={() => setActiveSection('recetarios')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🍳 Recetarios
                </button>
                <button 
                  onClick={() => setActiveSection('planes')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📋 Planes
                </button>
                <button 
                  onClick={() => setActiveSection('codigos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🏷️ Códigos
                </button>
              </div>
              
              {/* Info de descuentos */}
              <div className="rounded-xl p-4 border border-white/10" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                <p className="text-sm text-gray-300">
                  <span className="font-semibold">💰 Descuentos por plan:</span> Basic 20% | Premium/Competitivo 30% | Gold 50%
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{editingEbook ? '✏️ Editar Ebook' : '➕ Crear Nuevo Ebook'}</CardTitle>
                  <CardDescription>
                    {editingEbook ? 'Actualiza la información del ebook' : 'Sube un ebook en PDF o proporciona un enlace de Google Drive'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitEbook} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ebook_title">Título</Label>
                        <Input
                          id="ebook_title"
                          value={ebookData.title}
                          onChange={(e) => setEbookData({...ebookData, title: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ebook_price">Precio (USD)</Label>
                        <Input
                          id="ebook_price"
                          type="number"
                          step="0.01"
                          placeholder="0 para gratis"
                          value={ebookData.price}
                          onChange={(e) => setEbookData({...ebookData, price: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ebook_original_price" className="flex items-center gap-2">
                          <span className="line-through text-red-500">Precio Gancho</span>
                          <span className="text-xs text-gray-500">(Opcional - Tachado)</span>
                        </Label>
                        <Input
                          id="ebook_original_price"
                          type="number"
                          step="0.01"
                          placeholder="Ej: 35 (aparece tachado)"
                          value={ebookData.original_price}
                          onChange={(e) => setEbookData({...ebookData, original_price: e.target.value})}
                          className="border-red-200"
                        />
                      </div>
                      
                      {/* Control de Acceso para Ebooks */}
                      <div className="space-y-3 p-4 rounded-lg border-2 border-pink-500/30 bg-pink-500/10 md:col-span-2">
                        <Label className="text-pink-400 font-bold">🔐 Control de Acceso</Label>
                        
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="ebook_free"
                            checked={ebookData.is_free}
                            onChange={(e) => setEbookData({...ebookData, is_free: e.target.checked, allowed_plans: e.target.checked ? [] : ebookData.allowed_plans})}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="ebook_free" className="text-sm text-gray-200">✨ Ebook Gratuito para TODOS</Label>
                        </div>
                        
                        {!ebookData.is_free && (
                          <div className="space-y-2 mt-3">
                            <Label className="text-sm text-gray-300">¿Quiénes obtienen este ebook GRATIS? (Los no seleccionados deben pagar)</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {PLAN_OPTIONS.map((plan) => (
                                <label 
                                  key={plan.id} 
                                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                                    ebookData.allowed_plans?.includes(plan.id) 
                                      ? 'border-pink-500 bg-pink-500/20' 
                                      : 'border-gray-600 hover:border-pink-400 bg-gray-800/50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={ebookData.allowed_plans?.includes(plan.id) || false}
                                    onChange={(e) => {
                                      const newPlans = e.target.checked
                                        ? [...(ebookData.allowed_plans || []), plan.id]
                                        : (ebookData.allowed_plans || []).filter(p => p !== plan.id);
                                      setEbookData({...ebookData, allowed_plans: newPlans});
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className={`w-3 h-3 rounded-full ${plan.color}`}></span>
                                  <span className="text-sm text-gray-200">{plan.name}</span>
                                </label>
                              ))}
                            </div>
                            {(!ebookData.allowed_plans || ebookData.allowed_plans.length === 0) && (
                              <p className="text-xs text-amber-400 mt-1">ℹ️ Ningún plan seleccionado = Todos deben pagar para acceder</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="ebook_category">Categoría</Label>
                        <select
                          id="ebook_category"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={ebookData.category}
                          onChange={(e) => setEbookData({...ebookData, category: e.target.value})}
                          required
                        >
                          <option value="nutricion">Nutrición</option>
                          <option value="entrenamiento">Entrenamiento</option>
                          <option value="recetas">Recetas</option>
                          <option value="mindset">Mindset</option>
                          <option value="guias">Guías</option>
                          <option value="otros">Otros</option>
                        </select>
                      </div>
                    </div>

                    {/* Selector de Fuente del Archivo */}
                    {!editingEbook && (
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">📄 Fuente del Archivo</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setEbookData({...ebookData, file_source: 'file', file_url: ''})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              ebookData.file_source === 'file'
                                ? 'border-pink-500 bg-pink-500/20 shadow-md'
                                : 'border-gray-200 hover:border-pink-300'
                            }`}
                          >
                            <div className="text-2xl mb-2">📤</div>
                            <div className="font-semibold">Subir Archivo</div>
                            <div className="text-xs text-gray-500">PDF (hasta 50MB)</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEbookData({...ebookData, file_source: 'url', file_url: ''})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              ebookData.file_source === 'url'
                                ? 'border-blue-500 bg-blue-500/20 shadow-md'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="text-2xl mb-2">🔗</div>
                            <div className="font-semibold">Enlace Externo</div>
                            <div className="text-xs text-gray-500">Google Drive, Dropbox, etc.</div>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Campo según la fuente */}
                    {ebookData.file_source === 'file' && !editingEbook && (
                      <div className="space-y-2">
                        <Label htmlFor="ebook_file_upload">📄 Archivo PDF</Label>
                        <Input
                          id="ebook_file_upload"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setEbookFile(e.target.files[0])}
                          className="cursor-pointer"
                        />
                        <p className="text-xs text-gray-500">
                          {ebookFile ? `✅ ${ebookFile.name} (${(ebookFile.size / 1024 / 1024).toFixed(2)} MB)` : 'Selecciona un archivo PDF'}
                        </p>
                      </div>
                    )}

                    {ebookData.file_source === 'url' && (
                      <div className="space-y-2">
                        <Label htmlFor="ebook_file">🔗 URL del Archivo</Label>
                        <Input
                          id="ebook_file"
                          placeholder="https://drive.google.com/file/..."
                          value={ebookData.file_url}
                          onChange={(e) => setEbookData({...ebookData, file_url: e.target.value})}
                          required={ebookData.file_source === 'url'}
                        />
                        <p className="text-xs text-gray-500">Asegúrate de que el archivo esté compartido públicamente</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="ebook_description">Descripción</Label>
                      <Textarea
                        id="ebook_description"
                        value={ebookData.description}
                        onChange={(e) => setEbookData({...ebookData, description: e.target.value})}
                        required
                      />
                    </div>
                    
                    {/* Campo de portada - URL o archivo */}
                    <div className="space-y-2">
                      <Label>🖼️ Imagen de Portada</Label>
                      <div className="space-y-3">
                        {/* Opción 1: URL de imagen */}
                        <div>
                          <Label htmlFor="ebook_thumbnail_url" className="text-xs text-gray-400">Opción 1: URL de imagen (recomendado - no se borra al desplegar)</Label>
                          <Input
                            id="ebook_thumbnail_url"
                            type="text"
                            placeholder="https://drive.google.com/uc?id=... o URL directa"
                            value={ebookData.thumbnail_url || ''}
                            onChange={(e) => setEbookData({...ebookData, thumbnail_url: e.target.value})}
                            className="mt-1"
                          />
                          <p className="text-xs text-green-400 mt-1">
                            💡 Google Drive: Subir → Compartir → Copiar enlace → Usar: https://drive.google.com/uc?id=ID_ARCHIVO
                          </p>
                        </div>
                        
                        {/* Opción 2: Subir archivo */}
                        <div>
                          <Label htmlFor="ebook_thumbnail_file" className="text-xs text-gray-400">Opción 2: Subir archivo (⚠️ se borra al desplegar)</Label>
                          <Input
                            id="ebook_thumbnail_file"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setEbookThumbnailFile(e.target.files[0])}
                            className="cursor-pointer mt-1"
                          />
                          {ebookThumbnailFile && <p className="text-xs text-yellow-500 mt-1">⚠️ {ebookThumbnailFile.name} - Se perderá en próximo deploy</p>}
                        </div>
                      </div>
                      
                      {/* Vista previa */}
                      {(ebookData.thumbnail_url || (editingEbook && editingEbook.thumbnail_url)) && !ebookThumbnailFile && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
                          <img 
                            src={ebookData.thumbnail_url || editingEbook?.thumbnail_url} 
                            alt="Portada" 
                            className="w-24 h-32 object-cover rounded border"
                            onError={(e) => {e.target.style.display = 'none'}}
                          />
                        </div>
                      )}
                    </div>
                    
                    <Button type="submit" style={{ backgroundColor: '#FF69B4' }} disabled={uploadingEbook}>
                      {uploadingEbook ? '⏳ Subiendo...' : editingEbook ? '💾 Guardar' : '➕ Crear Ebook'}
                    </Button>
                    {editingEbook && (
                      <Button type="button" variant="outline" onClick={handleCancelEditEbook} className="ml-2">
                        Cancelar
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <CardTitle>📚 Ebooks ({ebooksList.length})</CardTitle>
                    <div className="flex items-center gap-3 flex-wrap">
                      <SortSelector 
                        type="ebooks" 
                        options={[
                          { field: 'title', label: 'Nombre' },
                          { field: 'created_at', label: 'Fecha' },
                          { field: 'price', label: 'Precio' },
                          { field: 'category', label: 'Categoría' }
                        ]} 
                      />
                      <Button 
                        onClick={async () => {
                          try {
                            const [field, order] = (sortConfig.ebooks?.field + '-' + sortConfig.ebooks?.order).split('-');
                            const sortField = field === 'created_at' ? 'date' : field;
                            await axios.post(`${API}/admin/ebooks/sort?sort_by=${sortField}&sort_order=${order}`, {}, { withCredentials: false });
                            toast.success(`✅ Ebooks ordenados por ${sortField} y guardado`);
                            fetchEbooks();
                          } catch (error) {
                            toast.error('Error al guardar orden');
                          }
                        }}
                        variant="outline" 
                        size="sm"
                        className="bg-pink-600/20 border-pink-500 hover:bg-pink-600/40"
                      >
                        💾 Guardar Orden
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingEbooks ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : ebooksList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No hay ebooks aún</div>
                  ) : (
                    <div className="space-y-4">
                      {sortItems(ebooksList, 'ebooks').map((ebook) => (
                        <div key={ebook.id} className="border rounded-lg p-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="flex gap-4">
                            <div className="w-32 h-20 bg-gray-200 rounded">
                              {ebook.thumbnail_url && (
                                <img src={ebook.thumbnail_url} alt={ebook.title} className="w-full h-full object-cover rounded" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">{ebook.title}</h4>
                              <p className="text-sm text-gray-600">{ebook.description}</p>
                              <span className="text-green-600 font-bold">${ebook.price}</span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditEbook(ebook)}>
                                ✏️ Editar
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteEbook(ebook.id)}>
                                🗑️ Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN RECETARIOS EN VIDEO */}
          {activeSection === 'recetarios' && (
            <div className="space-y-6">
              {/* Tabs para diferentes tipos de contenido */}
              <div className="flex gap-2 border-b border-gray-200 mb-6">
                <button 
                  onClick={() => setActiveSection('videos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📹 Videos
                </button>
                <button 
                  onClick={() => setActiveSection('ebooks')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📚 Ebooks
                </button>
                <button className="pb-3 px-4 border-b-2 border-[#FF69B4] text-[#FF69B4] font-semibold">
                  🍳 Recetarios
                </button>
                <button 
                  onClick={() => setActiveSection('planes')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📋 Planes
                </button>
                <button 
                  onClick={() => setActiveSection('codigos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🏷️ Códigos
                </button>
              </div>
              
              {/* Info de descuentos */}
              <div className="rounded-xl p-4 border border-white/10" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                <p className="text-sm text-gray-300">
                  <span className="font-semibold">💰 Descuentos por plan:</span> Basic 20% | Premium/Competitivo 30% | Gold 50%
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{editingRecetario ? '✏️ Editar Recetario' : '➕ Nuevo Recetario en Video'}</CardTitle>
                  <CardDescription>Gestiona los recetarios en video disponibles para tus clientas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Título del Recetario</Label>
                        <Input
                          value={recetarioData.title}
                          onChange={(e) => setRecetarioData({...recetarioData, title: e.target.value})}
                          placeholder="Ej: Desayunos Energéticos"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Precio (USD)</Label>
                        <Input
                          type="number"
                          value={recetarioData.price}
                          onChange={(e) => setRecetarioData({...recetarioData, price: e.target.value})}
                          placeholder="30"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <span className="line-through text-red-500">Precio Gancho</span>
                          <span className="text-xs text-gray-500">(Opcional)</span>
                        </Label>
                        <Input
                          type="number"
                          value={recetarioData.original_price}
                          onChange={(e) => setRecetarioData({...recetarioData, original_price: e.target.value})}
                          placeholder="Ej: 50 (tachado)"
                          className="border-red-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={recetarioData.is_free}
                            onChange={(e) => setRecetarioData({...recetarioData, is_free: e.target.checked})}
                            className="rounded"
                          />
                          <span>✓ Gratis</span>
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={recetarioData.description}
                        onChange={(e) => setRecetarioData({...recetarioData, description: e.target.value})}
                        placeholder="Descripción del recetario..."
                        rows={3}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Duración Total</Label>
                        <Input
                          value={recetarioData.duration}
                          onChange={(e) => setRecetarioData({...recetarioData, duration: e.target.value})}
                          placeholder="2h 30min"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cantidad de Recetas</Label>
                        <Input
                          type="number"
                          value={recetarioData.recipes_count}
                          onChange={(e) => setRecetarioData({...recetarioData, recipes_count: e.target.value})}
                          placeholder="15"
                        />
                      </div>
                    </div>
                    
                    {/* Selector de Fuente del Video */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">📹 Fuente del Video</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setRecetarioData({...recetarioData, video_source: 'youtube', video_url: ''})}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            recetarioData.video_source === 'youtube'
                              ? 'border-red-500 bg-red-50 shadow-md'
                              : 'border-gray-200 hover:border-red-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">▶️</div>
                          <div className="font-semibold text-sm">YouTube</div>
                          <div className="text-xs text-gray-500">Link del video</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setRecetarioData({...recetarioData, video_source: 'drive', video_url: ''})}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            recetarioData.video_source === 'drive'
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">📁</div>
                          <div className="font-semibold text-sm">Google Drive</div>
                          <div className="text-xs text-gray-500">Link compartido</div>
                        </button>
                      </div>
                    </div>

                    {/* Campo según la fuente seleccionada */}
                    {(recetarioData.video_source === 'youtube' || recetarioData.video_source === 'drive' || !recetarioData.video_source) && (
                      <div className="space-y-2">
                        <Label>URL del Video (Cloudinary, YouTube o Drive)</Label>
                        <Input
                          data-testid="recetario-video-url-input"
                          value={recetarioData.video_url}
                          onChange={(e) => setRecetarioData({...recetarioData, video_url: e.target.value})}
                          placeholder={recetarioData.video_source === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://res.cloudinary.com/... o https://drive.google.com/file/d/...'}
                        />
                        {recetarioData.video_source !== 'youtube' && (
                          <p className="text-xs text-green-400 leading-snug">
                            ✅ Recomendado: <b>Cloudinary</b>. Sube tu video (.mp4 / .mov) → clic en el archivo → <b>Copy URL</b> → pega aquí la URL <code>https://res.cloudinary.com/…/video/upload/…</code>. Reproduce nativo en iPhone.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Imagen de Portada</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setRecetarioThumbnailFile(e.target.files[0])}
                      />
                      {recetarioData.thumbnail_url && (
                        <img src={recetarioData.thumbnail_url} alt="Preview" className="w-32 h-24 object-cover rounded mt-2" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        onClick={handleSaveRecetario}
                        disabled={uploadingRecetario || !recetarioData.title || !recetarioData.price}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        {uploadingRecetario ? '⏳ Guardando...' : (editingRecetario ? '✏️ Actualizar' : '➕ Crear Recetario')}
                      </Button>
                      {editingRecetario && (
                        <Button type="button" variant="outline" onClick={handleCancelEditRecetario}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de Recetarios */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between flex-wrap gap-4">
                    <span>📋 Recetarios Existentes ({recetariosList.length})</span>
                    <div className="flex items-center gap-3">
                      <SortSelector 
                        type="recetarios" 
                        options={[
                          { field: 'title', label: 'Nombre' },
                          { field: 'created_at', label: 'Fecha' },
                          { field: 'price', label: 'Precio' }
                        ]} 
                      />
                      <Button variant="outline" size="sm" onClick={fetchRecetarios} disabled={loadingRecetarios}>
                        {loadingRecetarios ? '⏳' : '🔄'} Actualizar
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingRecetarios ? (
                    <p className="text-center py-4">⏳ Cargando recetarios...</p>
                  ) : recetariosList.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-lg mb-2">No hay recetarios aún</p>
                      <p className="text-sm">Crea tu primer recetario usando el formulario de arriba</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {sortItems(recetariosList, 'recetarios').map((recetario) => (
                        <div key={recetario.id} className="flex items-center gap-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          {recetario.thumbnail_url && (
                            <img src={recetario.thumbnail_url} alt={recetario.title} className="w-20 h-16 object-cover rounded" />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold">{recetario.title}</h4>
                            <p className="text-sm text-gray-600">
                              ${recetario.price} USD
                              {recetario.original_price && (
                                <span className="ml-2 line-through text-red-500">${recetario.original_price}</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">{recetario.recipes_count || 0} recetas • {recetario.duration}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditRecetario(recetario)}>
                              ✏️ Editar
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteRecetario(recetario.id)}>
                              🗑️
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN TIENDA */}
          {activeSection === 'tienda' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{editingProducto ? '✏️ Editar Producto' : '➕ Nuevo Producto'}</CardTitle>
                  <CardDescription>Gestiona los productos de tu tienda online</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre del Producto</Label>
                        <Input
                          value={productoData.name}
                          onChange={(e) => setProductoData({...productoData, name: e.target.value})}
                          placeholder="Ej: Proteína Whey Premium"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoría</Label>
                        <select
                          value={productoData.category}
                          onChange={(e) => setProductoData({...productoData, category: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="suplementos">Suplementos</option>
                          <option value="ropa">Ropa Deportiva</option>
                          <option value="accesorios">Accesorios</option>
                          <option value="equipos">Equipos</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={productoData.description}
                        onChange={(e) => setProductoData({...productoData, description: e.target.value})}
                        placeholder="Descripción del producto..."
                        rows={3}
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Precio (USD)</Label>
                        <Input
                          type="number"
                          value={productoData.price}
                          onChange={(e) => setProductoData({...productoData, price: e.target.value})}
                          placeholder="45"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <span className="line-through text-red-500">Precio Gancho</span>
                          <span className="text-xs text-gray-500">(Opcional)</span>
                        </Label>
                        <Input
                          type="number"
                          value={productoData.original_price}
                          onChange={(e) => setProductoData({...productoData, original_price: e.target.value})}
                          placeholder="Ej: 60 (tachado)"
                          className="border-red-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stock Disponible</Label>
                        <Input
                          type="number"
                          value={productoData.stock}
                          onChange={(e) => setProductoData({...productoData, stock: e.target.value})}
                          placeholder="50"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={productoData.is_free}
                          onChange={(e) => setProductoData({...productoData, is_free: e.target.checked})}
                          className="rounded"
                        />
                        <span>✓ Gratis</span>
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label>Imagen del Producto</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProductoThumbnailFile(e.target.files[0])}
                      />
                      {productoData.thumbnail_url && (
                        <img src={productoData.thumbnail_url} alt="Preview" className="w-32 h-32 object-cover rounded mt-2" />
                      )}
                      {productoThumbnailFile && (
                        <p className="text-sm text-green-600">✓ Archivo seleccionado: {productoThumbnailFile.name}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        onClick={handleSaveProducto} 
                        disabled={uploadingProducto || !productoData.name || !productoData.price}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        {uploadingProducto ? '⏳ Guardando...' : (editingProducto ? '✏️ Actualizar' : '➕ Crear Producto')}
                      </Button>
                      {editingProducto && (
                        <Button type="button" variant="outline" onClick={handleCancelEditProducto}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de Productos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between flex-wrap gap-4">
                    <span>🛒 Productos en Tienda ({productosList.length})</span>
                    <div className="flex items-center gap-3">
                      <SortSelector 
                        type="productos" 
                        options={[
                          { field: 'name', label: 'Nombre' },
                          { field: 'created_at', label: 'Fecha' },
                          { field: 'price', label: 'Precio' },
                          { field: 'category', label: 'Categoría' }
                        ]} 
                      />
                      <Button variant="outline" size="sm" onClick={fetchProductos} disabled={loadingProductos}>
                        {loadingProductos ? '⏳' : '🔄'} Actualizar
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Descuentos por plan: Basic 5% | Premium/Competitivo 10% | Gold 15%
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingProductos ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Cargando productos...</p>
                    </div>
                  ) : productosList.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-lg mb-2">📦 No hay productos aún</p>
                      <p className="text-sm">Crea tu primer producto usando el formulario de arriba</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sortItems(productosList, 'productos').map((producto) => (
                        <div key={producto.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="aspect-square mb-3 bg-gray-100 rounded-lg overflow-hidden">
                            {producto.thumbnail_url ? (
                              <img 
                                src={producto.thumbnail_url} 
                                alt={producto.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span className="text-4xl">📦</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <h4 className="font-semibold text-sm line-clamp-2">{producto.name}</h4>
                              <Badge variant="outline" className="text-xs ml-2 shrink-0">
                                {producto.category}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-2">{producto.description}</p>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-pink-600">${producto.price}</span>
                              {producto.original_price && producto.original_price > producto.price && (
                                <span className="text-xs text-gray-400 line-through">${producto.original_price}</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Stock: {producto.stock || 0}</span>
                              {producto.is_free && <Badge className="bg-green-500">GRATIS</Badge>}
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handleEditProducto(producto)}
                                className="flex-1"
                              >
                                ✏️ Editar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteProducto(producto.id)}
                                className="flex-1"
                              >
                                🗑️ Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === 'planes' && (
            <div className="space-y-6">
              {/* Tabs para diferentes tipos de contenido */}
              <div className="flex gap-2 border-b border-gray-200 mb-6">
                <button 
                  onClick={() => setActiveSection('videos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📹 Videos
                </button>
                <button 
                  onClick={() => setActiveSection('ebooks')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  📚 Ebooks
                </button>
                <button 
                  onClick={() => setActiveSection('recetarios')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🍳 Recetarios
                </button>
                <button className="pb-3 px-4 border-b-2 border-[#FF69B4] text-[#FF69B4] font-semibold">
                  📋 Planes
                </button>
                <button 
                  onClick={() => setActiveSection('codigos')}
                  className="pb-3 px-4 text-gray-600 hover:text-gray-800"
                >
                  🏷️ Códigos
                </button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{editingPlan ? '✏️ Editar Plan' : '➕ Crear Nuevo Plan'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitPlan} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre del Plan</Label>
                        <Input
                          value={planData.name}
                          onChange={(e) => setPlanData({...planData, name: e.target.value})}
                          placeholder="Ej: Plan Premium, Plan Gold, etc."
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Plan (Duración)</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={planData.plan_type}
                          onChange={(e) => setPlanData({...planData, plan_type: e.target.value})}
                          required
                        >
                          <option value="">Selecciona duración</option>
                          <option value="mensual">📅 Mensual (30 días)</option>
                          <option value="bimestral">📅 Bimestral (2 meses)</option>
                          <option value="trimestral">📅 Trimestral (3 meses)</option>
                          <option value="semestral">📅 Semestral (6 meses)</option>
                          <option value="anual">📅 Anual (12 meses)</option>
                          <option value="unico">💰 Pago único (sin renovación)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Precio (USD)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={planData.price}
                          onChange={(e) => setPlanData({...planData, price: e.target.value})}
                          placeholder="Ej: 99.99"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Código Promocional Asociado (Opcional)</Label>
                        <Input
                          placeholder="Ej: PROMO2024 - Deja vacío si no aplica"
                          value={planData.duration}
                          onChange={(e) => setPlanData({...planData, duration: e.target.value.toUpperCase()})}
                        />
                        <p className="text-xs text-gray-500">
                          Si este plan tiene un código promocional exclusivo, ingrésalo aquí
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={planData.description}
                        onChange={(e) => setPlanData({...planData, description: e.target.value})}
                        rows={5}
                        placeholder="Describe el plan (los saltos de línea y párrafos se respetan al mostrarlo)"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Características (una por línea)</Label>
                      <Textarea
                        rows={6}
                        placeholder="Característica 1&#10;Característica 2"
                        value={planData.features}
                        onChange={(e) => setPlanData({...planData, features: e.target.value})}
                        required
                      />
                    </div>
                    <Button type="submit" style={{ backgroundColor: '#FF69B4' }}>
                      {editingPlan ? '💾 Guardar' : '➕ Crear Plan'}
                    </Button>
                    {editingPlan && (
                      <Button type="button" variant="outline" onClick={handleCancelEditPlan} className="ml-2">
                        Cancelar
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>💎 Planes ({plansList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPlans ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : plansList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No hay planes aún</div>
                  ) : (
                    <div className="space-y-4">
                      {plansList.map((plan) => (
                        <div key={plan.id} className="border rounded-lg p-4">
                          <div className="flex justify-between">
                            <div>
                              <h4 className="font-bold text-xl">{plan.name}</h4>
                              <p className="text-gray-600 whitespace-pre-line" data-testid={`admin-plan-desc-${plan.id}`}>{plan.description}</p>
                              <span className="text-green-600 font-bold text-lg">${plan.price}</span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditPlan(plan)}>
                                ✏️ Editar
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeletePlan(plan.id)}>
                                🗑️ Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === 'codigos' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{editingPromo ? '✏️ Editar Código' : '➕ Crear Código Promocional'}</CardTitle>
                  <CardDescription>Los códigos se pueden aplicar a cualquier tipo de contenido (Videos, Ebooks, Recetarios, Planes, Tienda)</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitPromoCode} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Código</Label>
                        <Input
                          value={promoData.code}
                          onChange={(e) => setPromoData({...promoData, code: e.target.value.toUpperCase()})}
                          placeholder="Ej: DESCUENTO20"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Aplica a</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={promoData.applies_to || 'all'}
                          onChange={(e) => setPromoData({...promoData, applies_to: e.target.value})}
                        >
                          <option value="all">🌟 Todo (Videos, Ebooks, Recetarios, Tienda)</option>
                          <option value="videos">📹 Solo Videos</option>
                          <option value="ebooks">📚 Solo Ebooks</option>
                          <option value="recetarios">🍳 Solo Recetarios</option>
                          <option value="tienda">🛒 Solo Tienda</option>
                          <option value="planes">📋 Solo Planes de Suscripción</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Descuento</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={promoData.discount_type}
                          onChange={(e) => setPromoData({...promoData, discount_type: e.target.value})}
                        >
                          <option value="percentage">Porcentaje (%)</option>
                          <option value="fixed">Monto Fijo por Moneda ($)</option>
                        </select>
                      </div>
                      {promoData.discount_type === 'percentage' ? (
                        <div className="space-y-2">
                          <Label>Porcentaje de Descuento</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={promoData.discount_value}
                            onChange={(e) => setPromoData({...promoData, discount_value: e.target.value})}
                            placeholder="Ej: 20"
                            required
                          />
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>Descuento en COP</Label>
                            <Input
                              type="number"
                              step="1"
                              value={promoData.fixed_value_cop}
                              onChange={(e) => setPromoData({...promoData, fixed_value_cop: e.target.value})}
                              placeholder="Ej: 30000"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Descuento en USD</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={promoData.fixed_value_usd}
                              onChange={(e) => setPromoData({...promoData, fixed_value_usd: e.target.value})}
                              placeholder="Ej: 10"
                              required
                            />
                          </div>
                        </>
                      )}
                      <div className="space-y-2">
                        <Label>Usos Máximos</Label>
                        <Input
                          type="number"
                          value={promoData.max_uses}
                          onChange={(e) => setPromoData({...promoData, max_uses: e.target.value})}
                          placeholder="100"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha de Expiración</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white hover:bg-white/20"
                            >
                              <FaCalendarAlt className="mr-2 h-4 w-4" />
                              {promoData.expires_at ? (
                                format(new Date(promoData.expires_at), "PPP 'a las' HH:mm", { locale: es })
                              ) : (
                                <span className="text-gray-400">Seleccionar fecha...</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-gray-900 border-gray-700" align="start">
                            <Calendar
                              mode="single"
                              selected={promoData.expires_at ? new Date(promoData.expires_at) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  // Mantener la hora si ya existe, sino usar 23:59
                                  const existingDate = promoData.expires_at ? new Date(promoData.expires_at) : null;
                                  const hours = existingDate ? existingDate.getHours() : 23;
                                  const minutes = existingDate ? existingDate.getMinutes() : 59;
                                  date.setHours(hours, minutes, 0, 0);
                                  setPromoData({...promoData, expires_at: date.toISOString()});
                                }
                              }}
                              disabled={(date) => date < new Date()}
                              initialFocus
                              locale={es}
                              className="bg-gray-900 text-white"
                            />
                            <div className="p-3 border-t border-gray-700">
                              <Label className="text-gray-300 text-sm">Hora de expiración</Label>
                              <Input
                                type="time"
                                value={promoData.expires_at ? format(new Date(promoData.expires_at), 'HH:mm') : '23:59'}
                                onChange={(e) => {
                                  if (promoData.expires_at) {
                                    const date = new Date(promoData.expires_at);
                                    const [hours, minutes] = e.target.value.split(':');
                                    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                    setPromoData({...promoData, expires_at: date.toISOString()});
                                  }
                                }}
                                className="mt-1 bg-gray-800 border-gray-600 text-white"
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <Button type="submit" style={{ backgroundColor: '#FF69B4' }}>
                      {editingPromo ? '💾 Guardar' : '➕ Crear Código'}
                    </Button>
                    {editingPromo && (
                      <Button type="button" variant="outline" onClick={handleCancelEditPromo} className="ml-2">
                        Cancelar
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>🎫 Códigos Promocionales ({promosList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPromos ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : promosList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No hay códigos aún</div>
                  ) : (
                    <div className="space-y-4">
                      {promosList.map((promo) => (
                        <div key={promo.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-2xl text-purple-600">{promo.code}</h4>
                              <p className="text-sm">
                                Descuento: <span className="font-semibold">{promo.discount_type === 'percentage' ? `${promo.discount_value}%` : (promo.fixed_values ? `COP $${(promo.fixed_values.COP || 0).toLocaleString('es-CO')} / USD $${promo.fixed_values.USD || 0}` : `$${promo.discount_value}`)}</span>
                              </p>
                              <p className="text-sm">Usos: {promo.current_uses} / {promo.max_uses}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button size="sm" onClick={() => togglePromoStatus(promo)}>
                                {promo.is_active ? '⏸️ Desactivar' : '▶️ Activar'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleEditPromo(promo)}>
                                ✏️ Editar
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeletePromo(promo.id)}>
                                🗑️ Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN GALERÍA DE MEDIOS */}
          {activeSection === 'galeria' && (
            <div className="space-y-6">
              <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CardHeader>
                  <CardTitle className="text-white">🖼️ Subir Imágenes</CardTitle>
                  <CardDescription className="text-gray-400">Sube imágenes y asígnalas a diferentes partes de tu sitio web</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUploadMedia} className="space-y-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">📂 ¿Dónde se usará?</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md bg-gray-800 border-gray-700 text-white"
                          value={mediaCategory}
                          onChange={(e) => setMediaCategory(e.target.value)}
                        >
                          <option value="general">🖼️ General</option>
                          <option value="hero_landing">🌟 Hero Landing (Principal)</option>
                          <option value="about_section">👤 Sección Acerca de</option>
                          <option value="testimonials">💬 Testimonios</option>
                          <option value="video_thumbnail">📹 Miniatura de Video</option>
                          <option value="program_cover">💪 Portada de Programa</option>
                          <option value="ebook_cover">📚 Portada de Ebook</option>
                          <option value="recipe_cover">🍽️ Portada de Receta</option>
                          <option value="blog_post">📝 Post de Blog</option>
                          <option value="background">🎨 Fondo de Sección</option>
                          <option value="product_image">🛒 Imagen de Producto</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-300">📏 Tamaño recomendado</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md bg-gray-800 border-gray-700 text-white"
                          value={mediaSize}
                          onChange={(e) => setMediaSize(e.target.value)}
                        >
                          <option value="small">📱 Pequeño (300x300px)</option>
                          <option value="medium">💻 Mediano (800x600px)</option>
                          <option value="large">🖥️ Grande (1200x800px)</option>
                          <option value="hero">🌟 Hero/Banner (1920x1080px)</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-300">📍 Posición/Alineación</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md bg-gray-800 border-gray-700 text-white"
                          value={mediaPosition}
                          onChange={(e) => setMediaPosition(e.target.value)}
                        >
                          <option value="center">⬛ Centro</option>
                          <option value="top">⬆️ Arriba</option>
                          <option value="bottom">⬇️ Abajo</option>
                          <option value="left">⬅️ Izquierda</option>
                          <option value="right">➡️ Derecha</option>
                          <option value="cover">📐 Cubrir (Cover)</option>
                          <option value="contain">📦 Contener (Contain)</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-300">🏷️ Uso específico</Label>
                        <Input
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Ej: Ebook Recetas Saludables"
                          value={mediaUsedIn || ''}
                          onChange={(e) => setMediaUsedIn(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">📝 Descripción (opcional)</Label>
                      <Input
                        className="bg-gray-800 border-gray-700 text-white"
                        placeholder="Describe brevemente esta imagen..."
                        value={mediaDescription || ''}
                        onChange={(e) => setMediaDescription(e.target.value)}
                      />
                    </div>

                    <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <p className="text-sm text-blue-400">
                        <strong>💡 Guía rápida:</strong>
                      </p>
                      <ul className="text-xs text-blue-300 mt-2 space-y-1">
                        <li>• <strong>Hero Landing:</strong> Imagen principal de la página de inicio (1920x1080px)</li>
                        <li>• <strong>Miniatura Video:</strong> Preview para videos (800x600px)</li>
                        <li>• <strong>Portada Programa/Ebook:</strong> Imagen del contenido (1200x800px)</li>
                        <li>• <strong>Uso específico:</strong> Especifica exactamente dónde se usará (ej: &quot;Ebook: Postres Fit&quot;)</li>
                      </ul>
                    </div>

                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-pink-500 transition-colors bg-gray-800/50">
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setMediaFiles(e.target.files)}
                        className="hidden"
                        id="media-upload"
                      />
                      <label htmlFor="media-upload" className="cursor-pointer">
                        <div className="text-6xl mb-4">📸</div>
                        <p className="text-lg font-semibold mb-2 text-white">Click para seleccionar imágenes</p>
                        <p className="text-sm text-gray-400">Puedes seleccionar múltiples imágenes a la vez</p>
                        {mediaFiles && mediaFiles.length > 0 && (
                          <p className="mt-4 text-pink-400 font-bold">
                            ✅ {mediaFiles.length} imagen(es) seleccionada(s)
                          </p>
                        )}
                      </label>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full py-6 text-lg bg-pink-600 hover:bg-pink-700"
                      disabled={uploadingMedia || !mediaFiles || mediaFiles.length === 0}
                    >
                      {uploadingMedia ? '⏳ Subiendo...' : '📤 Subir Imágenes'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Filtro por categoría */}
              <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CardHeader>
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <div>
                      <CardTitle className="text-white">🖼️ Galería ({mediaList.length})</CardTitle>
                      <CardDescription className="text-gray-400">Tus imágenes organizadas por uso</CardDescription>
                    </div>
                    <div className="flex gap-2 items-center">
                      <select
                        className="px-3 py-2 border rounded-md bg-gray-800 border-gray-700 text-white text-sm"
                        value={mediaFilterCategory || 'all'}
                        onChange={(e) => setMediaFilterCategory(e.target.value)}
                      >
                        <option value="all">📁 Todas las categorías</option>
                        <option value="hero_landing">🌟 Hero Landing</option>
                        <option value="video_thumbnail">📹 Miniaturas Video</option>
                        <option value="program_cover">💪 Portadas Programa</option>
                        <option value="ebook_cover">📚 Portadas Ebook</option>
                        <option value="recipe_cover">🍽️ Portadas Receta</option>
                        <option value="product_image">🛒 Productos</option>
                        <option value="general">🖼️ General</option>
                      </select>
                      <Button onClick={fetchMedia} variant="outline" size="sm" className="border-gray-600 text-gray-300">🔄</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingMedia ? (
                    <div className="text-center py-8 text-gray-400">Cargando...</div>
                  ) : mediaList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-6xl mb-4">📸</div>
                      <p className="text-lg mb-2 text-gray-300">No hay imágenes aún</p>
                      <p className="text-sm text-gray-500">Sube tu primera imagen usando el formulario arriba</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {mediaList
                        .filter(m => !mediaFilterCategory || mediaFilterCategory === 'all' || m.category === mediaFilterCategory)
                        .map((media) => (
                        <div key={media.id} className="relative group rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <img src={media.url} alt={media.filename} className="w-full h-48 object-cover" />
                          <div className="absolute top-2 left-2 flex flex-col gap-1">
                            <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded">
                              {media.category === 'hero_landing' ? '🌟 Hero' :
                               media.category === 'video_thumbnail' ? '📹 Video' :
                               media.category === 'program_cover' ? '💪 Programa' :
                               media.category === 'ebook_cover' ? '📚 Ebook' :
                               media.category === 'recipe_cover' ? '🍽️ Receta' :
                               media.category === 'product_image' ? '🛒 Producto' :
                               '🖼️ General'}
                            </span>
                            {media.used_in && (
                              <span className="px-2 py-1 bg-cyan-600 text-white text-xs rounded max-w-[150px] truncate" title={media.used_in}>
                                📍 {media.used_in}
                              </span>
                            )}
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
                            <p className="text-white text-sm text-center mb-2 font-semibold">{media.filename}</p>
                            {media.description && (
                              <p className="text-gray-300 text-xs text-center mb-2">{media.description}</p>
                            )}
                            <Button size="sm" onClick={() => copyToClipboard(media.url)} className="bg-pink-600 hover:bg-pink-700 w-full">
                              📋 Copiar URL
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              const newUsedIn = prompt('¿Dónde se usa esta imagen?', media.used_in || '');
                              if (newUsedIn !== null) {
                                axios.put(`${API}/admin/media/${media.id}`, { used_in: newUsedIn }, { withCredentials: false })
                                  .then(() => { toast.success('Actualizado'); fetchMedia(); })
                                  .catch(() => toast.error('Error al actualizar'));
                              }
                            }} className="w-full border-cyan-500 text-cyan-400">
                              📍 Editar uso
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteMedia(media.id)} className="w-full">
                              🗑️ Eliminar
                            </Button>
                          </div>
                          <div className="p-3">
                            <p className="text-xs truncate font-semibold text-white">{media.filename}</p>
                            <div className="flex flex-wrap gap-1 mt-2 text-xs">
                              <span className="px-2 py-0.5 bg-blue-900/50 text-blue-300 rounded border border-blue-700">
                                {media.size === 'hero' ? '🌟 Hero' :
                                 media.size === 'large' ? '🖥️ Grande' :
                                 media.size === 'medium' ? '💻 Mediano' : '📱 Pequeño'}
                              </span>
                              <span className="px-2 py-0.5 bg-green-900/50 text-green-300 rounded border border-green-700">
                                📍 {media.position}
                              </span>
                            </div>
                            {media.used_in && (
                              <p className="text-xs text-cyan-400 mt-2 truncate" title={media.used_in}>
                                ➜ {media.used_in}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN PROGRAMAS DE ENTRENAMIENTO */}
          {activeSection === 'programas' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{editingProgram ? '✏️ Editar Programa' : '💪 Crear Programa de Entrenamiento'}</CardTitle>
                  <CardDescription>Programa completo con sistema de acceso y precios avanzado</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitProgram} className="space-y-6">
                    {/* Información Básica */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                      <h4 className="font-semibold mb-4 flex items-center gap-2">📝 Información Básica</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre del Programa *</Label>
                          <Input
                            placeholder="Ej: Glúteos de Acero 8 Semanas"
                            value={programData.name}
                            onChange={(e) => setProgramData({...programData, name: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Categoría *</Label>
                          <select
                            className="w-full px-3 py-2 border rounded-md"
                            value={programData.category}
                            onChange={(e) => setProgramData({...programData, category: e.target.value})}
                          >
                            <option value="fuerza">💪 Fuerza</option>
                            <option value="cardio">🏃 Cardio</option>
                            <option value="flexibilidad">🧘 Flexibilidad</option>
                            <option value="gluteos">🍑 Glúteos</option>
                            <option value="abdomen">🎯 Abdomen</option>
                            <option value="fullbody">⚡ Full Body</option>
                            <option value="perdida_peso">🔥 Pérdida de Peso</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Duración (semanas) *</Label>
                          <Input
                            type="number"
                            placeholder="8"
                            value={programData.duration_weeks}
                            onChange={(e) => setProgramData({...programData, duration_weeks: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nivel *</Label>
                          <select
                            className="w-full px-3 py-2 border rounded-md"
                            value={programData.level}
                            onChange={(e) => setProgramData({...programData, level: e.target.value})}
                          >
                            <option value="principiante">🌱 Principiante</option>
                            <option value="intermedio">💪 Intermedio</option>
                            <option value="avanzado">🔥 Avanzado</option>
                          </select>
                        </div>
                      </div>
                      <div className="space-y-2 mt-4">
                        <Label>Descripción Corta (para tarjetas)</Label>
                        <Input
                          placeholder="Transforma tus glúteos en 8 semanas con este programa intensivo"
                          value={programData.short_description}
                          onChange={(e) => setProgramData({...programData, short_description: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2 mt-4">
                        <Label>Descripción Completa *</Label>
                        <Textarea
                          rows={4}
                          placeholder="Describe el programa en detalle: objetivos, metodología, resultados esperados..."
                          value={programData.description}
                          onChange={(e) => setProgramData({...programData, description: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    {/* Precios y Acceso */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)' }}>
                      <h4 className="font-semibold mb-4 flex items-center gap-2">💰 Precios y Sistema de Acceso</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Precio USD *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="99.99"
                            value={programData.price}
                            onChange={(e) => setProgramData({...programData, price: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Precio Original (tachado)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="149.99"
                            value={programData.original_price}
                            onChange={(e) => setProgramData({...programData, original_price: e.target.value})}
                          />
                          <p className="text-xs text-gray-500">Para mostrar descuento</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Código Descuento</Label>
                          <Input
                            placeholder="GLUTEOS20"
                            value={programData.discount_code}
                            onChange={(e) => setProgramData({...programData, discount_code: e.target.value.toUpperCase()})}
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>🔓 Tipo de Acceso</Label>
                          <select
                            className="w-full px-3 py-2 border rounded-md"
                            value={programData.access_type}
                            onChange={(e) => setProgramData({...programData, access_type: e.target.value})}
                          >
                            <option value="free">🆓 Gratuito para todos</option>
                            <option value="basic">⭐ Fit Evolution</option>
                            <option value="premium">💎 Plan Premium</option>
                            <option value="gold">👑 Plan Gold</option>
                            <option value="paid">💳 De Pago (compra individual)</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>⏰ Tipo de Compra</Label>
                          <select
                            className="w-full px-3 py-2 border rounded-md"
                            value={programData.purchase_type}
                            onChange={(e) => setProgramData({...programData, purchase_type: e.target.value})}
                          >
                            <option value="one_time">♾️ Compra Única (acceso permanente)</option>
                            <option value="time_limited">⏳ Tiempo Limitado</option>
                          </select>
                        </div>
                      </div>

                      {programData.purchase_type === 'time_limited' && (
                        <div className="space-y-2 mt-4">
                          <Label>Días de Acceso</Label>
                          <Input
                            type="number"
                            placeholder="30"
                            value={programData.access_days}
                            onChange={(e) => setProgramData({...programData, access_days: e.target.value})}
                          />
                        </div>
                      )}

                      <div className="mt-4 space-y-2">
                        <Label>🎁 Gratis para estos planes (múltiple selección)</Label>
                        <div className="flex flex-wrap gap-3">
                          {['basic', 'premium', 'gold'].map((plan) => (
                            <label key={plan} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={programData.free_for.includes(plan)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setProgramData({...programData, free_for: [...programData.free_for, plan]});
                                  } else {
                                    setProgramData({...programData, free_for: programData.free_for.filter(p => p !== plan)});
                                  }
                                }}
                              />
                                  <span className="px-2 py-1 rounded border" style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)' }}>
                                {plan === 'basic' ? '⭐' : plan === 'premium' ? '💎' : '👑'} {plan.charAt(0).toUpperCase() + plan.slice(1)}
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">Estos planes tendrán acceso gratuito aunque el programa sea de pago</p>
                      </div>

                      <div className="space-y-2 mt-4">
                        <Label>⬆️ Plan Requerido para Upgrade</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={programData.upgrade_required}
                          onChange={(e) => setProgramData({...programData, upgrade_required: e.target.value})}
                        >
                          <option value="">Sin upgrade requerido</option>
                          <option value="basic">Upgrade a Basic</option>
                          <option value="premium">Upgrade a Premium</option>
                          <option value="gold">Upgrade a Gold</option>
                        </select>
                        <p className="text-xs text-gray-500">Si el usuario no tiene acceso, se le sugerirá hacer upgrade a este plan</p>
                      </div>
                    </div>

                    {/* Contenido del Programa */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}>
                      <h4 className="font-semibold mb-4 flex items-center gap-2">📋 Contenido del Programa</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>✅ Características (una por línea)</Label>
                          <Textarea
                            rows={4}
                            placeholder="Rutinas completas día a día&#10;Videos HD explicativos&#10;Calentamiento incluido&#10;Estiramientos post-entrenamiento"
                            value={programData.features}
                            onChange={(e) => setProgramData({...programData, features: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>🎯 Beneficios (una por línea)</Label>
                          <Textarea
                            rows={4}
                            placeholder="Glúteos más firmes y tonificados&#10;Mayor fuerza en piernas&#10;Mejora de postura&#10;Aumento de confianza"
                            value={programData.benefits}
                            onChange={(e) => setProgramData({...programData, benefits: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>📦 ¿Qué Incluye? (una por línea)</Label>
                          <Textarea
                            rows={4}
                            placeholder="24 rutinas de entrenamiento&#10;Plan nutricional personalizado&#10;Grupo privado de soporte&#10;Acceso a app móvil"
                            value={programData.what_includes}
                            onChange={(e) => setProgramData({...programData, what_includes: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>⚠️ Requisitos Previos (una por línea)</Label>
                          <Textarea
                            rows={3}
                            placeholder="No se necesita experiencia previa&#10;Mancuernas de 2-5kg (opcional)&#10;Banda elástica (opcional)"
                            value={programData.requirements}
                            onChange={(e) => setProgramData({...programData, requirements: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Multimedia */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                      <h4 className="font-semibold mb-4 flex items-center gap-2">🖼️ Multimedia</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Imagen de Portada</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setProgramImageFile(e.target.files[0])}
                          />
                          {programImageFile && (
                            <p className="text-xs text-green-600">✅ {programImageFile.name}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>URL Video Preview (opcional)</Label>
                          <Input
                            placeholder="https://youtube.com/watch?v=..."
                            value={programData.preview_video_url}
                            onChange={(e) => setProgramData({...programData, preview_video_url: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Opciones */}
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={programData.is_featured}
                          onChange={(e) => setProgramData({...programData, is_featured: e.target.checked})}
                        />
                        <span>⭐ Programa Destacado</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={programData.is_active}
                          onChange={(e) => setProgramData({...programData, is_active: e.target.checked})}
                        />
                        <span>✅ Programa Activo</span>
                      </label>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        type="submit" 
                        style={{ backgroundColor: '#FF69B4' }}
                        disabled={uploadingProgram}
                        className="px-8"
                      >
                        {uploadingProgram ? '⏳ Guardando...' : editingProgram ? '💾 Actualizar Programa' : '➕ Crear Programa'}
                      </Button>
                      {editingProgram && (
                        <Button type="button" variant="outline" onClick={() => {
                          setEditingProgram(null);
                          setProgramData({
                            name: '', description: '', short_description: '', price: '', original_price: '',
                            duration_weeks: '', level: 'intermedio', category: 'fuerza', features: '',
                            benefits: '', what_includes: '', requirements: '', discount_code: '',
                            access_type: 'paid', purchase_type: 'one_time', access_days: '',
                            free_for: [], upgrade_required: '', is_featured: false, is_active: true, preview_video_url: ''
                          });
                        }}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Lista de Programas */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>💪 Programas Creados ({programsList.length})</CardTitle>
                      <CardDescription>Gestiona tus programas de entrenamiento</CardDescription>
                    </div>
                    <Button onClick={fetchPrograms} variant="outline" size="sm">🔄 Actualizar</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingPrograms ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : programsList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-4xl mb-4">💪</p>
                      <p>No hay programas aún</p>
                      <p className="text-sm">Crea tu primer programa de entrenamiento</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {programsList.map((program) => (
                        <div key={program.id} className="border rounded-lg overflow-hidden hover:shadow-xl transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="relative">
                            {program.image_url ? (
                              <img src={program.image_url} alt={program.name} className="w-full h-48 object-cover" />
                            ) : (
                              <div className="w-full h-48 bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                                <span className="text-6xl">💪</span>
                              </div>
                            )}
                            {program.is_featured && (
                              <span className="absolute top-2 left-2 px-2 py-1 bg-yellow-400 text-black text-xs font-bold rounded">
                                ⭐ DESTACADO
                              </span>
                            )}
                            <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold ${
                              program.is_active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                            }`}>
                              {program.is_active ? '✅ Activo' : '⏸️ Inactivo'}
                            </span>
                          </div>
                          <div className="p-4">
                            <h3 className="text-lg font-bold mb-1">{program.name}</h3>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                              {program.short_description || program.description}
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3 text-xs">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-bold">
                                💰 ${program.price} USD
                              </span>
                              {program.original_price && (
                                <span className="px-2 py-1 bg-red-100 text-red-500 rounded line-through">
                                  ${program.original_price}
                                </span>
                              )}
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                📅 {program.duration_weeks} sem
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {program.level === 'principiante' ? '🌱' : program.level === 'intermedio' ? '💪' : '🔥'} {program.level}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-3 text-xs">
                              <span className={`px-2 py-1 rounded ${
                                program.access_type === 'free' ? 'bg-green-200 text-green-800' :
                                program.access_type === 'paid' ? 'bg-yellow-200 text-yellow-800' :
                                'bg-blue-200 text-blue-800'
                              }`}>
                                🔓 {program.access_type === 'free' ? 'Gratuito' : 
                                    program.access_type === 'paid' ? 'De Pago' : 
                                    `Plan ${program.access_type}`}
                              </span>
                              {program.free_for?.length > 0 && (
                                <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded">
                                  🎁 Gratis: {program.free_for.join(', ')}
                                </span>
                              )}
                            </div>
                            {program.discount_code && (
                              <p className="text-xs text-green-400 mb-3 font-mono px-2 py-1 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                                🎟️ {program.discount_code}
                              </p>
                            )}
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEditProgram(program)}>
                                ✏️ Editar
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteProgram(program.id)}>
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN CUENTAS DE PRUEBA */}
          {activeSection === 'cuentas-prueba' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>🧪 Crear Cuenta de Prueba</CardTitle>
                  <CardDescription>
                    Crea cuentas de prueba para testear diferentes niveles de suscripción
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateTestAccount} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          placeholder="test@ejemplo.com"
                          value={testAccountData.email}
                          onChange={(e) => setTestAccountData({...testAccountData, email: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          placeholder="Usuario de Prueba"
                          value={testAccountData.name}
                          onChange={(e) => setTestAccountData({...testAccountData, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña</Label>
                        <Input
                          placeholder="test123"
                          value={testAccountData.password}
                          onChange={(e) => setTestAccountData({...testAccountData, password: e.target.value})}
                        />
                        <p className="text-xs text-gray-500">Por defecto: test123</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Plan de Suscripción / Rol</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={testAccountData.subscription_plan}
                          onChange={(e) => setTestAccountData({...testAccountData, subscription_plan: e.target.value})}
                        >
                          <option value="admin">🔐 ADMINISTRADOR</option>
                          <option value="basic">⭐ Básico</option>
                          <option value="premium">💎 Premium</option>
                          <option value="gold">👑 Gold</option>
                          <option value="vip">💫 VIP</option>
                          <option value="competitivo">🏆 Competitivo</option>
                          <option value="">Sin Plan (Gratis)</option>
                        </select>
                        <p className="text-xs text-gray-500">
                          {testAccountData.subscription_plan === 'admin' 
                            ? '⚠️ Esta cuenta tendrá acceso completo al panel de administración'
                            : 'Define el nivel de acceso del usuario'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Género *</Label>
                        <select
                          className="w-full px-3 py-2 border rounded-md"
                          value={testAccountData.gender}
                          onChange={(e) => setTestAccountData({...testAccountData, gender: e.target.value})}
                        >
                          <option value="female">🌸 Guerrera (Mujer)</option>
                          <option value="male">💪 Guerrero (Hombre)</option>
                        </select>
                        <p className="text-xs text-gray-500">Define el tema de colores y textos personalizados</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={testAccountData.is_protected}
                            onChange={(e) => setTestAccountData({...testAccountData, is_protected: e.target.checked})}
                            className="w-4 h-4 rounded"
                          />
                          <span>🔒 Cuenta Protegida (no se puede eliminar)</span>
                        </Label>
                        <p className="text-xs text-gray-500">Marca esta opción para cuentas importantes que no deben borrarse</p>
                      </div>
                    </div>

                    <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <p className="text-sm text-blue-800">
                        <strong>💡 Tip:</strong> Usa emails únicos para cada cuenta de prueba. 
                        Puedes usar variantes como: test1@gmail.com, test2@gmail.com, etc.
                      </p>
                    </div>

                    <Button type="submit" style={{ backgroundColor: '#FF69B4' }} className="w-full">
                      ➕ Crear Cuenta de Prueba
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Vista Rápida de Cuentas Existentes */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>📋 Cuentas de Prueba ({usersList.filter(u => !u.is_vip_client && u.role !== 'admin').length})</CardTitle>
                      <CardDescription>Gestiona y elimina cuentas de prueba (no incluye Clientes VIP reales)</CardDescription>
                    </div>
                    <Button onClick={fetchUsers} variant="outline" size="sm">
                      🔄 Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingUsers ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : usersList.filter(u => !u.is_vip_client && u.role !== 'admin').length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No hay cuentas de prueba</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[700px]">
                        <thead style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                          <tr>
                            <th className="px-3 py-3 text-left text-sm font-semibold">Email</th>
                            <th className="px-3 py-3 text-left text-sm font-semibold">Nombre</th>
                            <th className="px-3 py-3 text-left text-sm font-semibold">Plan</th>
                            <th className="px-3 py-3 text-left text-sm font-semibold">Contraseña</th>
                            <th className="px-3 py-3 text-left text-sm font-semibold">Rol</th>
                            <th className="px-3 py-3 text-center text-sm font-semibold whitespace-nowrap">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.filter(u => !u.is_vip_client && u.role !== 'admin').map((user) => (
                            <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="px-3 py-3 text-sm break-all max-w-[180px]">{user.email}</td>
                              <td className="px-3 py-3 text-sm">{user.name || 'Sin nombre'}</td>
                              <td className="px-3 py-3 text-sm">
                                <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                                  user.subscription_plan === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                                  user.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                                  user.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.subscription_plan || 'Sin plan'}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm">
                                <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                                  {user.password || '***'}
                                </code>
                              </td>
                              <td className="px-3 py-3 text-sm">
                                {user.role === 'admin' ? (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold whitespace-nowrap">
                                    👑 ADMIN
                                  </span>
                                ) : (
                                  <span className="text-gray-500">Usuario</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm text-center">
                                {user.role !== 'admin' ? (
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => handleDeleteUser(user.id, user.email)}
                                    className="whitespace-nowrap"
                                  >
                                    🗑️ Eliminar
                                  </Button>
                                ) : (
                                  <span className="text-xs text-gray-400">Protegido</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN PLANES DE SUSCRIPCIÓN */}
          {activeSection === 'planes-suscripcion' && (
            <div className="space-y-6">
              {/* Formulario de Plan */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                  <CardTitle className="text-white">{editingSubscriptionPlan ? '✏️ Editar Plan' : '💎 Crear Nuevo Plan'}</CardTitle>
                  <CardDescription className="text-gray-400">
                    {editingSubscriptionPlan ? 'Modifica los detalles del plan' : 'Define un nuevo plan de suscripción'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmitSubscriptionPlan} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nombre */}
                      <div className="space-y-2">
                        <Label className="text-gray-300">Nombre del Plan *</Label>
                        <Input
                          value={subscriptionPlanData.name}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, name: e.target.value})}
                          placeholder="Ej: Plan Premium"
                          required
                        />
                      </div>
                      
                      {/* Slug */}
                      <div className="space-y-2">
                        <Label className="text-gray-300">Slug (identificador interno)</Label>
                        <Input
                          value={subscriptionPlanData.slug}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, slug: e.target.value.toLowerCase()})}
                          placeholder="Ej: premium"
                        />
                      </div>
                    </div>

                    {/* Sección de Precios en USD */}
                    <div className="space-y-4 p-4 rounded-xl border border-white/10" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                      <h4 className="font-semibold text-gray-200 flex items-center gap-2">
                        💵 Precios en Dólares (USD)
                        <span className="text-xs font-normal text-gray-400">(Deja en 0 los que no aplican)</span>
                      </h4>
                      <div className="grid md:grid-cols-5 gap-3">
                        {/* Precio Mensual USD */}
                        <div className="space-y-1">
                          <Label className="text-sm text-gray-300">Mensual USD</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_monthly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_monthly: e.target.value})}
                            placeholder="25"
                            className="text-center"
                          />
                        </div>

                        {/* Precio Bimestral USD */}
                        <div className="space-y-1">
                          <Label className="text-sm">Bimestral USD</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_bimonthly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_bimonthly: e.target.value})}
                            placeholder="45"
                            className="text-center"
                          />
                        </div>

                        {/* Precio Trimestral USD */}
                        <div className="space-y-1">
                          <Label className="text-sm">Trimestral USD</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_quarterly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_quarterly: e.target.value})}
                            placeholder="60"
                            className="text-center"
                          />
                        </div>

                        {/* Precio Semestral USD */}
                        <div className="space-y-1">
                          <Label className="text-sm">Semestral USD</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_semiannual}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_semiannual: e.target.value})}
                            placeholder="100"
                            className="text-center"
                          />
                        </div>

                        {/* Precio Anual USD */}
                        <div className="space-y-1">
                          <Label className="text-sm">Anual USD</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_yearly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_yearly: e.target.value})}
                            placeholder="180"
                            className="text-center"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sección de Precios Gancho USD (Opcional - Precio tachado) */}
                    <div className="space-y-4 p-4 rounded-xl border border-white/10" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                      <h4 className="font-semibold text-gray-200 flex items-center gap-2">
                        🏷️ Precios Gancho USD (Tachados)
                        <span className="text-xs font-normal text-gray-400">(Opcional - Aparecen tachados como oferta)</span>
                      </h4>
                      <div className="grid md:grid-cols-5 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm text-red-400">~Mensual~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_monthly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_monthly: e.target.value})}
                            placeholder="Ej: 35"
                            className="text-center line-through"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-red-400">~Bimestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_bimonthly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_bimonthly: e.target.value})}
                            placeholder="Ej: 65"
                            className="text-center line-through"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-red-400">~Trimestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_quarterly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_quarterly: e.target.value})}
                            placeholder="Ej: 90"
                            className="text-center border-red-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-red-600">~Semestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_semiannual}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_semiannual: e.target.value})}
                            placeholder="Ej: 150"
                            className="text-center border-red-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-red-600">~Anual~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_yearly}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_yearly: e.target.value})}
                            placeholder="Ej: 250"
                            className="text-center border-red-200 line-through text-gray-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sección de Precios en COP */}
                    <div className="space-y-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-100">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                        🇨🇴 Precios en Pesos Colombianos (COP)
                        <span className="text-xs font-normal text-gray-500">(Para mostrar a clientes colombianos)</span>
                      </h4>
                      <div className="grid md:grid-cols-5 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm">Mensual COP</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_monthly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_monthly_cop: e.target.value})}
                            placeholder="400000"
                            className="text-center"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Bimestral COP</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_bimonthly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_bimonthly_cop: e.target.value})}
                            placeholder="720000"
                            className="text-center"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Trimestral COP</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_quarterly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_quarterly_cop: e.target.value})}
                            placeholder="1000000"
                            className="text-center"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Semestral COP</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_semiannual_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_semiannual_cop: e.target.value})}
                            placeholder="1800000"
                            className="text-center"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">Anual COP</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.price_yearly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, price_yearly_cop: e.target.value})}
                            placeholder="3200000"
                            className="text-center"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sección de Precios Gancho COP (Opcional - Precio tachado) */}
                    <div className="space-y-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                        🇨🇴🏷️ Precios Gancho COP (Tachados)
                        <span className="text-xs font-normal text-gray-500">(Opcional - Aparecen tachados como oferta)</span>
                      </h4>
                      <div className="grid md:grid-cols-5 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm text-orange-600">~Mensual~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_monthly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_monthly_cop: e.target.value})}
                            placeholder="Ej: 500000"
                            className="text-center border-orange-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-orange-600">~Bimestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_bimonthly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_bimonthly_cop: e.target.value})}
                            placeholder="Ej: 900000"
                            className="text-center border-orange-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-orange-600">~Trimestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_quarterly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_quarterly_cop: e.target.value})}
                            placeholder="Ej: 1300000"
                            className="text-center border-orange-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-orange-600">~Semestral~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_semiannual_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_semiannual_cop: e.target.value})}
                            placeholder="Ej: 2300000"
                            className="text-center border-orange-200 line-through text-gray-500"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-orange-600">~Anual~</Label>
                          <Input
                            type="number"
                            value={subscriptionPlanData.original_price_yearly_cop}
                            onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, original_price_yearly_cop: e.target.value})}
                            placeholder="Ej: 4000000"
                            className="text-center border-orange-200 line-through text-gray-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Icono */}
                      <div className="space-y-2">
                        <Label>Icono/Emoji</Label>
                        <Input
                          value={subscriptionPlanData.icon}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, icon: e.target.value})}
                          placeholder="💎"
                        />
                      </div>

                      {/* Badge */}
                      <div className="space-y-2">
                        <Label>Badge (opcional)</Label>
                        <Input
                          value={subscriptionPlanData.badge}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, badge: e.target.value})}
                          placeholder="Ej: MÁS POPULAR, ELITE"
                        />
                      </div>

                      {/* Orden */}
                      <div className="space-y-2">
                        <Label>Orden de visualización</Label>
                        <Input
                          type="number"
                          value={subscriptionPlanData.order}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, order: e.target.value})}
                          placeholder="1"
                        />
                      </div>

                      {/* Popular */}
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          type="checkbox"
                          id="popular"
                          checked={subscriptionPlanData.popular}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, popular: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <Label htmlFor="popular">⭐ Marcar como popular</Label>
                      </div>
                    </div>

                    {/* Colores */}
                    <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                      <h4 className="font-semibold text-gray-700">🎨 Colores del Plan</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Color Principal</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={subscriptionPlanData.color}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, color: e.target.value})}
                              className="w-12 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={subscriptionPlanData.color}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, color: e.target.value})}
                              placeholder="#FF69B4"
                              className="flex-1"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Gradiente Desde</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={subscriptionPlanData.gradient_from}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, gradient_from: e.target.value})}
                              className="w-12 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={subscriptionPlanData.gradient_from}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, gradient_from: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Gradiente Hasta</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={subscriptionPlanData.gradient_to}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, gradient_to: e.target.value})}
                              className="w-12 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={subscriptionPlanData.gradient_to}
                              onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, gradient_to: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Preview del color */}
                      <div 
                        className="p-4 rounded-lg text-white text-center font-bold"
                        style={{ background: `linear-gradient(to bottom right, ${subscriptionPlanData.gradient_from}, ${subscriptionPlanData.gradient_to})` }}
                      >
                        Vista previa: {subscriptionPlanData.name || 'Nombre del Plan'}
                      </div>
                    </div>

                    {/* Imagen de Portada del Plan (para la página de planes) */}
                    <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg space-y-4 border border-pink-200">
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        📸 Imagen de Portada del Plan
                        <span className="text-xs font-normal text-gray-500">(Se mostrará arriba de la tarjeta en la página de planes)</span>
                      </h4>
                      
                      <div className="flex gap-3 items-center">
                        <Input
                          value={subscriptionPlanData.cover_image || ''}
                          onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, cover_image: e.target.value})}
                          placeholder="URL de la imagen de portada..."
                          className="flex-1"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          id="plan-cover-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('plan_slug', subscriptionPlanData.slug || 'custom');
                            try {
                              const response = await axios.post(`${API}/admin/upload-plan-background`, formData, {
                                withCredentials: false,
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSubscriptionPlanData({...subscriptionPlanData, cover_image: response.data.url});
                              toast.success('Imagen de portada subida');
                            } catch (error) {
                              toast.error('Error al subir la imagen');
                            }
                          }}
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => document.getElementById('plan-cover-upload').click()}
                        >
                          📤 Subir
                        </Button>
                      </div>
                      
                      {subscriptionPlanData.cover_image && (
                        <div className="flex items-center gap-3">
                          <img 
                            src={subscriptionPlanData.cover_image}
                            alt="Portada"
                            className="w-32 h-20 object-cover rounded"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500"
                            onClick={() => setSubscriptionPlanData({...subscriptionPlanData, cover_image: ''})}
                          >
                            Quitar
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Imagen de Fondo para PDFs */}
                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg space-y-4 border border-indigo-200">
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        🖼️ Imagen de Fondo para PDFs
                        <span className="text-xs font-normal text-gray-500">(Se usará en los PDFs de dietas generados)</span>
                      </h4>
                      
                      {/* Imágenes existentes - URLs de Cloudinary */}
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { type: 'basic', url: 'https://res.cloudinary.com/dsxsi1e5o/image/upload/v1771159571/deisycfit/plan_backgrounds/default_basic_3ae9a0b3.png' },
                          { type: 'premium', url: 'https://res.cloudinary.com/dsxsi1e5o/image/upload/v1771159572/deisycfit/plan_backgrounds/default_premium_d816c287.png' },
                          { type: 'gold', url: 'https://res.cloudinary.com/dsxsi1e5o/image/upload/v1771159573/deisycfit/plan_backgrounds/default_gold_428ea08b.png' },
                          { type: 'competitive', url: 'https://res.cloudinary.com/dsxsi1e5o/image/upload/v1771159574/deisycfit/plan_backgrounds/default_competitive_ad5ab386.png' }
                        ].map(({ type: planType, url }) => (
                          <div 
                            key={planType}
                            className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                              subscriptionPlanData.background_image === url 
                                ? 'border-pink-500 ring-2 ring-pink-300' 
                                : 'border-gray-200 hover:border-pink-300'
                            }`}
                            onClick={() => setSubscriptionPlanData({
                              ...subscriptionPlanData, 
                              background_image: url
                            })}
                          >
                            <img 
                              src={url}
                              alt={`Fondo ${planType}`}
                              className="w-full h-20 object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-1 capitalize">
                              {planType}
                            </div>
                            {subscriptionPlanData.background_image === url && (
                              <div className="absolute top-1 right-1 bg-pink-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">✓</div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Subir nueva imagen */}
                      <div className="border-t pt-4">
                        <Label className="text-sm text-gray-600 mb-2 block">O sube una nueva imagen de fondo:</Label>
                        <div className="flex gap-3 items-center">
                          <input
                            type="file"
                            accept="image/*"
                            id="plan-background-upload"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              
                              setUploadingPlanBackground(true);
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('plan_slug', subscriptionPlanData.slug || 'custom');
                              
                              try {
                                const response = await axios.post(`${API}/admin/upload-plan-background`, formData, {
                                  withCredentials: false,
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                setSubscriptionPlanData({
                                  ...subscriptionPlanData,
                                  background_image: response.data.url
                                });
                                toast.success('Imagen de fondo subida');
                              } catch (error) {
                                console.error('Error uploading background:', error);
                                toast.error('Error al subir la imagen');
                              } finally {
                                setUploadingPlanBackground(false);
                              }
                            }}
                          />
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => document.getElementById('plan-background-upload').click()}
                            disabled={uploadingPlanBackground}
                          >
                            {uploadingPlanBackground ? '⏳ Subiendo...' : '📤 Subir Imagen'}
                          </Button>
                          {subscriptionPlanData.background_image && (
                            <span className="text-sm text-green-600">✓ Imagen seleccionada</span>
                          )}
                        </div>
                      </div>

                      {/* Preview de imagen seleccionada */}
                      {subscriptionPlanData.background_image && (
                        <div className="border rounded-lg p-2" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <p className="text-xs text-gray-500 mb-2">Vista previa del fondo seleccionado:</p>
                          <img 
                            src={subscriptionPlanData.background_image.startsWith('/') 
                              ? `${API}${subscriptionPlanData.background_image}` 
                              : subscriptionPlanData.background_image}
                            alt="Vista previa"
                            className="w-full h-32 object-cover rounded"
                          />
                        </div>
                      )}
                    </div>

                    {/* Descripción */}
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={subscriptionPlanData.description}
                        onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, description: e.target.value})}
                        placeholder="Describe qué incluye este plan... (los saltos de línea se respetan)"
                        rows={5}
                        data-testid="admin-subscription-plan-description-input"
                      />
                      <p className="text-xs text-gray-500">Los saltos de línea y párrafos se muestran igual al cliente.</p>
                    </div>

                    {/* Características */}
                    <div className="space-y-2">
                      <Label>✅ Características incluidas</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          placeholder="Agregar característica..."
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeatureToSubscriptionPlan())}
                        />
                        <Button type="button" onClick={addFeatureToSubscriptionPlan} variant="outline">
                          + Agregar
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {subscriptionPlanData.features.map((feature, idx) => (
                          <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                            ✓ {feature}
                            <button 
                              type="button" 
                              onClick={() => removeFeatureFromSubscriptionPlan(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Activo */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={subscriptionPlanData.is_active}
                        onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, is_active: e.target.checked})}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="is_active">Plan activo (visible en la página)</Label>
                    </div>

                    {/* Disponible para Clientes VIP */}
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                      <input
                        type="checkbox"
                        id="available_for_vip"
                        checked={subscriptionPlanData.available_for_vip}
                        onChange={(e) => setSubscriptionPlanData({...subscriptionPlanData, available_for_vip: e.target.checked})}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="available_for_vip" className="text-yellow-300 font-medium">
                        👑 Disponible para Clientes VIP (aparecerá en la pestaña "Crear Cliente VIP")
                      </Label>
                    </div>

                    {/* Permisos de Acceso a Servicios */}
                    <div className="p-4 rounded-xl border border-white/10" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
                      <h4 className="font-semibold text-purple-300 flex items-center gap-2 mb-4">
                        🔐 Control de Acceso a Servicios
                        <span className="text-xs font-normal text-gray-400">(Define qué puede acceder este plan)</span>
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'videos', label: '🎬 Videos', desc: 'Acceso a videos de entrenamiento' },
                          { key: 'ebooks', label: '📚 Ebooks', desc: 'Acceso a ebooks y guías' },
                          { key: 'recetarios', label: '🍳 Recetarios', desc: 'Acceso a recetarios' },
                          { key: 'my_diet', label: '🥗 Mi Dieta', desc: 'Plan de alimentación personalizado' },
                          { key: 'my_workout', label: '💪 Mi Rutina', desc: 'Plan de entrenamiento personalizado' },
                          { key: 'data_progress', label: '📊 Data Progress', desc: 'Seguimiento de progreso' },
                          { key: 'gold_calendar', label: '📅 Calendario Gold', desc: 'Calendario exclusivo Gold' },
                          { key: 'recipe_ideas', label: '✨ Ideas Recetas', desc: 'Cocina Inteligente: generador de recetas' },
                          { key: 'appointments', label: '📆 Citas', desc: 'Agendar citas personales' }
                        ].map(({ key, label, desc }) => (
                          <div 
                            key={key}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${
                              subscriptionPlanData.permissions?.[key] 
                                ? 'bg-green-500/20 border border-green-500/50' 
                                : 'bg-gray-700/30 border border-gray-600/50'
                            }`}
                            onClick={() => setSubscriptionPlanData({
                              ...subscriptionPlanData,
                              permissions: {
                                ...subscriptionPlanData.permissions,
                                [key]: !subscriptionPlanData.permissions?.[key]
                              }
                            })}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={subscriptionPlanData.permissions?.[key] || false}
                                onChange={() => {}}
                                className="w-4 h-4"
                              />
                              <span className={`font-medium ${subscriptionPlanData.permissions?.[key] ? 'text-green-300' : 'text-gray-400'}`}>
                                {label}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 ml-6">{desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" style={{ backgroundColor: '#FF69B4' }} className="flex-1">
                        {editingSubscriptionPlan ? '💾 Guardar Cambios' : '➕ Crear Plan'}
                      </Button>
                      {editingSubscriptionPlan && (
                        <Button type="button" variant="outline" onClick={resetSubscriptionPlanForm}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Lista de Planes Existentes */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>💎 Planes de Suscripción ({subscriptionPlans.length})</CardTitle>
                      <CardDescription>Gestiona tus planes Basic, Premium, Gold y más</CardDescription>
                    </div>
                    <Button onClick={fetchSubscriptionPlans} variant="outline" size="sm">
                      🔄 Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingSubscriptionPlans ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto"></div>
                    </div>
                  ) : subscriptionPlans.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay planes creados aún</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {subscriptionPlans.map((plan) => (
                        <div
                          key={plan.id}
                          className="rounded-xl overflow-hidden shadow-lg border-2 hover:shadow-xl transition-shadow"
                          style={{ borderColor: plan.color }}
                        >
                          {/* Header del plan con gradiente */}
                          <div 
                            className="p-4 text-white"
                            style={{ background: `linear-gradient(to bottom right, ${plan.gradient_from || plan.color}, ${plan.gradient_to || plan.color})` }}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="text-3xl">{plan.icon}</span>
                                <h3 className="text-xl font-bold mt-1">{plan.name}</h3>
                                {plan.badge && (
                                  <span className="text-xs px-2 py-1 rounded-full mt-1 inline-block" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                                    {plan.badge}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold">
                                  ${(plan.price_monthly || 0).toLocaleString()}
                                </div>
                                <div className="text-xs opacity-80">/mes</div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Contenido */}
                          <div className="p-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                            <p className="text-sm text-gray-600 mb-3 whitespace-pre-line">{plan.description}</p>
                            
                            {/* Features */}
                            <ul className="space-y-1 mb-4">
                              {(plan.features || []).slice(0, 4).map((feature, idx) => (
                                <li key={idx} className="text-sm flex items-center gap-2">
                                  <span className="text-green-500">✓</span>
                                  {feature}
                                </li>
                              ))}
                              {(plan.features || []).length > 4 && (
                                <li className="text-xs text-gray-400">
                                  +{plan.features.length - 4} más...
                                </li>
                              )}
                            </ul>

                            {/* Estado */}
                            <div className="flex items-center justify-between mb-3">
                              <span className={`text-xs px-2 py-1 rounded ${plan.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {plan.is_active ? '✓ Activo' : '✗ Inactivo'}
                              </span>
                              <span className="text-xs text-gray-400">Orden: {plan.order}</span>
                            </div>

                            {/* Acciones */}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => handleEditSubscriptionPlan(plan)}
                              >
                                ✏️ Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteSubscriptionPlan(plan.id)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN BIBLIOTECA DE RECETAS */}
          {activeSection === 'recipe-library' && (
            <RecipeLibraryManager />
          )}

          {/* SECCIÓN MENSAJES DE LA COACH */}
          {activeSection === 'coach-messages' && (
            <CoachMessagesManager />
          )}

          {/* SECCIÓN EDITOR DE ESTILO DE RECETAS */}
          {activeSection === 'recipe-prompts' && (
            <RecipePromptTemplatesEditor />
          )}

          {/* SECCIÓN RECETARIOS EN VIDEO */}
          {activeSection === 'recipe-videos' && (
            <RecipeVideoManager />
          )}

          {/* SECCIÓN VLOGS */}
          {activeSection === 'vlogs' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{editingVlog ? '✏️ Editar Vlog' : '🎬 Subir Nuevo Vlog'}</CardTitle>
                  <CardDescription>
                    {editingVlog ? 'Actualiza la información del vlog' : 'Sube tu vlog directamente (video + miniatura)'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitVlog} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="vlog_title">Título *</Label>
                        <Input
                          id="vlog_title"
                          placeholder="Ej: Mi día de entrenamiento"
                          value={vlogData.title}
                          onChange={(e) => setVlogData({...vlogData, title: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vlog_duration">Duración *</Label>
                        <Input
                          id="vlog_duration"
                          placeholder="ej: 15:30"
                          value={vlogData.duration}
                          onChange={(e) => setVlogData({...vlogData, duration: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vlog_description">Descripción *</Label>
                      <Textarea
                        id="vlog_description"
                        placeholder="Describe el contenido de tu vlog..."
                        value={vlogData.description}
                        onChange={(e) => setVlogData({...vlogData, description: e.target.value})}
                        rows={4}
                        required
                      />
                    </div>

                    {/* Subida de archivos */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>📹 Archivo de Video {!editingVlog && '*'}</Label>
                        <Input
                          type="file"
                          accept="video/*"
                          onChange={(e) => setVlogFile(e.target.files[0])}
                          disabled={editingVlog}
                        />
                        {vlogFile && (
                          <p className="text-xs text-green-600">✅ {vlogFile.name}</p>
                        )}
                        {editingVlog && (
                          <p className="text-xs text-gray-500">El video no se puede cambiar. Si necesitas cambiarlo, elimina este vlog y crea uno nuevo.</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label>🖼️ Miniatura (opcional)</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setVlogThumbnailFile(e.target.files[0])}
                        />
                        {vlogThumbnailFile && (
                          <p className="text-xs text-green-600">✅ {vlogThumbnailFile.name}</p>
                        )}
                        {editingVlog && editingVlog.thumbnail_url && !vlogThumbnailFile && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Miniatura actual:</p>
                            <img src={editingVlog.thumbnail_url} alt="Miniatura actual" className="w-24 h-16 object-cover rounded" />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        type="submit" 
                        style={{ backgroundColor: '#FF69B4' }}
                        disabled={uploadingVlog}
                      >
                        {uploadingVlog ? '⏳ Subiendo...' : editingVlog ? '💾 Guardar Cambios' : '🎬 Subir Vlog'}
                      </Button>
                      {editingVlog && (
                        <Button type="button" variant="outline" onClick={handleCancelEditVlog}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Lista de Vlogs */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>🎬 Vlogs Subidos ({vlogsList.length})</CardTitle>
                      <CardDescription>Gestiona tus vlogs (videoblogs)</CardDescription>
                    </div>
                    <Button onClick={fetchVlogs} variant="outline" size="sm">
                      🔄 Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingVlogs ? (
                    <div className="text-center py-8">Cargando vlogs...</div>
                  ) : vlogsList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-lg mb-2">No hay vlogs aún</p>
                      <p className="text-sm">Sube tu primer vlog usando el formulario arriba</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {vlogsList.map((vlog) => (
                        <div 
                          key={vlog.id} 
                          className="border rounded-lg p-4 hover:bg-white/5 transition-colors"
                        >
                          <div className="flex gap-4">
                            <div className="w-32 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                              {vlog.thumbnail_url ? (
                                <img 
                                  src={vlog.thumbnail_url} 
                                  alt={vlog.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  🎬
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-lg truncate">{vlog.title}</h4>
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{vlog.description}</p>
                              <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs">
                                ⏱️ {vlog.duration}
                              </span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEditVlog(vlog)}
                              >
                                ✏️ Editar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteVlog(vlog.id)}
                              >
                                🗑️ Eliminar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => window.open(vlog.video_url, '_blank')}
                              >
                                👁️ Ver
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN PROGRESO DE USUARIOS */}
          {activeSection === 'progreso' && (
            <div className="space-y-6">
              {/* Filtro por Programa (Fit Evolution vs Asesoría 1 a 1) */}
              {(() => {
                // Mapa email → program_type (usa usersList que ya está cargado)
                const emailToProgram = new Map();
                (usersList || []).forEach(u => {
                  emailToProgram.set((u.email || '').toLowerCase(), u.program_type || null);
                });
                const filteredProgress = (usersProgress || []).filter(up => {
                  if (progressFilterProgram === 'all') return true;
                  const p = emailToProgram.get((up.user_email || '').toLowerCase()) || null;
                  if (progressFilterProgram === 'unset') return !p;
                  return p === progressFilterProgram;
                });
                const counts = {
                  fit: (usersProgress || []).filter(up => emailToProgram.get((up.user_email || '').toLowerCase()) === 'fit_evolution').length,
                  ase: (usersProgress || []).filter(up => emailToProgram.get((up.user_email || '').toLowerCase()) === 'asesoria_1a1').length,
                  unset: (usersProgress || []).filter(up => !emailToProgram.get((up.user_email || '').toLowerCase())).length,
                };
                return (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="text-sm font-medium">Filtrar por programa:</label>
                        <select
                          data-testid="progress-filter-program"
                          value={progressFilterProgram}
                          onChange={(e) => setProgressFilterProgram(e.target.value)}
                          className="border rounded-md px-3 py-1.5 bg-white text-gray-900"
                        >
                          <option value="all">Todos los programas</option>
                          <option value="fit_evolution">🚀 Fit Evolution</option>
                          <option value="asesoria_1a1">👑 Asesoría 1 a 1</option>
                          <option value="unset">Sin asignar</option>
                        </select>
                        <span className="ml-auto flex flex-wrap gap-2 text-xs">
                          <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">🚀 Fit Evolution: <b>{counts.fit}</b></span>
                          <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">👑 Asesoría 1 a 1: <b>{counts.ase}</b></span>
                          <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-800">Sin asignar: <b>{counts.unset}</b></span>
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>📈 Progreso de Usuarios</CardTitle>
                      <CardDescription>Ve el progreso semanal de tus clientes y deja comentarios</CardDescription>
                    </div>
                    <Button onClick={fetchUsersProgress} variant="outline" size="sm">
                      🔄 Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingProgress ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto"></div>
                      <p className="mt-2 text-gray-500">Cargando progreso...</p>
                    </div>
                  ) : usersProgress.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-6xl mb-4">📊</p>
                      <p className="text-lg">No hay registros de progreso aún</p>
                      <p className="text-sm mt-2">Los usuarios pueden registrar su progreso desde su dashboard</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {(() => {
                        // Aplicar filtro por programa antes de mostrar la lista
                        const emailToProgram = new Map();
                        (usersList || []).forEach(u => emailToProgram.set((u.email || '').toLowerCase(), u.program_type || null));
                        return (usersProgress || []).filter(up => {
                          if (progressFilterProgram === 'all') return true;
                          const p = emailToProgram.get((up.user_email || '').toLowerCase()) || null;
                          if (progressFilterProgram === 'unset') return !p;
                          return p === progressFilterProgram;
                        });
                      })().map((userProgress) => (
                        <Card key={userProgress.user_id} className="border-2 border-pink-100">
                          <CardHeader 
                            className="cursor-pointer bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100"
                            onClick={() => setSelectedUserProgress(
                              selectedUserProgress === userProgress.user_id ? null : userProgress.user_id
                            )}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <CardTitle className="text-lg flex items-center gap-2">
                                  👤 {userProgress.user_name || userProgress.user_email}
                                </CardTitle>
                                <CardDescription>{userProgress.user_email}</CardDescription>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <Badge className="bg-pink-500">{userProgress.total_entries} registros</Badge>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Último: {new Date(userProgress.latest_entry).toLocaleDateString('es-CO')}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAllUserProgress(userProgress.user_id, userProgress.user_email);
                                  }}
                                  title="Eliminar todo el progreso de este usuario"
                                >
                                  🗑️
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          
                          {selectedUserProgress === userProgress.user_id && (
                            <CardContent className="pt-4">
                              <div className="space-y-4">
                                {userProgress.entries.map((entry) => (
                                  <div 
                                    key={entry.id} 
                                    className="border rounded-lg overflow-hidden"
                                  >
                                    <div 
                                      className="p-4 cursor-pointer flex justify-between items-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                                      onClick={() => setExpandedProgressEntry(
                                        expandedProgressEntry === entry.id ? null : entry.id
                                      )}
                                    >
                                      <div className="flex items-center gap-4">
                                        <div className="bg-gradient-to-br from-pink-500 to-purple-600 text-white w-12 h-12 rounded-lg flex flex-col items-center justify-center">
                                          <span className="text-xs">SEM</span>
                                          <span className="text-lg font-bold">{entry.week_number}</span>
                                        </div>
                                        <div>
                                          <p className="font-semibold">{entry.weight} kg • {entry.height} cm</p>
                                          <div className="flex items-center gap-2 mt-1">
                                            <Badge className={`text-xs ${
                                              entry.completion_rate >= 80 ? 'bg-green-500' : 
                                              entry.completion_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                            }`}>
                                              {entry.completion_rate}% cumplimiento
                                            </Badge>
                                            {entry.photos?.length > 0 && (
                                              <Badge variant="outline" className="text-xs">📷 {entry.photos.length}</Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <span className="text-gray-400">
                                        {expandedProgressEntry === entry.id ? '▲' : '▼'}
                                      </span>
                                    </div>
                                    
                                    {expandedProgressEntry === entry.id && (
                                      <div className="p-4 border-t space-y-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                                        {/* Notas del usuario */}
                                        {entry.notes && (
                                          <div className="p-3 bg-blue-50 rounded-lg">
                                            <p className="text-xs text-blue-600 font-semibold mb-1">📝 Notas del usuario:</p>
                                            <p className="text-gray-700">{entry.notes}</p>
                                          </div>
                                        )}
                                        
                                        {/* Fotos */}
                                        {entry.photos && entry.photos.length > 0 && (
                                          <div>
                                            <p className="text-xs text-gray-500 font-semibold mb-2">📸 Fotos de progreso:</p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                              {entry.photos.map((photo, idx) => (
                                                <a key={idx} href={photo} target="_blank" rel="noopener noreferrer" className="block">
                                                  <img
                                                    src={photo}
                                                    alt={`Progreso semana ${entry.week_number}`}
                                                    className="w-full h-40 object-contain rounded-lg border-2 border-pink-200 hover:scale-105 transition-transform bg-gray-100"
                                                  />
                                                </a>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Comentarios existentes */}
                                        {entry.admin_comments && entry.admin_comments.length > 0 && (
                                          <div>
                                            <p className="text-xs text-gray-500 font-semibold mb-2">💬 Tus comentarios:</p>
                                            <div className="space-y-2">
                                              {entry.admin_comments.map((comment) => (
                                                <div key={comment.id} className="p-2 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                                                  <p className="text-purple-800 text-sm">{comment.text}</p>
                                                  <p className="text-xs text-purple-400 mt-1">
                                                    {new Date(comment.created_at).toLocaleString('es-CO')}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Agregar comentario */}
                                        <div className="flex gap-2">
                                          <Input
                                            placeholder="Escribe un comentario para el usuario..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            className="flex-1"
                                          />
                                          <Button 
                                            onClick={() => handleAddComment(entry.id)}
                                            disabled={submittingComment}
                                            style={{ backgroundColor: '#FF69B4' }}
                                          >
                                            {submittingComment ? '...' : '💬 Enviar'}
                                          </Button>
                                        </div>
                                        
                                        {/* Botón eliminar entrada */}
                                        <div className="flex justify-between items-center pt-2 border-t border-white/10">
                                          <p className="text-xs text-gray-400">
                                            Registrado: {new Date(entry.created_at).toLocaleString('es-CO')}
                                          </p>
                                          <Button 
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => handleDeleteProgressEntry(entry.id)}
                                            className="bg-red-500 hover:bg-red-600 text-white"
                                          >
                                            🗑️ Eliminar entrada
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN GENERADOR OFFLINE */}
          {activeSection === 'offline-generator' && (
            <OfflineGenerator />
          )}

          {/* SECCIÓN DIAGNÓSTICOS CLÍNICOS */}
          {activeSection === 'diagnosticos' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-white">🩺 Diagnósticos Clínicos</h1>
                <p className="text-gray-400">Gestiona los casos de diagnóstico y envía emails a usuarios con patologías</p>
              </div>
              <PathologyCasesManager />
            </div>
          )}

          {/* SECCIÓN GESTIÓN DE ALIMENTOS */}
          {activeSection === 'food-manager' && (
            <FoodManager />
          )}

          {/* SECCIÓN GESTOR DE ENTRENAMIENTO */}
          {activeSection === 'training-manager' && (
            <TrainingManager />
          )}

          {/* SECCIÓN CLIENTES VIP */}
          {activeSection === 'vip-clients' && (
            <VIPClientsManager />
          )}

          {/* SECCIÓN USUARIOS */}
          {activeSection === 'usuarios' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>👥 Usuarios Registrados ({usersList.length})</CardTitle>
                      <CardDescription>Gestiona los usuarios de la plataforma</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('deisycfit_token');
                            const res = await axios.post(`${API}/admin/cleanup-duplicate-plans`, {}, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const d = res.data;
                            toast.success(`Limpieza: ${d.users_merged} usuarios fusionados, ${d.plans_cleaned} planes duplicados eliminados`);
                            fetchUsers();
                          } catch (err) {
                            toast.error('Error al limpiar duplicados');
                          }
                        }}
                        variant="outline" 
                        size="sm"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid="cleanup-duplicates-btn"
                      >
                        Limpiar Duplicados
                      </Button>
                      <Button onClick={fetchUsers} variant="outline" size="sm">
                        🔄 Actualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingUsers ? (
                    <div className="text-center py-8">Cargando usuarios...</div>
                  ) : usersList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">No hay usuarios registrados</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Nombre</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Plan</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Vencimiento</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((user) => {
                            // Calcular estado de vencimiento
                            const endDate = user.subscription_end_date ? new Date(user.subscription_end_date) : null;
                            const now = new Date();
                            const daysUntilExpiry = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : null;
                            const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
                            const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
                            
                            return (
                            <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="px-4 py-3 text-sm">{user.email}</td>
                              <td className="px-4 py-3 text-sm">{user.name || 'Sin nombre'}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  user.subscription_plan === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                                  user.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                                  user.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.subscription_plan || 'Sin plan'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {endDate ? (
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    isExpired ? 'bg-red-100 text-red-800' :
                                    isExpiringSoon ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {isExpired 
                                      ? `Vencido hace ${Math.abs(daysUntilExpiry)} días`
                                      : isExpiringSoon
                                        ? `Vence en ${daysUntilExpiry} días`
                                        : endDate.toLocaleDateString('es-CO')
                                    }
                                  </span>
                                ) : (
                                  <span className="text-gray-500 text-xs">Sin fecha</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex gap-2">
                                  {user.role !== 'admin' ? (
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => handleDeleteUser(user.id, user.email)}
                                    >
                                      🗑️ Eliminar
                                    </Button>
                                  ) : (
                                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold">
                                      👑 ADMIN (Protegido)
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN NOTIFICACIONES */}
          {activeSection === 'notificaciones' && (
            <div className="space-y-6">
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader className="border-b border-white/10" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                  <CardTitle className="text-white">✉️ Enviar Notificación</CardTitle>
                  <CardDescription className="text-gray-400">Envía notificaciones a tus usuarios según su nivel de acceso (usa Brevo para Colombia)</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSendNotification} className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Título del nuevo contenido *</Label>
                      <Input
                        placeholder="Ej: Nueva rutina de glúteos disponible"
                        value={notificationData.title}
                        onChange={(e) => setNotificationData({...notificationData, title: e.target.value})}
                        required
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Mensaje de la notificación *</Label>
                      <Textarea
                        rows={3}
                        placeholder="Describe el nuevo contenido que estás anunciando..."
                        value={notificationData.message_preview}
                        onChange={(e) => setNotificationData({...notificationData, message_preview: e.target.value})}
                        required
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Tipo de contenido</Label>
                        <select
                          className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/5 text-white"
                          value={notificationData.content_type}
                          onChange={(e) => setNotificationData({...notificationData, content_type: e.target.value})}
                        >
                          <option value="video" className="bg-gray-900">📹 Video</option>
                          <option value="ebook" className="bg-gray-900">📚 Ebook</option>
                          <option value="program" className="bg-gray-900">💪 Programa</option>
                          <option value="vlog" className="bg-gray-900">🎬 Vlog</option>
                          <option value="workout" className="bg-gray-900">🏋️ Rutina</option>
                          <option value="general" className="bg-gray-900">📢 General</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-300">Sección</Label>
                        <select
                          className="w-full px-3 py-2 border border-white/20 rounded-md bg-white/5 text-white"
                          value={notificationData.section}
                          onChange={(e) => setNotificationData({...notificationData, section: e.target.value})}
                        >
                          <option value="entrenamiento" className="bg-gray-900">💪 Entrenamiento</option>
                          <option value="nutricion" className="bg-gray-900">🥗 Nutrición</option>
                          <option value="motivacion" className="bg-gray-900">🔥 Motivación</option>
                          <option value="general" className="bg-gray-900">📢 General</option>
                        </select>
                      </div>
                    </div>

                    {/* Nuevo Sistema de Acceso */}
                    <div className="p-4 rounded-lg space-y-4 border border-white/10" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
                      <h4 className="font-semibold flex items-center gap-2 text-white">🔓 Sistema de Acceso</h4>
                      
                      <div className="space-y-2">
                        <Label className="text-gray-300">¿Quién puede acceder a este contenido? (múltiple selección)</Label>
                        <div className="flex flex-wrap gap-3">
                          {/* Usar planes dinámicos cargados de la base de datos */}
                          {availablePlans.length > 0 ? (
                            <>
                              {availablePlans.map((plan) => (
                                <label 
                                  key={plan.id} 
                                  className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 transition-all ${
                                    notificationData.access_levels.includes(plan.id) 
                                      ? 'bg-green-500/20 border-green-500 text-green-300' 
                                      : 'bg-white/5 border-white/20 text-gray-400'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={notificationData.access_levels.includes(plan.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setNotificationData({
                                          ...notificationData, 
                                          access_levels: [...notificationData.access_levels, plan.id]
                                        });
                                      } else {
                                        setNotificationData({
                                          ...notificationData, 
                                          access_levels: notificationData.access_levels.filter(l => l !== plan.id)
                                        });
                                      }
                                    }}
                                  />
                                  <span className="text-sm font-medium">{plan.name}</span>
                                </label>
                              ))}
                              {/* Opción especial "De Pago" */}
                              <label 
                                className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 transition-all ${
                                  notificationData.access_levels.includes('paid') 
                                    ? 'bg-pink-500/20 border-pink-500 text-pink-300' 
                                    : 'bg-white/5 border-white/20 text-gray-400'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={notificationData.access_levels.includes('paid')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNotificationData({
                                        ...notificationData, 
                                        access_levels: [...notificationData.access_levels, 'paid']
                                      });
                                    } else {
                                      setNotificationData({
                                        ...notificationData, 
                                        access_levels: notificationData.access_levels.filter(l => l !== 'paid')
                                      });
                                    }
                                  }}
                                />
                                <span className="text-sm font-medium">💳 De Pago</span>
                              </label>
                            </>
                          ) : (
                            <p className="text-gray-400 text-sm">Cargando planes...</p>
                          )}
                        </div>
                      </div>

                      {notificationData.access_levels.includes('paid') && (
                        <div className="space-y-2">
                          <Label>💰 Precio (USD) - Para contenido de pago</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="19.99"
                            value={notificationData.price}
                            onChange={(e) => setNotificationData({...notificationData, price: e.target.value})}
                          />
                        </div>
                      )}
                    </div>

                    {/* Vista previa */}
                    {notificationData.title && (
                      <div className="p-4 border border-white/10 rounded-lg" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(168, 85, 247, 0.1))' }}>
                        <p className="text-sm font-semibold text-gray-300 mb-2">📧 Vista previa del email:</p>
                        <div className="p-3 rounded border" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <p className="font-bold text-lg text-white">{notificationData.title}</p>
                          <p className="text-gray-400 mt-1">{notificationData.message_preview || '(mensaje...)'}</p>
                          <div className="flex gap-2 mt-2 text-xs">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded">{notificationData.content_type}</span>
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded">{notificationData.section}</span>
                            <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded">
                              {notificationData.access_levels.includes('free') ? 'Gratuito' : 
                               notificationData.access_levels.includes('paid') && notificationData.price ? `$${notificationData.price} USD` :
                               notificationData.access_levels.join(', ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      style={{ backgroundColor: '#FF69B4' }} 
                      className="w-full py-6 text-lg"
                      disabled={sendingNotification}
                    >
                      {sendingNotification ? '⏳ Enviando...' : '📤 Enviar Notificación a Usuarios'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Historial de Notificaciones */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>📜 Historial de Notificaciones</CardTitle>
                    <Button onClick={fetchNotifications} variant="outline" size="sm">🔄</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingNotifications ? (
                    <div className="text-center py-8 text-gray-300">Cargando...</div>
                  ) : notificationsList.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-4xl mb-2">📭</p>
                      <p>No hay notificaciones enviadas</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {notificationsList.map((notif) => (
                        <div key={notif.id} className="border border-white/10 rounded-lg p-4 hover:bg-white/5 transition-all" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg text-white">{notif.title}</h4>
                              <p className="text-sm text-gray-400 mt-1">{notif.message_preview}</p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded">
                                  📹 {notif.content_type}
                                </span>
                                <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                                  📂 {notif.section}
                                </span>
                                {notif.access_levels?.map((level, idx) => (
                                  <span key={idx} className={`px-2 py-1 text-xs rounded ${
                                    level === 'free' ? 'bg-green-500/20 text-green-300' :
                                    level === 'paid' ? 'bg-pink-500/20 text-pink-300' :
                                    'bg-yellow-500/20 text-yellow-300'
                                  }`}>
                                    🔓 {level}
                                  </span>
                                ))}
                                {notif.price && (
                                  <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded font-bold">
                                    💰 ${notif.price} USD
                                  </span>
                                )}
                                <span className="px-2 py-1 bg-gray-500/20 text-gray-300 text-xs rounded">
                                  📧 {notif.sent_to_count || 0} enviados
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {new Date(notif.created_at).toLocaleDateString('es-ES', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleDeleteNotification(notif.id)}
                              >
                                🗑️ Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SECCIÓN CONFIGURACIÓN DEL SITIO */}
          {activeSection === 'configuracion' && (
            <div className="space-y-6">
              {/* Configuración de Fuentes */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    🔤 Tipografía / Fuentes
                  </CardTitle>
                  <CardDescription className="text-gray-400">Elige las fuentes para tu sitio web</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Fuente de Títulos */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Fuente para Títulos</Label>
                      <select
                        value={siteConfig.heading_font || 'Poppins'}
                        onChange={(e) => setSiteConfig({...siteConfig, heading_font: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg bg-zinc-800 border-white/10 text-white"
                      >
                        {availableFonts.map(font => (
                          <option key={font.name} value={font.name}>
                            {font.name} - {font.style}
                          </option>
                        ))}
                      </select>
                      <div 
                        className="p-4 rounded-lg mt-2 border border-white/10"
                        style={{ fontFamily: siteConfig.heading_font || 'Poppins', background: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <p className="text-2xl font-bold uppercase text-white">Vista Previa Título</p>
                      </div>
                    </div>

                    {/* Fuente de Cuerpo */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Fuente para Texto</Label>
                      <select
                        value={siteConfig.body_font || 'Poppins'}
                        onChange={(e) => setSiteConfig({...siteConfig, body_font: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg bg-zinc-800 border-white/10 text-white"
                      >
                        {availableFonts.map(font => (
                          <option key={font.name} value={font.name}>
                            {font.name} - {font.style}
                          </option>
                        ))}
                      </select>
                      <div 
                        className="p-4 rounded-lg mt-2 border border-white/10"
                        style={{ fontFamily: siteConfig.body_font || 'Poppins', background: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <p className="text-base text-gray-300">Este es un texto de ejemplo para ver cómo se ve el contenido.</p>
                      </div>
                    </div>

                    {/* Fuente de Botones */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Fuente para Botones</Label>
                      <select
                        value={siteConfig.primary_font || 'Poppins'}
                        onChange={(e) => setSiteConfig({...siteConfig, primary_font: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg bg-zinc-800 border-white/10 text-white"
                      >
                        {availableFonts.map(font => (
                          <option key={font.name} value={font.name}>
                            {font.name} - {font.style}
                          </option>
                        ))}
                      </select>
                      <div className="p-4 rounded-lg mt-2 border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <button 
                          className="px-6 py-2 text-white rounded-full font-semibold"
                          style={{ fontFamily: siteConfig.primary_font || 'Poppins', background: `linear-gradient(135deg, ${siteConfig.gradient_start || 'var(--brand-primary)'}, ${siteConfig.gradient_end || 'var(--brand-primary-strong)'})` }}
                        >
                          BOTÓN EJEMPLO
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Configuración de Colores */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    🎨 Paleta de Colores
                  </CardTitle>
                  <CardDescription className="text-gray-400">Personaliza los colores de tu marca - Los cambios se aplican inmediatamente al guardar</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Color Principal */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Color Principal (Botones, Textos destacados)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={siteConfig.theme_color || 'var(--brand-primary)'}
                          onChange={(e) => setSiteConfig({...siteConfig, theme_color: e.target.value})}
                          className="w-16 h-10 cursor-pointer border-0 p-0"
                        />
                        <Input
                          type="text"
                          value={siteConfig.theme_color || 'var(--brand-primary)'}
                          onChange={(e) => setSiteConfig({...siteConfig, theme_color: e.target.value})}
                          className="flex-1 bg-zinc-800 border-white/10 text-white"
                        />
                      </div>
                    </div>

                    {/* Color Secundario */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Color Secundario <span className="text-gray-500 text-xs">(Para títulos de sección como "ESPECIALIDADES")</span></Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={siteConfig.secondary_color || '#00F0FF'}
                          onChange={(e) => setSiteConfig({...siteConfig, secondary_color: e.target.value})}
                          className="w-16 h-10 cursor-pointer border-0 p-0"
                        />
                        <Input
                          type="text"
                          value={siteConfig.secondary_color || '#00F0FF'}
                          onChange={(e) => setSiteConfig({...siteConfig, secondary_color: e.target.value})}
                          className="flex-1 bg-zinc-800 border-white/10 text-white"
                        />
                      </div>
                    </div>

                    {/* Color Acento */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Color Acento</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={siteConfig.accent_color || 'var(--brand-primary-strong)'}
                          onChange={(e) => setSiteConfig({...siteConfig, accent_color: e.target.value})}
                          className="w-16 h-10 cursor-pointer border-0 p-0"
                        />
                        <Input
                          type="text"
                          value={siteConfig.accent_color || 'var(--brand-primary-strong)'}
                          onChange={(e) => setSiteConfig({...siteConfig, accent_color: e.target.value})}
                          className="flex-1 bg-zinc-800 border-white/10 text-white"
                        />
                      </div>
                    </div>

                    {/* Color de Fondo */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Color de Fondo</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={siteConfig.bg_color || '#0a0a0a'}
                          onChange={(e) => setSiteConfig({...siteConfig, bg_color: e.target.value})}
                          className="w-16 h-10 cursor-pointer border-0 p-0"
                        />
                        <Input
                          type="text"
                          value={siteConfig.bg_color || '#0a0a0a'}
                          onChange={(e) => setSiteConfig({...siteConfig, bg_color: e.target.value})}
                          className="flex-1 bg-zinc-800 border-white/10 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Gradiente */}
                  <div className="mt-6 space-y-4">
                    <Label className="text-gray-300">Gradiente (para botones principales)</Label>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-500">Color Inicio del Gradiente</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={siteConfig.gradient_start || 'var(--brand-primary)'}
                            onChange={(e) => setSiteConfig({...siteConfig, gradient_start: e.target.value})}
                            className="w-16 h-10 cursor-pointer border-0 p-0"
                          />
                          <Input
                            type="text"
                            value={siteConfig.gradient_start || 'var(--brand-primary)'}
                            onChange={(e) => setSiteConfig({...siteConfig, gradient_start: e.target.value})}
                            className="flex-1 bg-zinc-800 border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-500">Color Fin del Gradiente</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={siteConfig.gradient_end || 'var(--brand-primary-strong)'}
                            onChange={(e) => setSiteConfig({...siteConfig, gradient_end: e.target.value})}
                            className="w-16 h-10 cursor-pointer border-0 p-0"
                          />
                          <Input
                            type="text"
                            value={siteConfig.gradient_end || 'var(--brand-primary-strong)'}
                            onChange={(e) => setSiteConfig({...siteConfig, gradient_end: e.target.value})}
                            className="flex-1 bg-zinc-800 border-white/10 text-white"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Vista previa del gradiente */}
                    <div className="mt-4 p-4 rounded-xl border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                      <p className="text-sm text-gray-400 mb-3">Vista previa del gradiente:</p>
                      <div 
                        className="h-16 rounded-lg"
                        style={{ 
                          background: `linear-gradient(135deg, ${siteConfig.gradient_start || 'var(--brand-primary)'}, ${siteConfig.gradient_end || 'var(--brand-primary-strong)'})` 
                        }}
                      />
                      <div className="flex gap-3 mt-4">
                        <button 
                          className="px-6 py-2 text-white rounded-full font-semibold"
                          style={{ background: `linear-gradient(135deg, ${siteConfig.gradient_start || 'var(--brand-primary)'}, ${siteConfig.gradient_end || 'var(--brand-primary-strong)'})` }}
                        >
                          Botón Primario
                        </button>
                        <button 
                          className="px-6 py-2 rounded-full font-semibold"
                          style={{ border: `2px solid ${siteConfig.theme_color || 'var(--brand-primary)'}`, color: siteConfig.theme_color || 'var(--brand-primary)', background: 'transparent' }}
                        >
                          Botón Outline
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Colores de Texto según Fondo */}
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-gray-300 text-lg">✍️ Colores de Texto</Label>
                      <span className="text-gray-500 text-sm">(Se aplican a toda la página web)</span>
                    </div>
                    <p className="text-gray-500 text-sm">
                      Configura los colores de texto para que sean legibles sobre cualquier fondo.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Panel Fondo Oscuro */}
                      <div className="p-5 rounded-xl border border-white/10" style={{ backgroundColor: siteConfig.bg_color || '#0a0a0a' }}>
                        <p className="text-sm font-medium mb-4" style={{ color: siteConfig.text_on_dark || '#FFFFFF' }}>
                          📍 Panel Fondo Oscuro
                        </p>
                        <p className="text-sm mb-4" style={{ color: siteConfig.text_on_dark || '#FFFFFF' }}>
                          Este es un texto de ejemplo sobre fondo oscuro. Elige un color claro para mejor legibilidad.
                        </p>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Color de Texto para Fondo Oscuro</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={siteConfig.text_on_dark || '#FFFFFF'}
                              onChange={(e) => setSiteConfig({...siteConfig, text_on_dark: e.target.value})}
                              className="w-16 h-10 cursor-pointer border-0 p-0"
                            />
                            <Input
                              type="text"
                              value={siteConfig.text_on_dark || '#FFFFFF'}
                              onChange={(e) => setSiteConfig({...siteConfig, text_on_dark: e.target.value})}
                              className="flex-1 bg-zinc-800 border-white/10 text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Panel Fondo Claro */}
                      <div className="p-5 rounded-xl border border-gray-200" style={{ backgroundColor: '#FFFFFF' }}>
                        <p className="text-sm font-medium mb-4" style={{ color: siteConfig.text_on_light || '#1a1a1a' }}>
                          📍 Panel Fondo Claro
                        </p>
                        <p className="text-sm mb-4" style={{ color: siteConfig.text_on_light || '#1a1a1a' }}>
                          Este es un texto de ejemplo sobre fondo claro. Elige un color oscuro para mejor legibilidad.
                        </p>
                        <div className="space-y-2">
                          <Label style={{ color: siteConfig.text_on_light || '#1a1a1a' }} className="text-sm">Color de Texto para Fondo Claro</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={siteConfig.text_on_light || '#1a1a1a'}
                              onChange={(e) => setSiteConfig({...siteConfig, text_on_light: e.target.value})}
                              className="w-16 h-10 cursor-pointer border-0 p-0"
                            />
                            <Input
                              type="text"
                              value={siteConfig.text_on_light || '#1a1a1a'}
                              onChange={(e) => setSiteConfig({...siteConfig, text_on_light: e.target.value})}
                              className="flex-1 border-gray-300"
                              style={{ backgroundColor: '#f5f5f5', color: '#1a1a1a' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Estilos de Componentes */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    🎯 Estilos de Componentes
                  </CardTitle>
                  <CardDescription className="text-gray-400">Personaliza la apariencia de botones y tarjetas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Estilo de Botones */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Estilo de Botones</Label>
                      <select
                        value={siteConfig.button_style || 'rounded-full'}
                        onChange={(e) => setSiteConfig({...siteConfig, button_style: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg bg-zinc-800 border-white/10 text-white"
                      >
                        <option value="rounded-full">Pill / Redondeado (moderno)</option>
                        <option value="rounded-lg">Redondeado Suave</option>
                        <option value="rounded-md">Ligeramente Redondeado</option>
                        <option value="rounded-none">Cuadrado (sharp)</option>
                      </select>
                      <div className="flex gap-2 mt-3">
                        <button 
                          className={`px-6 py-2 text-white font-semibold ${siteConfig.button_style || 'rounded-full'}`}
                          style={{ background: `linear-gradient(135deg, ${siteConfig.gradient_start || 'var(--brand-primary)'}, ${siteConfig.gradient_end || 'var(--brand-primary-strong)'})` }}
                        >
                          Botón Primario
                        </button>
                        <button 
                          className={`px-6 py-2 font-semibold ${siteConfig.button_style || 'rounded-full'}`}
                          style={{ border: `2px solid ${siteConfig.theme_color || 'var(--brand-primary)'}`, color: siteConfig.theme_color || 'var(--brand-primary)', background: 'transparent' }}
                        >
                          Botón Outline
                        </button>
                      </div>
                    </div>

                    {/* Estilo de Tarjetas */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Estilo de Tarjetas</Label>
                      <select
                        value={siteConfig.card_style || 'rounded-3xl'}
                        onChange={(e) => setSiteConfig({...siteConfig, card_style: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg bg-zinc-800 border-white/10 text-white"
                      >
                        <option value="rounded-3xl">Muy Redondeado</option>
                        <option value="rounded-xl">Redondeado Grande</option>
                        <option value="rounded-lg">Redondeado Normal</option>
                        <option value="rounded-md">Redondeado Suave</option>
                        <option value="rounded-none">Cuadrado</option>
                      </select>
                      <div 
                        className={`p-4 border mt-3 ${siteConfig.card_style || 'rounded-3xl'}`}
                        style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <h4 className="font-bold mb-2" style={{ color: siteConfig.theme_color || 'var(--brand-primary)' }}>Tarjeta de Ejemplo</h4>
                        <p className="text-gray-400 text-sm">Este es el contenido de una tarjeta de ejemplo.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ==================== PORTADAS DEL DASHBOARD DE USUARIO ==================== */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white text-xl">
                    🖼️ Portadas del Dashboard de Usuario
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Personaliza las imágenes de portada que ven los usuarios en su dashboard. Las imágenes se guardan en Cloudinary para mayor estabilidad.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  
                  {/* SECCIÓN 1: Planes Premium/Gold/Competitivo */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-white/10">
                      <span className="text-lg">⭐</span>
                      <h3 className="text-white font-semibold">Planes Premium / Gold / Competitivo</h3>
                      <span className="text-xs text-gray-500 bg-purple-500/20 px-2 py-1 rounded-full">Personalizados</span>
                    </div>

                    {/* ═════ NUEVO · Portadas del Dashboard específicas por GÉNERO ═════ */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-blue-500/10 border border-pink-500/20 space-y-4" data-testid="gender-dashboard-covers">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🎨</span>
                        <h3 className="text-white font-semibold">Portadas del Dashboard por Género</h3>
                        <span className="text-xs text-gray-400 bg-white/10 px-2 py-1 rounded-full">👩 Mujer / 👨 Hombre</span>
                      </div>
                      <p className="text-xs text-gray-400 leading-snug">
                        Sube <b>una foto por género</b> para cada una de las 4 tarjetas principales. Si el usuario es
                        &lsquo;<b>female</b>&rsquo; se le muestra la foto rosa; si es &lsquo;<b>male</b>&rsquo; se le
                        muestra la foto azul. Si un usuario no tiene género definido, se usa la portada genérica de más abajo.
                      </p>
                      {[
                        { key: 'program_diet_image', label: '🍽️ Plan de Alimentación', hint: 'diet' },
                        { key: 'program_workout_image', label: '💪 Rutina de Entrenamiento', hint: 'workout' },
                        { key: 'program_progress_image', label: '📈 Mi Progreso', hint: 'progress' },
                        { key: 'program_videos_image', label: '🎬 Biblioteca de Videos', hint: 'videos' },
                      ].map(({ key, label, hint }) => (
                        <div key={key} className="grid md:grid-cols-2 gap-4 p-3 rounded-lg bg-black/20">
                          <div className="col-span-full text-sm font-semibold text-white">{label}</div>
                          {/* MUJERES */}
                          <div className="space-y-2 p-3 rounded-lg border border-pink-500/30 bg-pink-500/5">
                            <Label className="text-pink-300 font-medium">👩 Imagen para Mujeres</Label>
                            <Input
                              type="file"
                              accept="image/*"
                              data-testid={`upload-${hint}-female`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('program_type', `${hint}_female`);
                                try {
                                  const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setSiteConfig(prev => ({ ...prev, [`${key}_female`]: res.data.url }));
                                  toast.success(`Imagen de ${label.replace(/[^a-záéíóú ]/gi, '').trim()} (mujeres) actualizada`);
                                } catch { toast.error('Error al subir imagen'); }
                              }}
                              className="bg-zinc-800 border-pink-500/30 text-white file:bg-pink-500 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                            />
                            {siteConfig[`${key}_female`] && (
                              <div className="relative">
                                <img src={siteConfig[`${key}_female`]} alt={`${label} mujeres`} className="w-full h-24 object-cover rounded" data-testid={`preview-${hint}-female`} />
                                <Button size="sm" variant="destructive" className="absolute top-1 right-1 px-2 py-0.5 text-xs" onClick={() => setSiteConfig(prev => ({ ...prev, [`${key}_female`]: '' }))}>×</Button>
                              </div>
                            )}
                          </div>
                          {/* HOMBRES */}
                          <div className="space-y-2 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5">
                            <Label className="text-blue-300 font-medium">👨 Imagen para Hombres</Label>
                            <Input
                              type="file"
                              accept="image/*"
                              data-testid={`upload-${hint}-male`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('program_type', `${hint}_male`);
                                try {
                                  const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setSiteConfig(prev => ({ ...prev, [`${key}_male`]: res.data.url }));
                                  toast.success(`Imagen de ${label.replace(/[^a-záéíóú ]/gi, '').trim()} (hombres) actualizada`);
                                } catch { toast.error('Error al subir imagen'); }
                              }}
                              className="bg-zinc-800 border-blue-500/30 text-white file:bg-blue-500 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                            />
                            {siteConfig[`${key}_male`] && (
                              <div className="relative">
                                <img src={siteConfig[`${key}_male`]} alt={`${label} hombres`} className="w-full h-24 object-cover rounded" data-testid={`preview-${hint}-male`} />
                                <Button size="sm" variant="destructive" className="absolute top-1 right-1 px-2 py-0.5 text-xs" onClick={() => setSiteConfig(prev => ({ ...prev, [`${key}_male`]: '' }))}>×</Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* ═════════════════════════════════════════════════════════════ */}

                    {/* ═════ NUEVO · Portadas por CATEGORÍA DE PLAN + GÉNERO ═════ */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-emerald-500/10 border border-purple-500/30 space-y-4" data-testid="plan-gender-covers">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg">🎯</span>
                        <h3 className="text-white font-semibold">Portadas por Plan + Género</h3>
                        <span className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-2 py-1 rounded-full">🤖 AUTOMÁTICO · Fit Evolution</span>
                        <span className="text-xs text-purple-300 bg-purple-500/10 border border-purple-500/40 px-2 py-1 rounded-full">👑 PERSONALIZADO · Premium · Competitivo · Gold VIP</span>
                      </div>
                      <p className="text-xs text-gray-400 leading-snug">
                        Estas fotos <b>ganan prioridad</b>. Si un usuario está en <b>Fit Evolution</b> se muestran las
                        AUTOMÁTICO; si está en <b>Premium / Competitivo / Gold VIP</b> se muestran las PERSONALIZADO.
                        Si faltase alguna, cae a las &lsquo;Portadas por Género&rsquo; de arriba, y si tampoco existen, a la portada genérica.
                      </p>
                      {[
                        { key: 'diet',     label: '🍽️ Alimentación' },
                        { key: 'workout',  label: '💪 Rutina' },
                        { key: 'progress', label: '📈 Mi Progreso' },
                        { key: 'videos',   label: '🎬 Biblioteca de Videos' },
                      ].map(({ key: section, label }) => (
                        <div key={section} className="p-3 rounded-lg bg-black/20 space-y-3">
                          <div className="text-sm font-semibold text-white">{label}</div>
                          <div className="grid md:grid-cols-2 gap-3">
                            {[
                              { category: 'automatic',    catLabel: '🤖 AUTOMÁTICO',    catColor: 'emerald' },
                              { category: 'personalized', catLabel: '👑 PERSONALIZADO', catColor: 'purple' },
                            ].map(({ category, catLabel, catColor }) => (
                              <div key={category} className={`p-2 rounded-lg border border-${catColor}-500/30 bg-${catColor}-500/5 space-y-2`}>
                                <div className={`text-xs font-bold text-${catColor}-300`}>{catLabel}</div>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { gender: 'female', gLabel: '👩 Mujer', gTint: 'pink' },
                                    { gender: 'male',   gLabel: '👨 Hombre', gTint: 'blue' },
                                  ].map(({ gender, gLabel, gTint }) => {
                                    const fieldKey = `image_${category}_${gender}_${section}`;
                                    return (
                                      <div key={gender} className={`space-y-1 p-2 rounded border border-${gTint}-500/30 bg-${gTint}-500/5`}>
                                        <Label className={`text-${gTint}-300 text-xs font-medium block`}>{gLabel}</Label>
                                        <Input
                                          type="file"
                                          accept="image/*"
                                          data-testid={`upload-${category}-${gender}-${section}`}
                                          onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            formData.append('program_type', fieldKey);
                                            try {
                                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                              });
                                              setSiteConfig(prev => ({ ...prev, [fieldKey]: res.data.url }));
                                              toast.success(`${catLabel} ${gLabel} · ${label} actualizada`);
                                            } catch { toast.error('Error al subir imagen'); }
                                          }}
                                          className="bg-zinc-800 border-zinc-600 text-white text-xs file:bg-zinc-600 file:text-white file:border-0 file:rounded file:px-1.5 file:py-0.5 file:mr-1.5"
                                        />
                                        {siteConfig[fieldKey] && (
                                          <div className="relative">
                                            <img src={siteConfig[fieldKey]} alt={`${catLabel} ${gLabel} ${label}`} className="w-full h-16 object-cover rounded" data-testid={`preview-${category}-${gender}-${section}`} />
                                            <Button size="sm" variant="destructive" className="absolute top-0.5 right-0.5 px-1.5 py-0 text-[10px] h-5" onClick={() => setSiteConfig(prev => ({ ...prev, [fieldKey]: '' }))}>×</Button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* ═════════════════════════════════════════════════════════════ */}

                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Mi Plan de Alimentación */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium">
                          <span className="text-xl">🍽️</span> Mi Plan de Alimentación
                        </Label>
                        <p className="text-xs text-gray-400">Dieta personalizada del usuario</p>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'diet');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_diet_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white file:bg-green-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2 file:cursor-pointer"
                        />
                        {siteConfig.program_diet_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_diet_image} alt="Diet cover" className="w-full h-28 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" onClick={() => setSiteConfig({...siteConfig, program_diet_image: ''})}>
                                Quitar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-28 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-sm">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Mi Rutina */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium">
                          <span className="text-xl">💪</span> Mi Rutina
                        </Label>
                        <p className="text-xs text-gray-400">Entrenamiento personalizado</p>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'workout');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_workout_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white file:bg-blue-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2 file:cursor-pointer"
                        />
                        {siteConfig.program_workout_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_workout_image} alt="Workout cover" className="w-full h-28 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" onClick={() => setSiteConfig({...siteConfig, program_workout_image: ''})}>
                                Quitar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-28 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-sm">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Mi Progreso */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium">
                          <span className="text-xl">📊</span> Mi Progreso
                        </Label>
                        <p className="text-xs text-gray-400">Seguimiento de evolución</p>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'progress');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_progress_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white file:bg-purple-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2 file:cursor-pointer"
                        />
                        {siteConfig.program_progress_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_progress_image} alt="Progress cover" className="w-full h-28 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" onClick={() => setSiteConfig({...siteConfig, program_progress_image: ''})}>
                                Quitar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-28 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-sm">Sin imagen</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN 2: Plan Basic */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-white/10">
                      <span className="text-lg">📋</span>
                      <h3 className="text-white font-semibold">Fit Evolution</h3>
                      <span className="text-xs text-gray-500 bg-yellow-500/20 px-2 py-1 rounded-full">Generadores</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Crea tu Menú */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-yellow-500/10 to-orange-500/5 border border-yellow-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium">
                          <span className="text-xl">🥗</span> Crea tu Menú
                        </Label>
                        <p className="text-xs text-gray-400">Calculadora de dieta para plan básico</p>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'menu');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_menu_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white file:bg-yellow-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2 file:cursor-pointer"
                        />
                        {siteConfig.program_menu_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_menu_image} alt="Menu cover" className="w-full h-28 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" onClick={() => setSiteConfig({...siteConfig, program_menu_image: ''})}>
                                Quitar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-28 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-sm">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Crea tu Rutina - Nota: usa la misma imagen de workout */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium">
                          <span className="text-xl">🏋️</span> Crea tu Rutina
                        </Label>
                        <p className="text-xs text-gray-400">Generador de rutinas para plan básico</p>
                        <div className="p-3 rounded-lg bg-zinc-800/30 border border-white/5">
                          <p className="text-xs text-gray-400">
                            <span className="text-yellow-500">💡</span> Esta sección usa la misma imagen de "Mi Rutina" configurada arriba.
                          </p>
                        </div>
                        {siteConfig.program_workout_image ? (
                          <img src={siteConfig.program_workout_image} alt="Workout cover" className="w-full h-28 object-cover rounded-lg opacity-70" />
                        ) : (
                          <div className="w-full h-28 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-sm">Configura "Mi Rutina" arriba</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN 3: Contenido General (Todos los planes) */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-white/10">
                      <span className="text-lg">📚</span>
                      <h3 className="text-white font-semibold">Contenido General</h3>
                      <span className="text-xs text-gray-500 bg-cyan-500/20 px-2 py-1 rounded-full">Todos los planes</span>
                    </div>
                    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {/* Data Progresión */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-pink-500/10 to-pink-600/5 border border-pink-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium text-sm">
                          <span className="text-lg">📈</span> Data Progresión
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'data');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_data_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white text-xs file:bg-pink-600 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-1 file:cursor-pointer file:text-xs"
                        />
                        {siteConfig.program_data_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_data_image} alt="Data cover" className="w-full h-20 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-auto" onClick={() => setSiteConfig({...siteConfig, program_data_image: ''})}>
                                X
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Videos */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium text-sm">
                          <span className="text-lg">🎬</span> Videos
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'videos');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_videos_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white text-xs file:bg-red-600 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-1 file:cursor-pointer file:text-xs"
                        />
                        {siteConfig.program_videos_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_videos_image} alt="Videos cover" className="w-full h-20 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-auto" onClick={() => setSiteConfig({...siteConfig, program_videos_image: ''})}>
                                X
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Ebooks */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium text-sm">
                          <span className="text-lg">📚</span> Ebooks
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'ebooks');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_ebooks_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white text-xs file:bg-indigo-600 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-1 file:cursor-pointer file:text-xs"
                        />
                        {siteConfig.program_ebooks_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_ebooks_image} alt="Ebooks cover" className="w-full h-20 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-auto" onClick={() => setSiteConfig({...siteConfig, program_ebooks_image: ''})}>
                                X
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Recetas */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium text-sm">
                          <span className="text-lg">🍳</span> Recetas
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'recipes');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_recipes_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white text-xs file:bg-teal-600 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-1 file:cursor-pointer file:text-xs"
                        />
                        {siteConfig.program_recipes_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_recipes_image} alt="Recipes cover" className="w-full h-20 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-auto" onClick={() => setSiteConfig({...siteConfig, program_recipes_image: ''})}>
                                X
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      {/* Recetarios */}
                      <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
                        <Label className="text-white flex items-center gap-2 font-medium text-sm">
                          <span className="text-lg">📖</span> Recetarios
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('program_type', 'recetarios');
                            try {
                              const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setSiteConfig({...siteConfig, program_recetarios_image: res.data.url});
                              toast.success('Imagen subida a Cloudinary');
                            } catch (err) {
                              toast.error('Error al subir imagen');
                            }
                          }}
                          className="bg-zinc-800/50 border-white/10 text-white text-xs file:bg-amber-600 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-1 file:cursor-pointer file:text-xs"
                        />
                        {siteConfig.program_recetarios_image ? (
                          <div className="relative group">
                            <img src={siteConfig.program_recetarios_image} alt="Recetarios cover" className="w-full h-20 object-cover rounded-lg" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-auto" onClick={() => setSiteConfig({...siteConfig, program_recetarios_image: ''})}>
                                X
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-20 bg-zinc-800/30 rounded-lg flex items-center justify-center border border-dashed border-white/10">
                            <span className="text-gray-500 text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Nota informativa */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">☁️</span>
                      <div>
                        <p className="text-white font-medium text-sm">Almacenamiento en la Nube</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Las imágenes se guardan automáticamente en Cloudinary para mayor estabilidad y velocidad de carga. 
                          Tamaño recomendado: <span className="text-cyan-400">800x400 píxeles</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contenido del Sitio */}
              <Card className="border border-white/10" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    📝 Contenido del Sitio
                  </CardTitle>
                  <CardDescription>Imágenes y textos principales. Sube las imágenes directamente.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Fotos de bienvenida específicas por género (nuevas) */}
                  <div className="grid md:grid-cols-2 gap-6 p-4 rounded-xl border border-pink-500/20 bg-gradient-to-r from-pink-500/5 to-blue-500/5">
                    <div className="space-y-2">
                      <Label className="text-pink-300 font-semibold">👩 Foto de Bienvenida para Mujeres</Label>
                      <p className="text-xs text-gray-500 mb-2">Se muestra a las alumnas registradas como <b>female</b>. Recomendado 800x1200px.</p>
                      <Input
                        type="file"
                        accept="image/*"
                        data-testid="upload-hero-female"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('program_type', 'hero_female');
                          try {
                            const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setSiteConfig(prev => ({ ...prev, hero_image_female: res.data.url }));
                            toast.success('Foto para mujeres actualizada');
                          } catch (err) {
                            toast.error('Error al subir imagen');
                          }
                        }}
                        className="bg-zinc-800 border-pink-500/30 text-white file:bg-pink-500 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                      />
                      {siteConfig.hero_image_female && (
                        <div className="mt-2 relative">
                          <img src={siteConfig.hero_image_female} alt="Preview mujeres" className="w-full h-48 object-cover object-top rounded-lg" data-testid="hero-female-preview" />
                          <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => setSiteConfig(prev => ({ ...prev, hero_image_female: '' }))}>
                            Quitar
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-blue-300 font-semibold">👨 Foto de Bienvenida para Hombres</Label>
                      <p className="text-xs text-gray-500 mb-2">Se muestra a los alumnos registrados como <b>male</b>. Recomendado 800x1200px.</p>
                      <Input
                        type="file"
                        accept="image/*"
                        data-testid="upload-hero-male"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('program_type', 'hero_male');
                          try {
                            const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setSiteConfig(prev => ({ ...prev, hero_image_male: res.data.url }));
                            toast.success('Foto para hombres actualizada');
                          } catch (err) {
                            toast.error('Error al subir imagen');
                          }
                        }}
                        className="bg-zinc-800 border-blue-500/30 text-white file:bg-blue-500 file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                      />
                      {siteConfig.hero_image_male && (
                        <div className="mt-2 relative">
                          <img src={siteConfig.hero_image_male} alt="Preview hombres" className="w-full h-48 object-cover object-top rounded-lg" data-testid="hero-male-preview" />
                          <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => setSiteConfig(prev => ({ ...prev, hero_image_male: '' }))}>
                            Quitar
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="md:col-span-2 text-xs text-gray-400 italic">
                      💡 Estas fotos <b>reemplazan</b> a la Imagen Hero por defecto para usuarios con género definido. Si un usuario no tiene género o es "ambos", se usa la Imagen Hero de abajo.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-gray-300">📸 Imagen Hero (por defecto)</Label>
                      <p className="text-xs text-gray-500 mb-2">Fallback cuando el usuario no tiene género definido. Recomendado 800x1200px</p>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('program_type', 'hero');
                          try {
                            const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setSiteConfig({...siteConfig, hero_image: res.data.url});
                            toast.success('Imagen de portada actualizada');
                          } catch (err) {
                            toast.error('Error al subir imagen');
                          }
                        }}
                        className="bg-zinc-800 border-white/10 text-white file:bg-[var(--brand-primary)] file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                      />
                      {siteConfig.hero_image && (
                        <div className="mt-2 relative">
                          <img src={siteConfig.hero_image} alt="Hero preview" className="w-full h-48 object-cover object-top rounded-lg" />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => setSiteConfig({...siteConfig, hero_image: ''})}
                          >
                            Quitar
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Logo del Sitio</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('program_type', 'logo');
                          try {
                            const res = await axios.post(`${API}/admin/upload-program-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setSiteConfig({...siteConfig, logo_url: res.data.url});
                            toast.success('Logo subido');
                          } catch (err) {
                            toast.error('Error al subir imagen');
                          }
                        }}
                        className="bg-zinc-800 border-white/10 text-white file:bg-[var(--brand-primary)] file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
                      />
                      {siteConfig.logo_url && (
                        <img src={siteConfig.logo_url} alt="Logo preview" className="h-16 object-contain mt-2" />
                      )}
                    </div>
                  </div>

                  {/* Textos del Hero Principal */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">✨</span>
                      <h4 className="text-white font-semibold">Textos del Hero (Página Principal)</h4>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-sm">Título Línea 1</Label>
                        <Input
                          placeholder="Ej: Transforma"
                          value={siteConfig.hero_title_line1 || ''}
                          onChange={(e) => setSiteConfig({...siteConfig, hero_title_line1: e.target.value})}
                          className="bg-zinc-800/50 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-sm">Título Línea 2 <span className="text-pink-400">(con color)</span></Label>
                        <Input
                          placeholder="Ej: tu cuerpo"
                          value={siteConfig.hero_title_line2 || ''}
                          onChange={(e) => setSiteConfig({...siteConfig, hero_title_line2: e.target.value})}
                          className="bg-zinc-800/50 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300 text-sm">Título Línea 3</Label>
                        <Input
                          placeholder="Ej: tu vida"
                          value={siteConfig.hero_title_line3 || ''}
                          onChange={(e) => setSiteConfig({...siteConfig, hero_title_line3: e.target.value})}
                          className="bg-zinc-800/50 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-sm">Subtítulo / Descripción</Label>
                      <Textarea
                        rows={2}
                        placeholder="Ej: Espacio de transformación integral con planes personalizados de entrenamiento y nutrición."
                        value={siteConfig.hero_subtitle || ''}
                        onChange={(e) => setSiteConfig({...siteConfig, hero_subtitle: e.target.value})}
                        className="bg-zinc-800/50 border-white/10 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-sm">Texto Destacado <span className="text-cyan-400">(aparece con color)</span></Label>
                      <Input
                        placeholder="Ej: salud hormonal femenina"
                        value={siteConfig.hero_highlight || ''}
                        onChange={(e) => setSiteConfig({...siteConfig, hero_highlight: e.target.value})}
                        className="bg-zinc-800/50 border-white/10 text-white"
                      />
                      <p className="text-xs text-gray-500">Este texto aparece después del subtítulo con el color secundario.</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Texto "Acerca de"</Label>
                    <Textarea
                      rows={4}
                      placeholder="Descripción de tu marca..."
                      value={siteConfig.about_text}
                      onChange={(e) => setSiteConfig({...siteConfig, about_text: e.target.value})}
                      className="bg-zinc-800 border-white/10 text-white"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Vista Previa Completa */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    👁️ Vista Previa del Diseño
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className={`p-8 ${siteConfig.card_style || 'rounded-lg'}`}
                    style={{ 
                      backgroundColor: siteConfig.bg_color || '#0a0a0a',
                      color: siteConfig.text_color || '#ffffff'
                    }}
                  >
                    <h2 
                      className="text-4xl font-bold mb-4 uppercase"
                      style={{ 
                        fontFamily: siteConfig.heading_font || 'Oswald',
                        color: siteConfig.theme_color
                      }}
                    >
                      Título de Ejemplo
                    </h2>
                    <p 
                      className="text-lg mb-6"
                      style={{ fontFamily: siteConfig.body_font || 'DM Sans' }}
                    >
                      {siteConfig.about_text || 'Este es un texto de ejemplo para ver cómo se verá el contenido en tu sitio web.'}
                    </p>
                    <div className="flex gap-4">
                      <button 
                        className={`px-8 py-3 text-white font-bold ${siteConfig.button_style || 'rounded-full'}`}
                        style={{ 
                          background: `linear-gradient(to right, ${siteConfig.gradient_start || '#FF69B4'}, ${siteConfig.gradient_end || 'var(--brand-primary-strong)'})`,
                          fontFamily: siteConfig.primary_font || 'Oswald'
                        }}
                      >
                        COMENZAR AHORA
                      </button>
                      <button 
                        className={`px-8 py-3 border-2 font-bold ${siteConfig.button_style || 'rounded-full'}`}
                        style={{ 
                          borderColor: siteConfig.theme_color,
                          color: siteConfig.theme_color,
                          fontFamily: siteConfig.primary_font || 'Oswald'
                        }}
                      >
                        VER MÁS
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Botón Guardar */}
              <Card>
                <CardContent className="pt-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>💡 Nota:</strong> Al guardar, los cambios se aplicarán en toda la plataforma. 
                      Asegúrate de revisar la vista previa antes de guardar.
                    </p>
                  </div>
                  <Button 
                    onClick={handleUpdateSiteConfig}
                    style={{ backgroundColor: siteConfig.theme_color }} 
                    className="w-full text-white font-bold py-6 text-lg"
                  >
                    💾 Guardar Toda la Configuración
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === 'formularios' && (
            <div className="space-y-6">
              {/* Pestañas principales: Respuestas / Editor / Clínicos */}
              <div className="flex gap-2 p-1 bg-gray-800/50 rounded-lg w-fit">
                <Button
                  onClick={() => setFormSectionTab('responses')}
                  className={`px-6 ${formSectionTab === 'responses' 
                    ? 'bg-pink-600 hover:bg-pink-700 text-white' 
                    : 'bg-transparent hover:bg-gray-700 text-gray-400'}`}
                >
                  📋 Respuestas Recibidas
                </Button>
                <Button
                  onClick={() => setFormSectionTab('editor')}
                  className={`px-6 ${formSectionTab === 'editor' 
                    ? 'bg-cyan-600 hover:bg-cyan-700 text-white' 
                    : 'bg-transparent hover:bg-gray-700 text-gray-400'}`}
                >
                  ✏️ Editor de Formularios
                </Button>
                <Button
                  onClick={() => setFormSectionTab('clinical')}
                  className={`px-6 ${formSectionTab === 'clinical' 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-transparent hover:bg-gray-700 text-gray-400'}`}
                >
                  🏥 Formularios Clínicos {clinicalFormStats.with_red_flags > 0 && <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{clinicalFormStats.with_red_flags}</span>}
                </Button>
              </div>

              {/* Tab: Editor de Formularios */}
              {formSectionTab === 'editor' && (
                <FormBuilder />
              )}

              {/* Tab: Formularios Clínicos */}
              {formSectionTab === 'clinical' && (
                <div className="space-y-6">
                  {/* Estadísticas */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-white">{clinicalFormStats.total}</p>
                        <p className="text-sm text-gray-300">Total Enviados</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-white">{clinicalFormStats.pending}</p>
                        <p className="text-sm text-gray-300">Pendientes</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-white">{clinicalFormStats.completed}</p>
                        <p className="text-sm text-gray-300">Completados</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-500/20 to-pink-500/20 border-red-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-white">{clinicalFormStats.with_red_flags}</p>
                        <p className="text-sm text-gray-300">⚠️ Con Alertas</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Lista de formularios clínicos */}
                  <Card className="bg-gray-900/50 border-gray-700">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-white">
                        🏥 Formularios Clínicos Enviados
                      </CardTitle>
                      <CardDescription>
                        Formularios de evaluación clínica enviados a usuarios específicos
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingClinicalForms ? (
                        <div className="text-center py-8">
                          <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto"></div>
                        </div>
                      ) : clinicalFormAssignments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No hay formularios clínicos enviados</p>
                          <p className="text-sm mt-2">Envía formularios desde el Generador Offline</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {clinicalFormAssignments.map((assignment) => (
                            <div 
                              key={assignment.id}
                              className={`p-4 rounded-lg border ${assignment.has_red_flags ? 'border-red-500/50 bg-red-500/10' : 'border-gray-700 bg-gray-800/50'}`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-white">{assignment.user_name}</span>
                                    <span className="text-sm text-gray-400">({assignment.user_email})</span>
                                    {assignment.has_red_flags && (
                                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">⚠️ ALERTA</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1">
                                    📋 {assignment.form_name}
                                  </p>
                                  <div className="flex gap-2 mt-2">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      assignment.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      {assignment.status === 'completed' ? '✓ Completado' : '⏳ Pendiente'}
                                    </span>
                                    {assignment.tags?.map(tag => (
                                      <span key={tag} className={`text-xs px-2 py-1 rounded ${
                                        tag === 'RED_FLAG' ? 'bg-red-500/20 text-red-400' :
                                        tag === 'RENAL_DIAGNOSED' ? 'bg-orange-500/20 text-orange-400' :
                                        tag === 'RENAL_RISK_MODERATE' ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-green-500/20 text-green-400'
                                      }`}>
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                  <p className="text-xs text-gray-500">
                                    {new Date(assignment.assigned_at).toLocaleDateString('es-ES')}
                                  </p>
                                  <div className="flex gap-2">
                                    {assignment.status === 'completed' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setExpandedClinicalForm(expandedClinicalForm === assignment.id ? null : assignment.id)}
                                      >
                                        {expandedClinicalForm === assignment.id ? 'Ocultar' : 'Ver Respuestas'}
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                      data-testid={`delete-clinical-form-${assignment.id}`}
                                      onClick={() => handleDeleteClinicalForm(assignment.id)}
                                    >
                                      Eliminar
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Respuestas expandidas */}
                              {expandedClinicalForm === assignment.id && assignment.responses && (
                                <div className="mt-4 pt-4 border-t border-gray-700">
                                  <h4 className="font-bold text-white mb-3">Respuestas:</h4>
                                  <div className="space-y-3 text-sm">
                                    {Object.entries(assignment.responses).map(([key, value]) => (
                                      <div key={key} className="bg-gray-900/50 p-3 rounded">
                                        <p className="text-gray-400 text-xs mb-1">{key}</p>
                                        <p className="text-white">
                                          {Array.isArray(value) ? value.join(', ') : value || '-'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                  {assignment.red_flag_items?.length > 0 && (
                                    <div className="mt-4 p-3 bg-red-500/20 border border-red-500/40 rounded">
                                      <p className="text-red-400 font-bold mb-2">⚠️ Señales de Alarma Detectadas:</p>
                                      <ul className="text-sm text-red-300 list-disc list-inside">
                                        {assignment.red_flag_items.map((item, idx) => (
                                          <li key={idx}>{item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tab: Respuestas Recibidas */}
              {formSectionTab === 'responses' && (
                <>
                  {/* Estadísticas de Formularios */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-white">{formStats.total_all}</p>
                        <p className="text-sm text-gray-300">Total Formularios</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-yellow-400">{formStats.total_pending}</p>
                        <p className="text-sm text-gray-300">Por Liberar</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-green-400">{formStats.total_released}</p>
                        <p className="text-sm text-gray-300">Liberados</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-pink-500/20 to-pink-600/20 border-pink-500/30">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-pink-400">{formStats.total_workout}</p>
                        <p className="text-sm text-gray-300">Rutinas</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Estadísticas por Plan */}
                  {Object.keys(formStats.by_plan || {}).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">📊 Formularios por Plan de Usuario</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(formStats.by_plan || {}).map(([plan, data]) => (
                        <div key={plan} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <p className="font-semibold text-white capitalize">{plan === 'sin_plan' ? 'Sin Plan' : plan}</p>
                          <div className="flex justify-between mt-2 text-sm">
                            <span className="text-gray-400">Total: <span className="text-white">{data.total}</span></span>
                            <span className="text-yellow-400">Pend: {data.pending}</span>
                            <span className="text-green-400">Lib: {data.released}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Selector de Vista */}
              <div className="flex gap-2">
                <Button
                  onClick={() => { setFormViewMode('pending'); setFormFilter('pending_release'); }}
                  className={`flex-1 ${formViewMode === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  data-testid="admin-forms-view-pending"
                >
                  ⏳ Por Liberar ({formStats.total_pending})
                </Button>
                <Button
                  onClick={() => { setFormViewMode('all'); setFormFilter('all'); }}
                  className={`flex-1 ${formViewMode === 'all' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  data-testid="admin-forms-view-all"
                >
                  📋 Todos los Formularios ({formStats.total_all})
                </Button>
              </div>

              {/* Búsqueda por nombre / email de usuario */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formSearchQuery}
                  onChange={(e) => setFormSearchQuery(e.target.value)}
                  placeholder="🔍 Buscar por nombre o email de usuario..."
                  className="flex-1 px-4 py-2 border rounded-lg text-sm bg-gray-800 text-white border-gray-600 focus:outline-none focus:border-pink-500"
                  data-testid="admin-forms-search-input"
                />
                {formSearchQuery && (
                  <Button
                    onClick={() => setFormSearchQuery('')}
                    variant="outline"
                    size="sm"
                    data-testid="admin-forms-search-clear"
                  >
                    ✖ Limpiar
                  </Button>
                )}
              </div>

              {/* Filtros */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <CardTitle className="flex items-center gap-2">
                      {formViewMode === 'pending' ? '⏳ Formularios por Liberar' : '📋 Historial de Formularios'}
                      <Badge variant="secondary" data-testid="admin-forms-visible-count">
                        {(() => {
                          const q = (formSearchQuery || '').trim().toLowerCase();
                          if (!q) return formSubmissions.length;
                          return (formSubmissions || []).filter(s =>
                            (s.user_name || '').toLowerCase().includes(q) ||
                            (s.user_email || '').toLowerCase().includes(q)
                          ).length;
                        })()}
                      </Badge>
                    </CardTitle>
                    <div className="flex gap-3 flex-wrap">
                      {formViewMode === 'all' && (
                        <select
                          className="px-4 py-2 border rounded-lg text-sm bg-gray-800 text-white border-gray-600"
                          value={formFilter}
                          onChange={(e) => setFormFilter(e.target.value)}
                        >
                          <option value="all">Todos los estados</option>
                          <option value="pending_release">⏳ Pendiente de liberación</option>
                          <option value="released">✅ Liberados</option>
                        </select>
                      )}
                      <select
                        className="px-4 py-2 border rounded-lg text-sm bg-gray-800 text-white border-gray-600"
                        value={formTypeFilter}
                        onChange={(e) => setFormTypeFilter(e.target.value)}
                      >
                        <option value="all">Todos los tipos</option>
                        <option value="workout">🏋️ Rutina de Entrenamiento</option>
                        <option value="diet">🥗 Plan de Nutrición</option>
                        <option value="monthly_update">📅 Actualización Mensual</option>
                      </select>
                      <Button onClick={fetchFormSubmissions} variant="outline" size="sm">
                        🔄 Actualizar
                      </Button>
                      <Button
                        onClick={() => setSupplementsManagerOpen(true)}
                        variant="outline"
                        size="sm"
                        className="border-pink-400 text-pink-300 hover:bg-pink-500/10"
                        data-testid="open-supplements-manager-btn"
                      >
                        💊 Estado suplementos
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Lista de Formularios */}
              {(() => {
                const q = (formSearchQuery || '').trim().toLowerCase();
                const visibleSubmissions = q
                  ? (formSubmissions || []).filter(s => {
                      const name = (s.user_name || '').toLowerCase();
                      const email = (s.user_email || '').toLowerCase();
                      return name.includes(q) || email.includes(q);
                    })
                  : (formSubmissions || []);
                return (
              loadingFormSubmissions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
                </div>
              ) : visibleSubmissions.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No hay formularios</h3>
                    <p className="text-gray-500" data-testid="admin-forms-empty-msg">
                      {q
                        ? `No se encontraron formularios que coincidan con "${formSearchQuery}"`
                        : (formFilter !== 'all' || formTypeFilter !== 'all'
                            ? 'Prueba cambiando los filtros'
                            : 'Los formularios de rutinas aparecerán aquí cuando los usuarios los envíen')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="admin-forms-list">
                  {visibleSubmissions.map((submission) => (
                    <Card 
                      key={submission.id} 
                      className={`border-2 transition-all ${
                        submission.status === 'pending_release' 
                          ? 'border-yellow-500/50 bg-yellow-500/10' 
                          : 'border-green-500/50 bg-green-500/10'
                      }`}
                    >
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4 flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
                              submission.form_type === 'workout' ? 'bg-purple-500/20' : 
                              submission.form_type === 'monthly_update' ? 'bg-cyan-500/20' : 'bg-green-500/20'
                            }`}>
                              {submission.form_type === 'workout' ? '🏋️' : 
                               submission.form_type === 'monthly_update' ? '📅' : '🥗'}
                            </div>
                            <div>
                              <h4 className="text-xl font-bold text-white">{submission.user_name}</h4>
                              <p className="text-gray-300">{submission.user_email}</p>
                              <p className="text-sm text-gray-400 mt-1">
                                📅 {submission.created_at_formatted || new Date(submission.created_at).toLocaleString('es-CO')}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge 
                              className={`px-4 py-2 text-sm ${
                                submission.status === 'pending_release'
                                  ? 'bg-yellow-500 text-white'
                                  : 'bg-green-500 text-white'
                              }`}
                            >
                              {submission.status === 'pending_release' ? '⏳ Pendiente de revisión' : '✅ Revisado'}
                            </Badge>
                            <Badge variant="outline" className="text-xs border-gray-500 text-gray-300">
                              {submission.form_type === 'workout' ? 'Rutina de Entrenamiento' : 
                               submission.form_type === 'monthly_update' ? 'Actualización Mensual' : 
                               (submission.form_data?.is_fit_evolution || submission.form_data?.training_profile) ? 'Plan Fit Evolution (Nutrición + Rutina)' :
                               'Plan de Nutrición'}
                            </Badge>
                            <Badge 
                              className={`text-xs ${
                                submission.user_subscription_plan === 'basic' ? 'bg-blue-500/20 text-blue-300 border-blue-500/50' :
                                submission.user_subscription_plan === 'premium' ? 'bg-purple-500/20 text-purple-300 border-purple-500/50' :
                                submission.user_subscription_plan === 'vip_gold' || submission.user_subscription_plan === 'gold' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' :
                                'bg-gray-500/20 text-gray-300 border-gray-500/50'
                              }`}
                              variant="outline"
                            >
                              👤 Plan: {submission.user_subscription_plan || 'Sin Plan'}
                            </Badge>
                          </div>
                        </div>

                        {/* Datos del Formulario - Expandible */}
                        <div className="mt-4 flex gap-2 flex-wrap">
                          <button
                            onClick={() => setExpandedForm(expandedForm === submission.id ? null : submission.id)}
                            className="flex-1 min-w-[200px] flex justify-between items-center p-3 rounded-lg border hover:bg-white/10 transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
                          >
                            <span className="font-semibold text-gray-200">📝 Ver datos del formulario</span>
                            <span className={`transform transition-transform text-gray-400 ${expandedForm === submission.id ? 'rotate-180' : ''}`}>
                              ▼
                            </span>
                          </button>
                          <button
                            onClick={() => setProfileModalEmail(submission.user_email)}
                            className="px-4 py-3 rounded-lg border font-semibold text-pink-300 hover:bg-pink-500/10 transition-colors"
                            style={{ backgroundColor: 'rgba(236,72,153,0.08)', borderColor: 'rgba(236,72,153,0.35)' }}
                            data-testid={`open-client-profile-${submission.id}`}
                            title="Ver formulario inicial + actualizaciones + historial de peso, con edición"
                          >
                            👤 Ver perfil completo
                          </button>
                        </div>
                        <div>
                          {expandedForm === submission.id && (
                            <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                              <div className="grid md:grid-cols-2 gap-4">
                                {Object.entries(formatFormData(submission.form_data || submission.data, submission.form_type)).map(([key, value]) => (
                                  <div key={key} className="flex gap-2">
                                    <span className="font-semibold text-pink-400">{key}:</span>
                                    <span className="text-gray-200">{value || 'N/A'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Acciones */}
                        <div className="mt-4 flex gap-3 justify-end flex-wrap items-center">
                          {submission.status === 'pending_release' && (() => {
                            const emailKey = (submission.user_email || '').toLowerCase();
                            const feProgress = submission.form_type === 'diet' ? feProgressByEmail[emailKey] : null;
                            const isReady = !feProgress || feProgress.is_ready === true;
                            const overallPct = feProgress?.overall_pct ?? (isReady ? 100 : 0);
                            const rTotal = feProgress?.recipes_total || 0;
                            const rDone = feProgress?.recipes_done || 0;
                            const iTotal = feProgress?.images_total || 0;
                            const iDone = feProgress?.images_done || 0;
                            const wStatus = feProgress?.workout_status || 'pending';
                            // ⭐ FIT EVOLUTION: bloquear liberación si no hay fotos iniciales (solo plan basic)
                            const planNorm = (submission.user_subscription_plan || '').toLowerCase();
                            const isBasicPlan = planNorm === 'basic' || planNorm === 'fit_evolution' || planNorm === 'fit-evolution';
                            const missingInitialPhotos = isBasicPlan && submission.has_initial_photos === false;
                            const canRelease = isReady && !missingInitialPhotos;
                            return (
                              <>
                                {missingInitialPhotos && (
                                  <div className="w-full mb-2 p-3 rounded-lg bg-red-500/10 border border-red-500/40" data-testid={`fe-missing-photos-${submission.id}`}>
                                    <div className="text-xs text-red-300 font-semibold flex items-center gap-2">
                                      📸 Fotos iniciales faltantes
                                    </div>
                                    <p className="text-xs text-red-100/80 mt-1">
                                      La clienta debe subir sus fotos iniciales en <b>Mi Progreso</b> antes de poder liberar el plan Fit Evolution.
                                    </p>
                                  </div>
                                )}
                                {feProgress && !isReady && (
                                  <div className="w-full mb-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/40" data-testid={`fe-progress-${submission.id}`}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-yellow-300 font-semibold">⏳ Preparando plan Fit Evolution ({overallPct}%)</span>
                                      <span className="text-yellow-200">Recetas {rDone}/{rTotal} · Fotos {iDone}/{iTotal} · Rutina {wStatus === 'done' ? '✅' : wStatus === 'in_progress' ? '⏳' : '⏸️'}</span>
                                    </div>
                                    <div className="w-full h-2 bg-black/40 rounded overflow-hidden">
                                      <div className="h-2 bg-gradient-to-r from-yellow-400 to-orange-500 transition-all" style={{ width: `${overallPct}%` }} />
                                    </div>
                                    <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                                      <p className="text-xs text-yellow-100/80">
                                        No podrás liberar hasta que esté 100% listo. Se auto-liberará a las 24h si no lo haces tú.
                                      </p>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs border-yellow-500 text-yellow-200 hover:bg-yellow-500/20"
                                        data-testid={`fe-retry-generation-${submission.id}`}
                                        onClick={async () => {
                                          try {
                                            const emailKey = (submission.user_email || '').toLowerCase();
                                            await axios.post(`${API}/personalized-plans/fit-evolution/retry-generation/${encodeURIComponent(emailKey)}`);
                                            toast.success('Generación reintentada. Actualizando…');
                                            setTimeout(() => fetchFormSubmissions(), 1500);
                                          } catch (e) {
                                            toast.error('No se pudo reintentar: ' + (e?.response?.data?.detail || e.message));
                                          }
                                        }}
                                      >
                                        🔄 Reintentar generación
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                <Button
                                  onClick={() => handleReleaseForm(submission.id)}
                                  disabled={releasingForm === submission.id || !canRelease}
                                  data-testid={`release-form-btn-${submission.id}`}
                                  title={missingInitialPhotos ? 'La clienta debe subir sus fotos iniciales antes de liberar el plan' : (!isReady ? `Preparando ${overallPct}%…` : '')}
                                  className={`bg-gradient-to-r ${canRelease ? 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700' : 'from-gray-500 to-gray-600 opacity-60 cursor-not-allowed'}`}
                                >
                                  {releasingForm === submission.id ? (
                                    <>
                                      <span className="animate-spin mr-2">⏳</span>
                                      Liberando...
                                    </>
                                  ) : missingInitialPhotos ? (
                                    <>🔒 Esperando fotos iniciales</>
                                  ) : (
                                    <>
                                      {isReady ? '🚀 Liberar plan nutricional y de entrenamiento' : `⏳ Preparando ${overallPct}%…`}
                                    </>
                                  )}
                                </Button>

                              </>
                            );
                          })()}

                          {/* Botones de FORCE REGENERATE — visibles SIEMPRE (pendiente o liberado) para arreglar rutinas/dietas mal generadas */}
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`view-plan-btn-${submission.id}`}
                            className="text-xs border-emerald-500 text-emerald-300 hover:bg-emerald-500/20"
                            onClick={() => setPlanViewerEmail(submission.user_email)}
                          >
                            👁️ Ver / Editar Plan
                          </Button>
                          {/* ═════ Selector MANUAL de sexo — SPRINT anti-bug ═════ */}
                          {/* La admin elige "Hombre" / "Mujer" y el backend obliga ese split
                              sin importar lo que diga el perfil. Fix definitivo. */}
                          <div className="flex items-stretch gap-1" data-testid={`sex-override-group-${submission.id}`}>
                            <select
                              data-testid={`sex-override-${submission.id}`}
                              value={regenSexOverride[submission.id] || 'auto'}
                              onChange={(e) => setRegenSexOverride(prev => ({ ...prev, [submission.id]: e.target.value }))}
                              className="text-xs h-8 rounded border border-cyan-500/60 bg-black/40 text-cyan-100 px-2"
                              title="Elige el split que quieres aplicar"
                            >
                              <option value="auto">🎯 Auto (perfil)</option>
                              <option value="hombre">👨 Forzar Hombre</option>
                              <option value="mujer">👩 Forzar Mujer</option>
                            </select>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`force-regen-workout-${submission.id}`}
                              className="text-xs border-cyan-500 text-cyan-300 hover:bg-cyan-500/20"
                              onClick={async () => {
                                const forceSexValue = regenSexOverride[submission.id] && regenSexOverride[submission.id] !== 'auto'
                                  ? regenSexOverride[submission.id]
                                  : null;
                                const confirmMsg = forceSexValue
                                  ? `¿Regenerar RUTINA forzando sexo=${forceSexValue}?\nSe aplicará en 1-2 min.`
                                  : '¿Regenerar la RUTINA de este usuario con los tags actuales? Auto-detección del sexo desde su perfil.';
                                if (!window.confirm(confirmMsg)) return;
                                try {
                                  const emailKey = (submission.user_email || '').toLowerCase();
                                  const url = `${API}/personalized-plans/admin/fit-evolution/regenerate-workout/${encodeURIComponent(emailKey)}` +
                                    (forceSexValue ? `?force_sex=${encodeURIComponent(forceSexValue)}` : '');
                                  const res = await axios.post(url);
                                  const resolvedSex = res?.data?.resolved_sex || forceSexValue || 'auto';
                                  toast.success(`Rutina regenerándose con sexo=${resolvedSex}`);
                                  setTimeout(() => fetchFormSubmissions(), 2000);
                                } catch (e) {
                                  toast.error('Error: ' + (e?.response?.data?.detail || e.message));
                                }
                              }}
                            >
                              🔄 Regenerar Rutina
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`force-regen-diet-${submission.id}`}
                            className="text-xs border-pink-500 text-pink-300 hover:bg-pink-500/20"
                            onClick={async () => {
                              if (!window.confirm('¿Regenerar TODAS las recetas de este usuario? Las recetas anteriores se borrarán.')) return;
                              try {
                                const emailKey = (submission.user_email || '').toLowerCase();
                                await axios.post(`${API}/personalized-plans/admin/fit-evolution/regenerate-diet/${encodeURIComponent(emailKey)}`);
                                toast.success('Dieta en regeneración · nuevas recetas desde cero');
                                setTimeout(() => fetchFormSubmissions(), 2000);
                              } catch (e) {
                                toast.error('Error: ' + (e?.response?.data?.detail || e.message));
                              }
                            }}
                          >
                            🔄 Regenerar Dieta
                          </Button>

                          <Button
                            variant="outline"
                            onClick={() => handleDeleteFormSubmission(submission.id)}
                            className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                          >
                            🗑️ Eliminar
                          </Button>
                        </div>

                        {/* Info de liberación */}
                        {submission.status === 'released' && submission.released_at && (
                          <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-200">
                            <p className="text-sm text-green-800">
                              ✅ Liberado el {new Date(submission.released_at).toLocaleString('es-CO')}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
                );
              })()}

              {/* Info explicativa */}
              <Card className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700">
                <CardContent className="p-6">
                  <h4 className="font-bold text-pink-400 mb-3 flex items-center gap-2">
                    💡 ¿Cómo funciona?
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-500">⏳</span>
                      <span><strong className="text-yellow-400">Pendiente de liberación:</strong> El usuario envió el formulario y la rutina fue generada, pero aún no puede verla.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">🚀</span>
                      <span><strong className="text-green-400">Liberar:</strong> Al hacer clic en &quot;Liberar Plan&quot;, el usuario podrá ver su rutina inmediatamente.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✅</span>
                      <span><strong className="text-green-400">Liberado:</strong> El usuario ya tiene acceso completo a su plan personalizado.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Bulk Tag Editor Modal */}
      {showBulkTagEditor && (
        <BulkTagEditor onClose={() => { setShowBulkTagEditor(false); fetchVideos(); }} />
      )}
      {/* Gestor de Categorías de Video */}
      {showVideoCategoryManager && (
        <VideoCategoryManagerModal
          categories={customVideoCategories}
          onClose={() => setShowVideoCategoryManager(false)}
          onChanged={fetchCustomVideoCategories}
        />
      )}
      {/* User Plan Viewer / Editor Modal */}
      <UserPlanViewer
        email={planViewerEmail}
        isOpen={!!planViewerEmail}
        onClose={() => setPlanViewerEmail(null)}
      />
      {/* 2026-02 · Vista unificada del perfil de una clienta */}
      {profileModalEmail && (
        <ClientProfileModal
          email={profileModalEmail}
          onClose={() => setProfileModalEmail(null)}
        />
      )}
      {/* 2026-02 · Buscador rápido de suplementación */}
      <SupplementsStatusManager
        isOpen={supplementsManagerOpen}
        onClose={() => setSupplementsManagerOpen(false)}
      />
    </div>
  );
};

// 2026-02 · Modal ligero para crear/editar/eliminar categorías de video
// personalizadas. Todo se guarda en la colección `video_categories` y el
// selector del formulario de videos se re-consulta al cerrar/tras cambios.
function VideoCategoryManagerModal({ categories, onClose, onChanged }) {
  const [newLabel, setNewLabel] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id, label, emoji }

  const create = async () => {
    if (!newLabel.trim()) { toast.error('Escribe un nombre'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/video-categories`, { label: newLabel.trim(), emoji: newEmoji.trim() });
      toast.success('Categoría creada');
      setNewLabel(''); setNewEmoji('');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.label?.trim()) { toast.error('El nombre no puede estar vacío'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/admin/video-categories/${editing.id}`, {
        label: editing.label.trim(),
        emoji: (editing.emoji || '').trim(),
      });
      toast.success('Categoría actualizada');
      setEditing(null);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.label}"? Sólo se puede si no tiene videos asignados.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/video-categories/${cat.id}`);
      toast.success('Categoría eliminada');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      data-testid="video-category-manager-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">🗂️ Categorías de Video</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl" aria-label="Cerrar">✕</button>
        </div>

        <div className="p-5 space-y-6">
          {/* Crear nueva */}
          <div className="border rounded-lg p-4 bg-pink-50/40 space-y-3">
            <p className="text-sm font-semibold text-gray-800">➕ Crear nueva categoría</p>
            <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-2 items-end">
              <div>
                <Label htmlFor="new-cat-emoji" className="text-xs">Emoji (opcional)</Label>
                <Input
                  id="new-cat-emoji"
                  data-testid="video-category-new-emoji"
                  value={newEmoji}
                  onChange={(e) => setNewEmoji(e.target.value)}
                  placeholder="📁"
                  maxLength={4}
                />
              </div>
              <div>
                <Label htmlFor="new-cat-label" className="text-xs">Nombre</Label>
                <Input
                  id="new-cat-label"
                  data-testid="video-category-new-label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Ej: Cardio en casa"
                />
              </div>
              <Button
                type="button"
                onClick={create}
                disabled={busy}
                data-testid="video-category-create-btn"
                className="bg-pink-600 hover:bg-pink-700 text-white"
              >
                Crear
              </Button>
            </div>
          </div>

          {/* Listado */}
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-2">
              📋 Categorías personalizadas ({categories.length})
            </p>
            {categories.length === 0 ? (
              <p className="text-sm text-gray-500 italic px-2 py-4">
                Aún no hay categorías personalizadas. Crea una arriba.
              </p>
            ) : (
              <ul className="divide-y border rounded-lg" data-testid="video-category-list">
                {categories.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 p-3">
                    {editing?.id === c.id ? (
                      <>
                        <Input
                          className="w-20"
                          value={editing.emoji || ''}
                          maxLength={4}
                          onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                          placeholder="📁"
                          data-testid={`video-category-edit-emoji-${c.id}`}
                        />
                        <Input
                          className="flex-1"
                          value={editing.label}
                          onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          data-testid={`video-category-edit-label-${c.id}`}
                        />
                        <Button size="sm" onClick={saveEdit} disabled={busy}
                          data-testid={`video-category-save-${c.id}`}>Guardar</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                      </>
                    ) : (
                      <>
                        <span className="text-xl w-8 text-center">{c.emoji || '📁'}</span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{c.label}</p>
                          <p className="text-[11px] text-gray-500 font-mono">{c.slug}</p>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => setEditing({ id: c.id, label: c.label, emoji: c.emoji || '' })}
                          data-testid={`video-category-edit-${c.id}`}
                        >Editar</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => remove(c)}
                          data-testid={`video-category-delete-${c.id}`}
                        >Eliminar</Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              Nota: sólo se pueden eliminar categorías que no tengan videos asignados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanelNew;
